import { useState, useEffect } from "react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

/* ══════════════════════════════════════════════
   翻訳データ
══════════════════════════════════════════════ */
const TRANSLATIONS = {
  ja: {
    siteName: "SC-NET Japan",
    siteTagline: "幹細胞上清液 臨床研究ネットワーク",
    login: "ログイン", register: "新規登録",
    heroTitle1: "ヒト幹細胞培養上清液", heroTitleItalic: "点滴投与",
    heroTitle2: "臨床研究データ共有ネットワーク",
    heroSub: "パーキンソン病 / アルツハイマー型認知症 / ALS / 癌　― Macro Bio 監修の評価設計に準拠",
    btnInput: "＋ 症例データを入力する", btnView: "症例データを閲覧する",
    tab1:"症例データ", tab2:"✏ 症例を入力する", tab3:"プロトコル・文書", tab4:"安全性情報",
    tabDashboard:"📊 ダッシュボード",
    dashboardTitle:"📊 ダッシュボード", dashboardSub:"登録された症例データの可視化と分析",
    totalCases:"総症例数", byDisease:"疾患別構成", casesPerDisease:"疾患別症例数",
    timelineTitle:"経時推移", filterByDisease:"疾患で絞り込み", allDiseases:"すべての疾患",
    selectCase:"症例を選択", selectCasePh:"— 症例を選んでください —",
    histogramTitle:"主要指標の分布", noData:"まだデータがありません。症例を入力するとここに表示されます。",
    exportCSV:"CSV出力", reseedSample:"サンプル再投入", clearAll:"全データ削除",
    confirmClear:"本当にすべてのデータを削除しますか？この操作は取り消せません。",
    confirmReseed:"現在のデータをサンプルデータで上書きします。よろしいですか？",
    timelineNeedMore:"📈 経時推移グラフを表示するには、同じ症例IDで2つ以上の評価時点のデータが必要です。",
    savedCaseInfo:"保存された症例",
    subTabMine:"📁 自分のデータ", subTabPublic:"🌐 全体の公開事例",
    publicSub:"査読済み・他施設の公開研究事例",
    searchPlaceholder:"症例ID・施設名・疾患名で検索…",
    sortNewest:"新しい順", sortOldest:"古い順",
    matchedCases:"該当症例", noMatch:"条件に一致する症例がありません",
    confirmDeleteCase:"この症例データを削除しますか？", delete:"削除",
    edit:"編集", editFormTitle:"症例編集", editingMode:"編集中",
    errorTitle:"入力内容にエラーがあります", errorHint:"必須項目の入力漏れや、数値の範囲外の項目があります。確認してください。",
    errorRequired:"必須項目です", errorNumeric:"数値で入力してください", errorRange:"許容範囲外",
    exportPDF:"PDF出力",
    trendSectionTitle:"個人データ推移",
    trendIndividual:"個別詳細", trendCompare:"症例比較", trendMetric:"指標フォーカス", trendMean:"疾患平均", trendGrid:"全症例一覧",
    selectMetric:"評価指標を選択", selectCasesToCompare:"比較する症例を選択（最大8件）",
    compareTitle:"症例比較グラフ", compareHint:"症例を選んで重ねて表示", compareNeedSelection:"症例を1つ以上選択してください",
    individualBreakdown:"指標別の個別グラフ",
    metricFocusSub:"この指標を持つ全症例", meanTitle:"平均推移", meanSub:"赤線=平均、灰線=個別症例", meanLabel:"平均",
    gridSub:"全症例のミニグラフ", casesUnit:"件", pointsUnit:"点", recordsUnit:"レコード",
    noMetricData:"この指標のデータがありません", noDataForMetric:"この疾患・指標のデータがまだありません",
    caseCount:"登録症例", cases:"件", btnAddCase:"＋ 症例を入力する",
    selectDisease:"疾患を選択してください",
    selectDiseaseSub:"各疾患のデータ取得設計（Macro Bio監修）に準拠したフォームが表示されます",
    backDisease:"← 疾患を選び直す", formTitle:"症例入力フォーム",
    notice:"📋 Macro Bio Co., Ltd. 監修のデータ取得設計に準拠。患者情報は必ず", noticeStrong:"匿名化", noticeEnd:"の上ご入力ください。",
    noticeAD:"⚠ アルツハイマー型認知症は", noticeADStrong:"最低3〜6か月単位", noticeADEnd:"の評価が必要です",
    noticeALS:"⚠ ALSは", noticeALSStrong:"最低6〜12か月の長期追跡", noticeALSEnd:"が必要です。ALSFRS-Rは低下速度を重視します。",
    done:"症例データを登録しました", doneSub:"査読チームが確認後、ステータスが更新されます（通常3〜5営業日）", doneBtn:"続けて入力する",
    prev:"← 前へ", next:"次へ →", submit:"✓ 登録する", step:"STEP",
    // 共通フィールド
    caseId:"症例ID（匿名）", caseIdPh:"例：PD-2026-001",
    institution:"施設名", institutionPh:"例：○○大学附属病院",
    physician:"担当医師名", physicianPh:"例：田中 誠一",
    startDate:"治療開始日", timePoint:"評価時点",
    age:"年齢", agePh:"例：68",
    sex:"性別", male:"男性", female:"女性", other:"その他",
    required:"*必須",
    // 安全性
    safetyTitle:"安全性評価（CTCAE v5.0）",
    aeLabel:"有害事象（複数選択可）",
    gradeLabel:"Gradeを選択", saeLabel:"重篤有害事象（SAE）が発生した",
    saeWarn:"⚠ SAE報告フォームを速やかに提出してください。事務局に自動通知されます。",
    notesLabel:"備考", notesPh:"特記事項など自由記載",
    // PD
    pdLabel:"パーキンソン病", pdSub:"MDS-UPDRS / Hoehn&Yahr / PDQ-39",
    pdStep1:"基本・診断", pdStep2:"重症度・薬剤", pdStep3:"MDS-UPDRS", pdStep4:"QOL・安全性",
    hyTitle:"Hoehn & Yahr 分類", hySub:"患者のベースライン状態を明確にします",
    medTitle:"服薬管理（ON/OFF状態）", medSub:"薬効との混同を排除するため厳密に記録します",
    medLabel:"現在の服薬内容", medPh:"例：L-dopa 300mg/日",
    ldopaTime:"評価前の服薬（分前）", ldopaPh:"60",
    onOffLabel:"評価時のON/OFF状態",
    on:"ON状態（薬が効いている）", off:"OFF状態（薬が切れている）", partialOn:"部分ON（中間）", unknown:"不明",
    updrsTitle:"MDS-UPDRS Part III（運動機能）", updrsSub:"【最重要】治療前後で客観的に比較します（0〜132点）",
    updrsB:"治療前スコア", updrsA:"今回スコア", updrsBPh:"例：42", updrsAPh:"例：35",
    changeRate:"変化率（自動計算）",
    pdq39:"PDQ-39 合計点（0〜100・低いほど良好）", pdq39Ph:"例：42",
    duration:"罹病期間（年）", durationPh:"5",
    // AD
    adLabel:"アルツハイマー型認知症", adSub:"MMSE / CDR-SB / ADAS-Cog / ADL",
    adStep1:"基本・診断", adStep2:"認知機能", adStep3:"ADL・行動", adStep4:"安全性",
    diagName:"確定診断名", mriCt:"MRI/CT所見", mriPh:"例：海馬萎縮あり",
    proxy:"代諾者（家族/法定代理人）の同意を取得済み",
    cdrTitle:"重症度（CDR / CDR-SB）", cdrSub:"【最重要】認知機能と生活機能を統合した全体評価",
    cdrLabel:"CDR", cdrsb:"CDR-SB（0〜18点）", cdrsbPh:"例：4.5",
    mmse:"MMSE（0〜30点）", mmsePh:"例：22",
    moca:"MoCA（0〜30点）", mocaPh:"例：20",
    adasCog:"ADAS-Cog（0〜70点・高いほど低下）", adasPh:"例：18",
    dasc21:"DASC-21（21〜84点）", dasc21Ph:"例：38",
    npiQ:"NPI-Q 重症度合計（0〜36点）", npiPh:"例：6",
    careNote:"介護者からの評価", careNotePh:"例：「最近は名前を呼ぶと反応が増えた」「夜間の徘徊が週1→0回」など",
    // ALS
    alsLabel:"ALS（筋萎縮性側索硬化症）", alsSub:"ALSFRS-R / FVC / 進行イベント",
    alsStep1:"基本・診断", alsStep2:"ALSFRS-R", alsStep3:"呼吸・栄養", alsStep4:"安全性",
    onsetSite:"発症部位", diagCriteria:"診断基準",
    niv:"NIV（人工呼吸器）使用中", gastrostomy:"胃ろう造設済み", tracheostomy:"気管切開済み", wheelChair:"車椅子使用中",
    alsfrsTitle:"ALSFRS-R（0〜4点 × 12項目 = 最大48点）", alfssrsSub:"【最重要】点数の低下速度が鈍化しているかを確認します",
    alsfrsTotal:"ALSFRS-R 合計", alsfrsMax:"/ 48点",
    fvc:"FVC（努力性肺活量・%）", fvcPh:"72.4",
    svc:"SVC（緩徐肺活量・%）", svcPh:"68.0",
    spo2:"SpO₂（安静時・%）", spo2Ph:"97",
    fvcSevere:"⚠ 重度低下（NIV適応検討）", fvcWarn:"注意（定期観察強化）", fvcNormal:"正常範囲内",
    weight:"体重（kg）", bmi:"BMI",
    progressEvents:"進行イベント記録",
    nivDate:"NIV開始", trachDate:"気管切開", pegDate:"胃ろう造設",
    // Cancer
    cancerLabel:"癌治療", cancerSub:"RECIST v1.1 / PFS / OS / CTCAE",
    cancerStep1:"基本・対象", cancerStep2:"画像・RECIST", cancerStep3:"マーカー・生存", cancerStep4:"安全性",
    cancerType:"癌種", stage:"病期（Stage）", treatmentLine:"治療ライン", ps:"PS（全身状態）",
    imageType:"画像種別", imageDate:"撮影日",
    sumBaseline:"標的病変径和：ベースライン（mm）", sumCurrent:"標的病変径和：今回（mm）",
    recistFinal:"RECIST v1.1 最終判定", autoCalc:"自動計算",
    cea:"CEA（ng/mL）", ca199:"CA19-9（U/mL）",
    pfsWeeks:"PFS期間（週）", pfsWPh:"例：24",
    osStatus:"OSステータス", alive:"生存", dead:"死亡確認", lost:"追跡不能",
    // Stroke (脳梗塞)
    strokeLabel:"脳梗塞", strokeSub:"NIHSS / mRS / Barthel Index",
    strokeStep1:"基本・診断", strokeStep2:"神経学的評価", strokeStep3:"ADL・QOL", strokeStep4:"安全性",
    onsetDate:"発症日", strokeType:"病型",
    strokeLacunar:"ラクナ梗塞", strokeAthero:"アテローム血栓性", strokeCardio:"心原性塞栓性", strokeOther:"その他",
    onsetTime:"発症からの時間",
    tpaUsed:"t-PA投与（rt-PA）実施済み",
    nihssTitle:"NIHSS（脳卒中重症度スケール）", nihssSub:"【最重要】0〜42点・高いほど重症",
    nihssScore:"NIHSS合計点", nihssPh:"例：8",
    mrsTitle:"mRS（modified Rankin Scale）", mrsSub:"機能的予後評価・0〜6",
    mrsScore:"mRSスコア",
    mrsOpt0:"0：症状なし", mrsOpt1:"1：軽微な症状", mrsOpt2:"2：軽度", mrsOpt3:"3：中等度", mrsOpt4:"4：中等度〜重度", mrsOpt5:"5：重度", mrsOpt6:"6：死亡",
    barthelTitle:"Barthel Index（ADL）", barthelSub:"日常生活動作・0〜100",
    barthelScore:"Barthel Index", barthelPh:"例：65",
    fimScore:"FIM（任意）", fimPh:"例：90",
    recurrence:"脳卒中の再発", hemorrhage:"出血性変化あり",
    // SCI (脊髄損傷)
    sciLabel:"脊髄損傷", sciSub:"AIS / ISNCSCI / SCIM-III",
    sciStep1:"基本・診断", sciStep2:"運動・感覚評価", sciStep3:"ADL・痙縮", sciStep4:"安全性",
    injuryDate:"受傷日", injuryLevel:"損傷高位",
    levelCervical:"頸髄（C1〜C8）", levelThoracic:"胸髄（T1〜T12）", levelLumbar:"腰髄（L1〜L5）", levelSacral:"仙髄（S1〜S5）",
    aisGrade:"AIS（ASIA Impairment Scale）",
    aisA:"A：完全麻痺", aisB:"B：感覚不全", aisC:"C：運動不全（筋力<3）", aisD:"D：運動不全（筋力≧3）", aisE:"E：正常",
    isncsciTitle:"ISNCSCI 運動・感覚スコア", isncsciSub:"【最重要】神経学的評価の標準",
    motorUE:"運動スコア・上肢（0〜50）", motorUEPh:"例：35",
    motorLE:"運動スコア・下肢（0〜50）", motorLEPh:"例：20",
    sensoryPP:"感覚スコア・Pin Prick（0〜112）", sensoryPPPh:"例：80",
    sensoryLT:"感覚スコア・Light Touch（0〜112）", sensoryLTPh:"例：85",
    scimTitle:"SCIM-III（脊髄損傷自立度評価）", scimSub:"日常生活自立度・0〜100",
    scimScore:"SCIM-III合計", scimPh:"例：55",
    ashworthTitle:"Modified Ashworth Scale（痙縮）",
    ashworthScore:"MAS（0〜4）",
    ashworth0:"0：正常", ashworth1:"1：軽度", ashworth1p:"1+：軽度〜中度", ashworth2:"2：明らかな増加", ashworth3:"3：高度", ashworth4:"4：強直",
    autonomicDys:"自律神経反射異常の発症", pressureUlcer:"褥瘡の発生",
    // DM (糖尿病)
    dmLabel:"糖尿病", dmSub:"HbA1c / 血糖 / 合併症",
    dmStep1:"基本・診断", dmStep2:"血糖管理", dmStep3:"合併症評価", dmStep4:"安全性",
    dmType:"糖尿病型",
    dm1:"1型糖尿病", dm2:"2型糖尿病", dmGes:"妊娠糖尿病", dmOther:"その他特殊型",
    dmDuration:"罹病期間（年）", dmDurationPh:"例：10",
    bmi:"BMI", bmiPh:"例：24.5",
    complications:"既知の合併症",
    compNephro:"腎症", compNeuro:"神経障害", compRetino:"網膜症", compCardio:"心血管疾患", compNone:"なし",
    hba1cTitle:"血糖管理指標", hba1cSub:"【最重要】HbA1cと血糖値の推移",
    hba1c:"HbA1c（%）", hba1cPh:"例：7.2",
    fpg:"空腹時血糖（mg/dL）", fpgPh:"例：130",
    ppg2h:"食後2時間血糖（mg/dL）", ppg2hPh:"例：180",
    timeInRange:"TIR・CGM時間内割合（任意・%）", tirPh:"例：65",
    nephroTitle:"腎症評価", egfr:"eGFR（mL/min/1.73m²）", egfrPh:"例：65",
    urineAlb:"尿アルブミン（mg/gCr）", urineAlbPh:"例：45",
    neuroVAS:"神経障害VAS（しびれ・痛み）", neuroVASPh:"0〜10",
    retinoStage:"網膜症ステージ",
    retino0:"なし", retino1:"単純網膜症", retino2:"前増殖網膜症", retino3:"増殖網膜症",
    hypoEvents:"低血糖発作回数（過去4週）", hypoPh:"例：2",
    // Other (その他疾患)
    otherLabel:"その他疾患", otherSub:"汎用フォーム（評価項目自由記載）",
    otherStep1:"基本・診断", otherStep2:"主要評価項目", otherStep3:"QOL・ADL", otherStep4:"安全性",
    diseaseName:"疾患名（自由記述）", diseaseNamePh:"例：間質性肺炎",
    icd10:"ICD-10コード（任意）", icd10Ph:"例：J84.1",
    diagDate:"診断日／発症日",
    primaryEval:"主要評価項目（最大3つ）",
    evalName:"項目名", evalNamePh:"例：6分間歩行距離",
    evalValue:"数値・結果", evalValuePh:"例：320",
    evalUnit:"単位", evalUnitPh:"例：m",
    evalSupp:"補足情報", evalSuppPh:"使用したスケール・参考値・備考など",
    qolDesc:"QOL・ADL評価（自由記述）", qolDescPh:"例：SF-36 PCS 45 / EQ-5D 0.72 / 日常生活ほぼ自立",
    // プロトコル
    protocolTitle:"プロトコル・文書管理",
    docLoginMsg:"この機能を利用するにはログインが必要です。",
    docDownloadBtn:"⬇ ダウンロード",
    docUploadedLabel:"アップロード",
    docEmpty:"登録されている文書はまだありません。",
    docLoadingMsg:"読み込み中…",
    docErrorMsg:"文書一覧の取得に失敗しました。",
    // 安全性情報
    saeTitle:"SAE（重篤有害事象）報告",
    saeDesc:"重篤有害事象が発生した場合は速やかに報告フォームを提出してください。",
    saeBtn:"SAEを報告する",
    safetyStatsTitle:"安全性統計サマリー",
    safetyTotalCases:"総症例数",
    safetySaeCount:"SAE件数",
    safetyOverallRate:"全体SAE発生率",
    safetyByDiseaseTitle:"疾患別SAE発生率",
    safetyTopEventsTitle:"頻度の高い有害事象（上位10件）",
    safetyEmpty:"集計対象データがまだありません。",
    safetyLoadingMsg:"集計データを読み込み中…",
    safetyErrorMsg:"統計データの取得に失敗しました。",
    safetyCasesUnit:"件",
    // フッター
    footerTitle:"研究ネットワークに参加する",
    footerSub:"参加資格：医師免許保有者・医療機関所属の研究者\n申請後、事務局確認のうえ承認（通常3〜5営業日）",
    footerBtn:"参加申請フォームへ",
    footerCopy:"© 2026 SC-NET Japan　│　医師限定クローズドプラットフォーム　│　Macro Bio Co., Ltd. 監修",
    // 投与情報（共通）
    dosageTitle:"上清液投与情報",
    dosageSub:"投与した上清液の詳細を記録してください",
    dailyDose:"１．投与量/日（ml）", dailyDosePh:"例：90",
    treatmentPeriod:"２．投与期間及び回数", treatmentPeriodPh:"例：4週間・週1回・計4回",
    serumSource:"３．使用上清液の由来及び投与量",
    serumRow1:"乳歯髄由来（ml）", serumRow1Ph:"例：30",
    serumRow2:"臍帯由来（ml）",   serumRow2Ph:"例：30",
    serumRow3:"骨髄由来（ml）",   serumRow3Ph:"例：30",
    serumOther:"その他由来・内容", serumOtherPh:"例：脂肪由来 20ml など",
    // 症例
    caseAuthor:"著者", caseDate:"日付",
    // 認証まわり（フェーズ2追加）
    authLoginTitle:"ログイン", authEmailLabel:"メールアドレス", authPasswordLabel:"パスワード",
    authLoginBtn:"ログイン", authLoginError:"メールアドレスまたはパスワードが違います",
    authNoAccount:"アカウントをお持ちでない方は", authGoApply:"新規申込はこちら",
    authHaveAccount:"アカウントをお持ちの方は", authGoLogin:"ログインはこちら",
    authForgotPassword:"パスワードをお忘れですか？", authLogoutBtn:"ログアウト",
    authApplyTitle:"新規申込", authDisplayNameLabel:"氏名", authInstitutionLabel:"所属施設",
    authApplySubmitBtn:"申込む", authApplySuccess:"申込みを受け付けました。事務局の承認をお待ちください。",
    authResetRequestTitle:"パスワード再設定",
    authResetRequestDesc:"登録済みのメールアドレスを入力してください。再設定用のリンクをお送りします。",
    authResetRequestSubmitBtn:"送信する",
    authResetRequestSent:"メールを送信しました。届いたメールのリンクからパスワードを再設定してください。",
    authChangePasswordTitle:"パスワード変更", authMustChangeNotice:"初回ログインのため、パスワードの変更が必要です。",
    authCurrentPasswordLabel:"現在のパスワード", authNewPasswordLabel:"新しいパスワード",
    authNewPasswordConfirmLabel:"新しいパスワード（確認）", authChangePasswordSubmitBtn:"変更する",
    authChangePasswordSuccess:"パスワードを変更しました。", authPasswordMismatch:"新しいパスワードが一致しません",
    authResetConfirmTitle:"新しいパスワードの設定", authResetConfirmSubmitBtn:"設定する",
    authResetConfirmSuccess:"パスワードを再設定しました。ログインしてください。",
    authResetConfirmInvalid:"リンクが無効か期限切れです。もう一度パスワード再設定を申請してください。",
    authLoginRequiredTitle:"ログインが必要です",
    authLoginRequiredDesc:"症例データの入力にはログインが必要です。アカウントをお持ちでない場合は新規申込を行ってください。",
    authMineLoginRequired:"「自分のデータ」を見るにはログインが必要です。", authWelcome:"ようこそ",
    prefectureLabel:"都道府県", prefecturePh:"選択してください",
    dashboardDeleteMineBtn:"自分のデータを削除",
    dashboardDeleteMineConfirm:"自分が入力した症例データをすべて削除します。よろしいですか？",
  },
  en: {
    siteName: "SC-NET Japan",
    siteTagline: "Stem Cell Conditioned Medium Clinical Research Network",
    login: "Login", register: "Sign Up",
    heroTitle1: "Human Stem Cell Conditioned Medium", heroTitleItalic: "IV Administration",
    heroTitle2: "Clinical Research Data Sharing Network",
    heroSub: "Parkinson's / Alzheimer's / ALS / Cancer — Based on Macro Bio clinical data design",
    btnInput: "+ Enter Case Data", btnView: "Browse Case Data",
    tab1:"Cases", tab2:"✏ Enter Case", tab3:"Protocols & Documents", tab4:"Safety Info",
    tabDashboard:"📊 Dashboard",
    dashboardTitle:"📊 Dashboard", dashboardSub:"Visualization and analysis of registered case data",
    totalCases:"Total cases", byDisease:"Composition by disease", casesPerDisease:"Cases per disease",
    timelineTitle:"Longitudinal trend", filterByDisease:"Filter by disease", allDiseases:"All diseases",
    selectCase:"Select a case", selectCasePh:"— Choose a case —",
    histogramTitle:"Distribution of key metrics", noData:"No data yet. Enter cases and they will appear here.",
    exportCSV:"Export CSV", reseedSample:"Reseed samples", clearAll:"Clear all data",
    confirmClear:"Are you sure you want to delete all data? This action cannot be undone.",
    confirmReseed:"This will overwrite current data with sample data. Continue?",
    timelineNeedMore:"📈 A timeline chart requires data from 2 or more time points with the same case ID.",
    savedCaseInfo:"Saved case",
    subTabMine:"📁 My Data", subTabPublic:"🌐 Public Cases",
    publicSub:"Peer-reviewed and publicly shared research cases from other institutions",
    searchPlaceholder:"Search by case ID, institution, disease…",
    sortNewest:"Newest first", sortOldest:"Oldest first",
    matchedCases:"Matched cases", noMatch:"No cases match the criteria",
    confirmDeleteCase:"Delete this case record?", delete:"Delete",
    edit:"Edit", editFormTitle:"Edit Case", editingMode:"Editing",
    errorTitle:"There are errors in your input", errorHint:"Some required fields are missing or values are out of range. Please review.",
    errorRequired:"Required", errorNumeric:"Must be numeric", errorRange:"Out of range",
    exportPDF:"Export PDF",
    trendSectionTitle:"Individual Data Trends",
    trendIndividual:"Detailed", trendCompare:"Compare Cases", trendMetric:"Metric Focus", trendMean:"Disease Mean", trendGrid:"All Cases",
    selectMetric:"Select metric", selectCasesToCompare:"Select cases to compare (max 8)",
    compareTitle:"Case comparison chart", compareHint:"Pick cases to overlay", compareNeedSelection:"Please select at least one case",
    individualBreakdown:"Individual charts by metric",
    metricFocusSub:"All cases with this metric", meanTitle:"Mean trend", meanSub:"Red=mean, gray=individual cases", meanLabel:"Mean",
    gridSub:"Mini chart per case", casesUnit:" cases", pointsUnit:" pts", recordsUnit:" recs",
    noMetricData:"No data for this metric", noDataForMetric:"No data yet for this disease/metric",
    caseCount:"Registered cases", cases:"", btnAddCase:"+ Enter Case",
    selectDisease:"Select Disease",
    selectDiseaseSub:"A form compliant with Macro Bio's clinical data design will appear",
    backDisease:"← Change Disease", formTitle:"Case Entry Form",
    notice:"📋 Compliant with Macro Bio Co., Ltd. clinical data design. Patient info must be ", noticeStrong:"anonymized", noticeEnd:" before entry.",
    noticeAD:"⚠ Alzheimer's requires evaluation over ", noticeADStrong:"at least 3–6 months", noticeADEnd:"",
    noticeALS:"⚠ ALS requires ", noticeALSStrong:"long-term follow-up of at least 6–12 months", noticeALSEnd:". Focus on ALSFRS-R decline rate.",
    done:"Case data registered", doneSub:"The review team will update the status after confirmation (usually within 3–5 business days)", doneBtn:"Enter another case",
    prev:"← Back", next:"Next →", submit:"✓ Register", step:"STEP",
    caseId:"Case ID (anonymized)", caseIdPh:"e.g. PD-2026-001",
    institution:"Institution", institutionPh:"e.g. University Hospital",
    physician:"Physician", physicianPh:"e.g. Dr. Seiichi Tanaka",
    startDate:"Treatment start date", timePoint:"Assessment time point",
    age:"Age", agePh:"e.g. 68",
    sex:"Sex", male:"Male", female:"Female", other:"Other",
    required:"*Required",
    safetyTitle:"Safety Assessment (CTCAE v5.0)",
    aeLabel:"Adverse events (multiple selection)",
    gradeLabel:"Select Grade", saeLabel:"A serious adverse event (SAE) has occurred",
    saeWarn:"⚠ Please submit the SAE report form promptly. The office will be notified automatically.",
    notesLabel:"Notes", notesPh:"Free text for special findings",
    pdLabel:"Parkinson's Disease", pdSub:"MDS-UPDRS / Hoehn&Yahr / PDQ-39",
    pdStep1:"Basic Info", pdStep2:"Severity & Meds", pdStep3:"MDS-UPDRS", pdStep4:"QOL & Safety",
    hyTitle:"Hoehn & Yahr Classification", hySub:"Clearly define the patient's baseline status",
    medTitle:"Medication Management (ON/OFF)", medSub:"Record strictly to exclude drug effect confounding",
    medLabel:"Current medications", medPh:"e.g. L-dopa 300mg/day",
    ldopaTime:"Minutes since last dose", ldopaPh:"60",
    onOffLabel:"ON/OFF status at assessment",
    on:"ON state (drug effective)", off:"OFF state (drug worn off)", partialOn:"Partial ON", unknown:"Unknown",
    updrsTitle:"MDS-UPDRS Part III (Motor Function)", updrsSub:"[Most Important] Objective comparison before and after treatment (0–132)",
    updrsB:"Pre-treatment score", updrsA:"Current score", updrsBPh:"e.g. 42", updrsAPh:"e.g. 35",
    changeRate:"Change rate (auto-calculated)",
    pdq39:"PDQ-39 Total (0–100, lower is better)", pdq39Ph:"e.g. 42",
    duration:"Disease duration (years)", durationPh:"5",
    adLabel:"Alzheimer's Disease", adSub:"MMSE / CDR-SB / ADAS-Cog / ADL",
    adStep1:"Basic Info", adStep2:"Cognition", adStep3:"ADL & Behavior", adStep4:"Safety",
    diagName:"Confirmed diagnosis", mriCt:"MRI/CT findings", mriPh:"e.g. Hippocampal atrophy",
    proxy:"Consent obtained from proxy (family/legal guardian)",
    cdrTitle:"Severity (CDR / CDR-SB)", cdrSub:"[Most Important] Integrated assessment of cognition and daily function",
    cdrLabel:"CDR", cdrsb:"CDR-SB (0–18)", cdrsbPh:"e.g. 4.5",
    mmse:"MMSE (0–30)", mmsePh:"e.g. 22",
    moca:"MoCA (0–30)", mocaPh:"e.g. 20",
    adasCog:"ADAS-Cog (0–70, higher = worse)", adasPh:"e.g. 18",
    dasc21:"DASC-21 (21–84)", dasc21Ph:"e.g. 38",
    npiQ:"NPI-Q Severity total (0–36)", npiPh:"e.g. 6",
    careNote:"Caregiver assessment", careNotePh:"e.g. Patient responds more to name calls; nighttime wandering reduced",
    alsLabel:"ALS (Amyotrophic Lateral Sclerosis)", alsSub:"ALSFRS-R / FVC / Progression Events",
    alsStep1:"Basic Info", alsStep2:"ALSFRS-R", alsStep3:"Respiratory & Nutrition", alsStep4:"Safety",
    onsetSite:"Onset site", diagCriteria:"Diagnostic criteria",
    niv:"NIV (ventilator) in use", gastrostomy:"Gastrostomy performed", tracheostomy:"Tracheostomy performed", wheelChair:"Wheelchair user",
    alsfrsTitle:"ALSFRS-R (0–4 × 12 items = max 48)", alfssrsSub:"[Most Important] Assess whether the rate of decline is slowing",
    alsfrsTotal:"ALSFRS-R Total", alsfrsMax:"/ 48",
    fvc:"FVC (Forced Vital Capacity, %)", fvcPh:"72.4",
    svc:"SVC (Slow Vital Capacity, %)", svcPh:"68.0",
    spo2:"SpO₂ (resting, %)", spo2Ph:"97",
    fvcSevere:"⚠ Severe decline (consider NIV)", fvcWarn:"Caution (increase monitoring)", fvcNormal:"Within normal range",
    weight:"Body weight (kg)", bmi:"BMI",
    progressEvents:"Progression event record",
    nivDate:"NIV start", trachDate:"Tracheostomy", pegDate:"Gastrostomy",
    cancerLabel:"Cancer Treatment", cancerSub:"RECIST v1.1 / PFS / OS / CTCAE",
    cancerStep1:"Basic & Target", cancerStep2:"Imaging & RECIST", cancerStep3:"Markers & Survival", cancerStep4:"Safety",
    cancerType:"Cancer type", stage:"Disease stage", treatmentLine:"Treatment line", ps:"Performance Status (PS)",
    imageType:"Imaging type", imageDate:"Date of imaging",
    sumBaseline:"Target lesion sum: Baseline (mm)", sumCurrent:"Target lesion sum: Current (mm)",
    recistFinal:"RECIST v1.1 Final Assessment", autoCalc:"Auto-calculated",
    cea:"CEA (ng/mL)", ca199:"CA19-9 (U/mL)",
    pfsWeeks:"PFS duration (weeks)", pfsWPh:"e.g. 24",
    osStatus:"OS status", alive:"Alive", dead:"Deceased", lost:"Lost to follow-up",
    // Stroke
    strokeLabel:"Stroke (Cerebral Infarction)", strokeSub:"NIHSS / mRS / Barthel Index",
    strokeStep1:"Basic & Diagnosis", strokeStep2:"Neurological Assessment", strokeStep3:"ADL & QOL", strokeStep4:"Safety",
    onsetDate:"Date of onset", strokeType:"Stroke subtype",
    strokeLacunar:"Lacunar", strokeAthero:"Atherothrombotic", strokeCardio:"Cardioembolic", strokeOther:"Other",
    onsetTime:"Time since onset",
    tpaUsed:"t-PA (rt-PA) administered",
    nihssTitle:"NIHSS (NIH Stroke Scale)", nihssSub:"[Key] 0–42, higher = more severe",
    nihssScore:"NIHSS total score", nihssPh:"e.g. 8",
    mrsTitle:"mRS (modified Rankin Scale)", mrsSub:"Functional outcome, 0–6",
    mrsScore:"mRS score",
    mrsOpt0:"0: No symptoms", mrsOpt1:"1: Minor symptoms", mrsOpt2:"2: Slight disability", mrsOpt3:"3: Moderate", mrsOpt4:"4: Moderate–severe", mrsOpt5:"5: Severe", mrsOpt6:"6: Dead",
    barthelTitle:"Barthel Index (ADL)", barthelSub:"Activities of daily living, 0–100",
    barthelScore:"Barthel Index", barthelPh:"e.g. 65",
    fimScore:"FIM (optional)", fimPh:"e.g. 90",
    recurrence:"Stroke recurrence", hemorrhage:"Hemorrhagic transformation",
    // SCI
    sciLabel:"Spinal Cord Injury", sciSub:"AIS / ISNCSCI / SCIM-III",
    sciStep1:"Basic & Diagnosis", sciStep2:"Motor & Sensory", sciStep3:"ADL & Spasticity", sciStep4:"Safety",
    injuryDate:"Date of injury", injuryLevel:"Level of injury",
    levelCervical:"Cervical (C1–C8)", levelThoracic:"Thoracic (T1–T12)", levelLumbar:"Lumbar (L1–L5)", levelSacral:"Sacral (S1–S5)",
    aisGrade:"AIS (ASIA Impairment Scale)",
    aisA:"A: Complete", aisB:"B: Sensory incomplete", aisC:"C: Motor incomplete (<3)", aisD:"D: Motor incomplete (≥3)", aisE:"E: Normal",
    isncsciTitle:"ISNCSCI Motor & Sensory", isncsciSub:"[Key] Standard neurological assessment",
    motorUE:"Motor score - Upper Extremity (0–50)", motorUEPh:"e.g. 35",
    motorLE:"Motor score - Lower Extremity (0–50)", motorLEPh:"e.g. 20",
    sensoryPP:"Sensory - Pin Prick (0–112)", sensoryPPPh:"e.g. 80",
    sensoryLT:"Sensory - Light Touch (0–112)", sensoryLTPh:"e.g. 85",
    scimTitle:"SCIM-III (Spinal Cord Independence)", scimSub:"Independence in ADL, 0–100",
    scimScore:"SCIM-III total", scimPh:"e.g. 55",
    ashworthTitle:"Modified Ashworth Scale (Spasticity)",
    ashworthScore:"MAS (0–4)",
    ashworth0:"0: Normal", ashworth1:"1: Slight", ashworth1p:"1+: Slight–moderate", ashworth2:"2: Marked increase", ashworth3:"3: Considerable", ashworth4:"4: Rigid",
    autonomicDys:"Autonomic dysreflexia onset", pressureUlcer:"Pressure ulcer occurrence",
    // DM
    dmLabel:"Diabetes Mellitus", dmSub:"HbA1c / BG / Complications",
    dmStep1:"Basic & Diagnosis", dmStep2:"Glycemic Control", dmStep3:"Complications", dmStep4:"Safety",
    dmType:"Type of diabetes",
    dm1:"Type 1", dm2:"Type 2", dmGes:"Gestational", dmOther:"Other specific types",
    dmDuration:"Disease duration (years)", dmDurationPh:"e.g. 10",
    bmi:"BMI", bmiPh:"e.g. 24.5",
    complications:"Known complications",
    compNephro:"Nephropathy", compNeuro:"Neuropathy", compRetino:"Retinopathy", compCardio:"Cardiovascular", compNone:"None",
    hba1cTitle:"Glycemic Control Indices", hba1cSub:"[Key] HbA1c & blood glucose trends",
    hba1c:"HbA1c (%)", hba1cPh:"e.g. 7.2",
    fpg:"Fasting plasma glucose (mg/dL)", fpgPh:"e.g. 130",
    ppg2h:"2h post-prandial glucose (mg/dL)", ppg2hPh:"e.g. 180",
    timeInRange:"TIR (CGM time in range, %, opt.)", tirPh:"e.g. 65",
    nephroTitle:"Nephropathy", egfr:"eGFR (mL/min/1.73m²)", egfrPh:"e.g. 65",
    urineAlb:"Urine albumin (mg/gCr)", urineAlbPh:"e.g. 45",
    neuroVAS:"Neuropathy VAS (numbness/pain)", neuroVASPh:"0–10",
    retinoStage:"Retinopathy stage",
    retino0:"None", retino1:"Simple", retino2:"Pre-proliferative", retino3:"Proliferative",
    hypoEvents:"Hypoglycemic events (past 4 wks)", hypoPh:"e.g. 2",
    // Other
    otherLabel:"Other Disease", otherSub:"General-purpose form (free-text evaluations)",
    otherStep1:"Basic & Diagnosis", otherStep2:"Primary Outcomes", otherStep3:"QOL & ADL", otherStep4:"Safety",
    diseaseName:"Disease name (free text)", diseaseNamePh:"e.g. Interstitial pneumonia",
    icd10:"ICD-10 code (optional)", icd10Ph:"e.g. J84.1",
    diagDate:"Date of diagnosis / onset",
    primaryEval:"Primary endpoints (up to 3)",
    evalName:"Item name", evalNamePh:"e.g. 6-min walk distance",
    evalValue:"Value / result", evalValuePh:"e.g. 320",
    evalUnit:"Unit", evalUnitPh:"e.g. m",
    evalSupp:"Additional info", evalSuppPh:"Scale name, reference values, notes",
    qolDesc:"QOL & ADL (free text)", qolDescPh:"e.g. SF-36 PCS 45 / EQ-5D 0.72 / mostly independent",
    protocolTitle:"Protocol & Document Management",
    docLoginMsg:"Please log in to use this feature.",
    docDownloadBtn:"⬇ Download",
    docUploadedLabel:"Uploaded",
    docEmpty:"No documents have been published yet.",
    docLoadingMsg:"Loading…",
    docErrorMsg:"Failed to load the document list.",
    saeTitle:"SAE (Serious Adverse Event) Reporting",
    saeDesc:"Please submit the SAE report form promptly if a serious adverse event occurs.",
    saeBtn:"Report an SAE",
    safetyStatsTitle:"Safety Statistics Summary",
    safetyTotalCases:"Total Cases",
    safetySaeCount:"SAE Count",
    safetyOverallRate:"Overall SAE Rate",
    safetyByDiseaseTitle:"SAE Rate by Disease",
    safetyTopEventsTitle:"Most Common Adverse Events (Top 10)",
    safetyEmpty:"No aggregated data is available yet.",
    safetyLoadingMsg:"Loading statistics…",
    safetyErrorMsg:"Failed to load statistics.",
    safetyCasesUnit:"cases",
    footerTitle:"Join the Research Network",
    footerSub:"Eligibility: Licensed physicians or institutional researchers\nApproval typically within 3–5 business days",
    footerBtn:"Apply for Membership",
    footerCopy:"© 2026 SC-NET Japan　│　Physicians-only closed platform　│　Supervised by Macro Bio Co., Ltd.",
    // Dosage Info (common)
    dosageTitle:"Conditioned Medium Administration Info",
    dosageSub:"Please record details of the conditioned medium administered",
    dailyDose:"1. Daily Dosage (ml)", dailyDosePh:"e.g. 90",
    treatmentPeriod:"2. Treatment Period & Sessions", treatmentPeriodPh:"e.g. 4 weeks, once/week, 4 sessions total",
    serumSource:"3. Source & Volume of Conditioned Medium Used",
    serumRow1:"Deciduous Tooth Pulp-derived (ml)", serumRow1Ph:"e.g. 30",
    serumRow2:"Umbilical Cord-derived (ml)",       serumRow2Ph:"e.g. 30",
    serumRow3:"Bone Marrow-derived (ml)",          serumRow3Ph:"e.g. 30",
    serumOther:"Other source / details",           serumOtherPh:"e.g. Adipose-derived 20ml",
    caseAuthor:"Author", caseDate:"Date",
    authLoginTitle:"Login", authEmailLabel:"Email", authPasswordLabel:"Password",
    authLoginBtn:"Login", authLoginError:"Incorrect email or password",
    authNoAccount:"Don't have an account?", authGoApply:"Apply here",
    authHaveAccount:"Already have an account?", authGoLogin:"Login here",
    authForgotPassword:"Forgot your password?", authLogoutBtn:"Logout",
    authApplyTitle:"New Application", authDisplayNameLabel:"Full Name", authInstitutionLabel:"Institution",
    authApplySubmitBtn:"Apply", authApplySuccess:"Your application has been received. Please wait for approval.",
    authResetRequestTitle:"Reset Password",
    authResetRequestDesc:"Enter your registered email address. We'll send you a reset link.",
    authResetRequestSubmitBtn:"Send",
    authResetRequestSent:"An email has been sent. Please use the link to reset your password.",
    authChangePasswordTitle:"Change Password", authMustChangeNotice:"You must change your password on first login.",
    authCurrentPasswordLabel:"Current Password", authNewPasswordLabel:"New Password",
    authNewPasswordConfirmLabel:"Confirm New Password", authChangePasswordSubmitBtn:"Change",
    authChangePasswordSuccess:"Password changed successfully.", authPasswordMismatch:"New passwords do not match",
    authResetConfirmTitle:"Set New Password", authResetConfirmSubmitBtn:"Set Password",
    authResetConfirmSuccess:"Password has been reset. Please log in.",
    authResetConfirmInvalid:"This link is invalid or expired. Please request a new reset link.",
    authLoginRequiredTitle:"Login Required",
    authLoginRequiredDesc:"You must log in to enter case data. If you don't have an account, please apply.",
    authMineLoginRequired:"You must log in to view \"My Data\".", authWelcome:"Welcome",
    prefectureLabel:"Prefecture", prefecturePh:"Select",
    dashboardDeleteMineBtn:"Delete My Data",
    dashboardDeleteMineConfirm:"This will delete all case data you entered. Continue?",
  },
  mn: {
    siteName: "SC-NET Japan",
    siteTagline: "Үүдэл эсийн ялгарал дахь клиник судалгааны сүлжээ",
    login: "Нэвтрэх", register: "Бүртгүүлэх",
    heroTitle1: "Хүний үүдэл эсийн өсгөвөрлөсөн шингэний", heroTitleItalic: "судсаар хийх эмчилгээ",
    heroTitle2: "Клиник судалгааны мэдээлэл хуваалцах сүлжээ",
    heroSub: "Паркинсоны өвчин / Альцгеймерийн өвчин / ALS / Хорт хавдар — Macro Bio зохион байгуулсан",
    btnInput: "+ Тохиолдлын мэдээлэл оруулах", btnView: "Тохиолдлын мэдээлэл харах",
    tab1:"Тохиолдлууд", tab2:"✏ Тохиолдол оруулах", tab3:"Протокол ба баримт бичиг", tab4:"Аюулгүй байдлын мэдээлэл",
    tabDashboard:"📊 Хяналтын самбар",
    dashboardTitle:"📊 Хяналтын самбар", dashboardSub:"Бүртгэгдсэн тохиолдлын мэдээлэл харуулах ба шинжилгээ",
    totalCases:"Нийт тохиолдол", byDisease:"Өвчнөөр", casesPerDisease:"Өвчин тус бүрийн тохиолдол",
    timelineTitle:"Хугацааны явц", filterByDisease:"Өвчнөөр шүүх", allDiseases:"Бүх өвчин",
    selectCase:"Тохиолдол сонгох", selectCasePh:"— Тохиолдол сонгоно уу —",
    histogramTitle:"Үндсэн үзүүлэлтүүдийн тархалт", noData:"Мэдээлэл байхгүй. Тохиолдол оруулсны дараа энд харагдана.",
    exportCSV:"CSV экспорт", reseedSample:"Жишээ дахин ачаалах", clearAll:"Бүх мэдээллийг устгах",
    confirmClear:"Бүх мэдээллийг устгахдаа итгэлтэй байна уу? Энэ үйлдлийг буцаах боломжгүй.",
    confirmReseed:"Одоогийн мэдээллийг жишээ мэдээллээр дарж бичнэ. Үргэлжлүүлэх үү?",
    timelineNeedMore:"📈 Хугацааны явцын графикийн хувьд ижил тохиолдлын дугаараар 2 ба түүнээс дээш цэгийн мэдээлэл шаардлагатай.",
    savedCaseInfo:"Хадгалсан тохиолдол",
    subTabMine:"📁 Миний мэдээлэл", subTabPublic:"🌐 Нийтлэгдсэн тохиолдол",
    publicSub:"Хянагдсан, бусад байгууллагаас нийтэлсэн судалгааны тохиолдлууд",
    searchPlaceholder:"Тохиолдлын дугаар, байгууллага, өвчнөөр хайх…",
    sortNewest:"Шинээр нь", sortOldest:"Хуучнаар нь",
    matchedCases:"Тохирох тохиолдол", noMatch:"Нөхцөлд тохирох тохиолдол байхгүй",
    confirmDeleteCase:"Энэ тохиолдлын мэдээллийг устгах уу?", delete:"Устгах",
    edit:"Засах", editFormTitle:"Тохиолдол засах", editingMode:"Засаж байна",
    errorTitle:"Оруулсан мэдээлэлд алдаа байна", errorHint:"Заавал бөглөх талбар дутуу эсвэл утга нь хүрээнээс гадуур байна. Шалгана уу.",
    errorRequired:"Заавал бөглөнө", errorNumeric:"Тоо оруулна уу", errorRange:"Хүрээнээс гадуур",
    exportPDF:"PDF татах",
    trendSectionTitle:"Хувь хүний өгөгдлийн явц",
    trendIndividual:"Дэлгэрэнгүй", trendCompare:"Харьцуулах", trendMetric:"Үзүүлэлт", trendMean:"Дундаж", trendGrid:"Бүгд",
    selectMetric:"Үнэлгээний үзүүлэлт сонгох", selectCasesToCompare:"Харьцуулах тохиолдол сонгох (хамгийн ихдээ 8)",
    compareTitle:"Тохиолдол харьцуулах график", compareHint:"Тохиолдлуудыг давхарлан харах", compareNeedSelection:"Дор хаяж 1 тохиолдол сонгоно уу",
    individualBreakdown:"Үзүүлэлт тус бүрийн графикууд",
    metricFocusSub:"Энэ үзүүлэлттэй бүх тохиолдол", meanTitle:"Дундаж явц", meanSub:"Улаан=дундаж, саарал=хувь хүн", meanLabel:"Дундаж",
    gridSub:"Тохиолдол тус бүрийн жижиг график", casesUnit:" тохиолдол", pointsUnit:" цэг", recordsUnit:" бичлэг",
    noMetricData:"Энэ үзүүлэлтийн мэдээлэл байхгүй", noDataForMetric:"Энэ өвчин/үзүүлэлтийн мэдээлэл байхгүй",
    caseCount:"Бүртгэгдсэн тохиолдол", cases:"", btnAddCase:"+ Тохиолдол оруулах",
    selectDisease:"Өвчнийг сонгоно уу",
    selectDiseaseSub:"Macro Bio-ийн клиник мэдээлэл цуглуулах загварт нийцсэн маягт гарч ирнэ",
    backDisease:"← Өвчнийг дахин сонгох", formTitle:"Тохиолдол оруулах маягт",
    notice:"📋 Macro Bio Co., Ltd. загварт нийцсэн. Өвчтөний мэдээллийг заавал ", noticeStrong:"нэргүй болгож", noticeEnd:" оруулна уу.",
    noticeAD:"⚠ Альцгеймерийн өвчний үнэлгээ ", noticeADStrong:"хамгийн багадаа 3–6 сарын хугацаанд", noticeADEnd:" хийгдэх шаардлагатай",
    noticeALS:"⚠ ALS өвчинд ", noticeALSStrong:"хамгийн багадаа 6–12 сарын урт хугацааны хяналт", noticeALSEnd:" шаардлагатай. ALSFRS-R-ийн бууралтын хурдыг анхаарна уу.",
    done:"Тохиолдлын мэдээлэл бүртгэгдлээ", doneSub:"Хянан магадлагч баг баталгаажуулсны дараа статус шинэчлэгдэнэ (ихэвчлэн 3–5 ажлын өдрийн дотор)", doneBtn:"Дахин оруулах",
    prev:"← Өмнөх", next:"Дараах →", submit:"✓ Бүртгэх", step:"АЛХАМ",
    caseId:"Тохиолдлын дугаар (нэргүй)", caseIdPh:"жнь: PD-2026-001",
    institution:"Эмнэлгийн байгууллага", institutionPh:"жнь: Их сургуулийн эмнэлэг",
    physician:"Эмчийн нэр", physicianPh:"жнь: Танака Сэйичи",
    startDate:"Эмчилгээ эхлэх огноо", timePoint:"Үнэлгээний цаг үе",
    age:"Нас", agePh:"жнь: 68",
    sex:"Хүйс", male:"Эрэгтэй", female:"Эмэгтэй", other:"Бусад",
    required:"*Заавал",
    safetyTitle:"Аюулгүй байдлын үнэлгээ (CTCAE v5.0)",
    aeLabel:"Гаж нөлөөнүүд (олон сонголт боломжтой)",
    gradeLabel:"Зэрэглэл сонгох", saeLabel:"Ноцтой гаж нөлөө (SAE) илэрсэн",
    saeWarn:"⚠ SAE-ийн тайлангийн маягтыг яаралтай гаргана уу. Алба автоматаар мэдэгдэгдэнэ.",
    notesLabel:"Тэмдэглэл", notesPh:"Тусгай олдворуудыг чөлөөтэй бичих",
    pdLabel:"Паркинсоны өвчин", pdSub:"MDS-UPDRS / Hoehn&Yahr / PDQ-39",
    pdStep1:"Үндсэн мэдээлэл", pdStep2:"Хүнд байдал ба эм", pdStep3:"MDS-UPDRS", pdStep4:"ЧАЧ ба Аюулгүй байдал",
    hyTitle:"Хоэн ба Яр ангилал", hySub:"Өвчтөний суурь байдлыг тодорхой тодорхойлох",
    medTitle:"Эмийн удирдлага (ON/OFF)", medSub:"Эмийн нөлөөтэй хольцлогдохоос зайлсхийхийн тулд нарийвчлан бичнэ",
    medLabel:"Одоогийн эмийн жагсаалт", medPh:"жнь: L-dopa 300мг/өдөр",
    ldopaTime:"Сүүлийн тун уусан минутын тоо", ldopaPh:"60",
    onOffLabel:"Үнэлгээний үеийн ON/OFF байдал",
    on:"ON (эм үйлчилж байна)", off:"OFF (эм дууссан)", partialOn:"Хагас ON", unknown:"Тодорхойгүй",
    updrsTitle:"MDS-UPDRS III хэсэг (Хөдөлгөөний үйл ажиллагаа)", updrsSub:"[Хамгийн чухал] Эмчилгээний өмнө ба дараа объектив харьцуулалт (0–132)",
    updrsB:"Эмчилгээний өмнөх оноо", updrsA:"Одоогийн оноо", updrsBPh:"жнь: 42", updrsAPh:"жнь: 35",
    changeRate:"Өөрчлөлтийн хэмжээ (автоматаар тооцсон)",
    pdq39:"PDQ-39 Нийт оноо (0–100, бага нь дээр)", pdq39Ph:"жнь: 42",
    duration:"Өвчний үргэлжлэх хугацаа (жил)", durationPh:"5",
    adLabel:"Альцгеймерийн өвчин", adSub:"MMSE / CDR-SB / ADAS-Cog / ADL",
    adStep1:"Үндсэн мэдээлэл", adStep2:"Танин мэдэхүй", adStep3:"ADL ба Зан байдал", adStep4:"Аюулгүй байдал",
    diagName:"Баталгаажсан оношлогоо", mriCt:"MRI/CT олдвор", mriPh:"жнь: Гиппокампийн атрофи",
    proxy:"Орлогч (гэр бүл/хуулийн төлөөлөгч) зөвшөөрөл авсан",
    cdrTitle:"Хүнд байдлын үнэлгээ (CDR / CDR-SB)", cdrSub:"[Хамгийн чухал] Танин мэдэхүй ба өдөр тутмын үйл ажиллагааны цогц үнэлгээ",
    cdrLabel:"CDR", cdrsb:"CDR-SB (0–18)", cdrsbPh:"жнь: 4.5",
    mmse:"MMSE (0–30)", mmsePh:"жнь: 22",
    moca:"MoCA (0–30)", mocaPh:"жнь: 20",
    adasCog:"ADAS-Cog (0–70, өндөр нь муу)", adasPh:"жнь: 18",
    dasc21:"DASC-21 (21–84)", dasc21Ph:"жнь: 38",
    npiQ:"NPI-Q Хүнд байдлын нийт оноо (0–36)", npiPh:"жнь: 6",
    careNote:"Асрагчийн үнэлгээ", careNotePh:"жнь: Нэрийг нь дуудахад илүү хариу үзүүлж байна",
    alsLabel:"ALS (Хажуу талын амиотрофик склероз)", alsSub:"ALSFRS-R / FVC / Дэвшилтийн үйл явдал",
    alsStep1:"Үндсэн мэдээлэл", alsStep2:"ALSFRS-R", alsStep3:"Амьсгал ба Тэжээл", alsStep4:"Аюулгүй байдал",
    onsetSite:"Эхлэлийн хэсэг", diagCriteria:"Оношлогооны шалгуур",
    niv:"NIV (агааржуулагч) хэрэглэж байна", gastrostomy:"Гастростоми хийгдсэн", tracheostomy:"Трахеостоми хийгдсэн", wheelChair:"Өглөөний тэрэг хэрэглэгч",
    alsfrsTitle:"ALSFRS-R (0–4 × 12 зүйл = дээд 48)", alfssrsSub:"[Хамгийн чухал] Бууралтын хурд удаашрал эсэхийг шалгана",
    alsfrsTotal:"ALSFRS-R Нийт", alsfrsMax:"/ 48",
    fvc:"FVC (Хүчлэлтийн амин чадал, %)", fvcPh:"72.4",
    svc:"SVC (Удаан амин чадал, %)", svcPh:"68.0",
    spo2:"SpO₂ (амрах үед, %)", spo2Ph:"97",
    fvcSevere:"⚠ Ноцтой бууралт (NIV авч үзэх)", fvcWarn:"Анхааруулга (хяналтыг нэмэгдүүлэх)", fvcNormal:"Хэвийн хэмжээнд байна",
    weight:"Биеийн жин (кг)", bmi:"БЖИ",
    progressEvents:"Дэвшилтийн үйл явдлын бүртгэл",
    nivDate:"NIV эхлэх", trachDate:"Трахеостоми", pegDate:"Гастростоми",
    cancerLabel:"Хорт хавдрын эмчилгээ", cancerSub:"RECIST v1.1 / PFS / OS / CTCAE",
    cancerStep1:"Үндсэн & Зорилтот", cancerStep2:"Зурагжуулалт & RECIST", cancerStep3:"Маркер & Амьдрах хугацаа", cancerStep4:"Аюулгүй байдал",
    cancerType:"Хорт хавдрын төрөл", stage:"Өвчний үе шат", treatmentLine:"Эмчилгээний шат", ps:"Гүйцэтгэлийн статус (PS)",
    imageType:"Зурагжуулалтын төрөл", imageDate:"Зурагжуулалтын огноо",
    sumBaseline:"Зорилтот гэмтлийн нийлбэр: Суурь (мм)", sumCurrent:"Зорилтот гэмтлийн нийлбэр: Одоогийн (мм)",
    recistFinal:"RECIST v1.1 Эцсийн үнэлгээ", autoCalc:"Автоматаар тооцсон",
    cea:"CEA (нг/мл)", ca199:"CA19-9 (Н/мл)",
    pfsWeeks:"PFS хугацаа (долоо хоног)", pfsWPh:"жнь: 24",
    osStatus:"OS статус", alive:"Амьд", dead:"Нас барсан", lost:"Хяналтаас гарсан",
    // Stroke
    strokeLabel:"Тархины цус харвалт", strokeSub:"NIHSS / mRS / Barthel Index",
    strokeStep1:"Үндсэн ба онош", strokeStep2:"Мэдрэлийн үнэлгээ", strokeStep3:"ADL & QOL", strokeStep4:"Аюулгүй байдал",
    onsetDate:"Эхэлсэн огноо", strokeType:"Төрөл",
    strokeLacunar:"Лакунар", strokeAthero:"Атеротромбот", strokeCardio:"Зүрхний гаралтай эмболи", strokeOther:"Бусад",
    onsetTime:"Эхэлснээс хойшхи хугацаа",
    tpaUsed:"t-PA (rt-PA) хийгдсэн",
    nihssTitle:"NIHSS (NIH цус харвалтын шкал)", nihssSub:"[Чухал] 0–42, өндөр = илүү хүнд",
    nihssScore:"NIHSS нийт оноо", nihssPh:"жнь: 8",
    mrsTitle:"mRS (Rankin шкал)", mrsSub:"Үйл ажиллагааны прогноз, 0–6",
    mrsScore:"mRS оноо",
    mrsOpt0:"0: Шинж тэмдэггүй", mrsOpt1:"1: Бага зэргийн шинж", mrsOpt2:"2: Хөнгөн", mrsOpt3:"3: Дунд", mrsOpt4:"4: Дунд–хүнд", mrsOpt5:"5: Хүнд", mrsOpt6:"6: Нас барсан",
    barthelTitle:"Barthel Index (ADL)", barthelSub:"Өдөр тутмын үйл ажиллагаа, 0–100",
    barthelScore:"Barthel Index", barthelPh:"жнь: 65",
    fimScore:"FIM (сонголтоор)", fimPh:"жнь: 90",
    recurrence:"Цус харвалт дахисан", hemorrhage:"Цус харвалтын цусархаг хувирал",
    // SCI
    sciLabel:"Нугасны гэмтэл", sciSub:"AIS / ISNCSCI / SCIM-III",
    sciStep1:"Үндсэн ба онош", sciStep2:"Хөдөлгөөн & мэдрэхүй", sciStep3:"ADL & булчингийн чангарал", sciStep4:"Аюулгүй байдал",
    injuryDate:"Гэмтсэн огноо", injuryLevel:"Гэмтлийн түвшин",
    levelCervical:"Хүзүү (C1–C8)", levelThoracic:"Цээж (T1–T12)", levelLumbar:"Бүсэлхий (L1–L5)", levelSacral:"Ариун (S1–S5)",
    aisGrade:"AIS (ASIA шкал)",
    aisA:"A: Бүрэн саажилт", aisB:"B: Мэдрэхүйн бүрэн бус", aisC:"C: Хөдөлгөөний бүрэн бус (<3)", aisD:"D: Хөдөлгөөний бүрэн бус (≥3)", aisE:"E: Хэвийн",
    isncsciTitle:"ISNCSCI Хөдөлгөөн & мэдрэхүй", isncsciSub:"[Чухал] Стандарт мэдрэлийн үнэлгээ",
    motorUE:"Хөдөлгөөн - Дээд мөч (0–50)", motorUEPh:"жнь: 35",
    motorLE:"Хөдөлгөөн - Доод мөч (0–50)", motorLEPh:"жнь: 20",
    sensoryPP:"Мэдрэхүй - Pin Prick (0–112)", sensoryPPPh:"жнь: 80",
    sensoryLT:"Мэдрэхүй - Light Touch (0–112)", sensoryLTPh:"жнь: 85",
    scimTitle:"SCIM-III (Нугасны бие даалт)", scimSub:"ADL бие даалт, 0–100",
    scimScore:"SCIM-III нийт", scimPh:"жнь: 55",
    ashworthTitle:"Modified Ashworth Scale (Чангарал)",
    ashworthScore:"MAS (0–4)",
    ashworth0:"0: Хэвийн", ashworth1:"1: Бага", ashworth1p:"1+: Бага–дунд", ashworth2:"2: Илэрхий нэмэгдсэн", ashworth3:"3: Их хэмжээний", ashworth4:"4: Хатуу",
    autonomicDys:"Вегетатив рефлексийн эмгэг үүсэх", pressureUlcer:"Хэвтрийн шарх үүсэх",
    // DM
    dmLabel:"Чихрийн шижин", dmSub:"HbA1c / Цусны сахар / Хүндрэл",
    dmStep1:"Үндсэн ба онош", dmStep2:"Сахар хяналт", dmStep3:"Хүндрэлийн үнэлгээ", dmStep4:"Аюулгүй байдал",
    dmType:"Чихрийн шижингийн төрөл",
    dm1:"1-р хэлбэр", dm2:"2-р хэлбэр", dmGes:"Жирэмсний", dmOther:"Бусад",
    dmDuration:"Өвчилсөн хугацаа (жил)", dmDurationPh:"жнь: 10",
    bmi:"BMI", bmiPh:"жнь: 24.5",
    complications:"Мэдэгдсэн хүндрэлүүд",
    compNephro:"Бөөрний эмгэг", compNeuro:"Мэдрэлийн эмгэг", compRetino:"Нүдний эмгэг", compCardio:"Зүрх судасны эмгэг", compNone:"Байхгүй",
    hba1cTitle:"Сахар хяналтын үзүүлэлт", hba1cSub:"[Чухал] HbA1c ба цусны сахрын чиг хандлага",
    hba1c:"HbA1c (%)", hba1cPh:"жнь: 7.2",
    fpg:"Өлөн үеийн сахар (мг/дл)", fpgPh:"жнь: 130",
    ppg2h:"Хоолны дараах 2 цагийн сахар (мг/дл)", ppg2hPh:"жнь: 180",
    timeInRange:"TIR (CGM, %, сонголтоор)", tirPh:"жнь: 65",
    nephroTitle:"Бөөрний эмгэгийн үнэлгээ", egfr:"eGFR (мл/мин/1.73м²)", egfrPh:"жнь: 65",
    urineAlb:"Шээсний альбумин (мг/гCr)", urineAlbPh:"жнь: 45",
    neuroVAS:"Мэдрэлийн VAS (бадайралт/өвдөлт)", neuroVASPh:"0–10",
    retinoStage:"Нүдний эмгэгийн үе шат",
    retino0:"Байхгүй", retino1:"Энгийн", retino2:"Үржилтийн өмнөх", retino3:"Үржилтийн",
    hypoEvents:"Бага сахартлалтын тохиолдол (сүүлийн 4 долоо хоног)", hypoPh:"жнь: 2",
    // Other
    otherLabel:"Бусад өвчин", otherSub:"Ерөнхий маягт (чөлөөт бичвэр)",
    otherStep1:"Үндсэн ба онош", otherStep2:"Үндсэн үнэлгээ", otherStep3:"QOL & ADL", otherStep4:"Аюулгүй байдал",
    diseaseName:"Өвчний нэр (чөлөөт)", diseaseNamePh:"жнь: Завсрын уушги",
    icd10:"ICD-10 код (сонголтоор)", icd10Ph:"жнь: J84.1",
    diagDate:"Оношилсон / эхэлсэн огноо",
    primaryEval:"Үндсэн үнэлгээ (хамгийн ихдээ 3)",
    evalName:"Зүйлийн нэр", evalNamePh:"жнь: 6 минутын алхалт",
    evalValue:"Утга / үр дүн", evalValuePh:"жнь: 320",
    evalUnit:"Нэгж", evalUnitPh:"жнь: м",
    evalSupp:"Нэмэлт мэдээлэл", evalSuppPh:"Шкалын нэр, лавлах утга гэх мэт",
    qolDesc:"QOL & ADL (чөлөөт бичвэр)", qolDescPh:"жнь: SF-36 PCS 45 / EQ-5D 0.72 / голчлон бие даасан",
    protocolTitle:"Протокол ба баримт бичгийн удирдлага",
    docLoginMsg:"Энэ функцийг ашиглахын тулд нэвтэрнэ үү.",
    docDownloadBtn:"⬇ Татаж авах",
    docUploadedLabel:"Байршуулсан",
    docEmpty:"Одоогоор бүртгэгдсэн баримт бичиг байхгүй байна.",
    docLoadingMsg:"Уншиж байна…",
    docErrorMsg:"Баримт бичгийн жагсаалтыг татахад алдаа гарлаа.",
    saeTitle:"SAE (Ноцтой гаж нөлөө) тайлагнах",
    saeDesc:"Ноцтой гаж нөлөө гарсан тохиолдолд тайлангийн маягтыг яаралтай гаргана уу.",
    saeBtn:"SAE тайлагнах",
    safetyStatsTitle:"Аюулгүй байдлын статистикийн хураангуй",
    safetyTotalCases:"Нийт тохиолдол",
    safetySaeCount:"SAE тоо",
    safetyOverallRate:"Нийт SAE-ийн түвшин",
    safetyByDiseaseTitle:"Өвчнөөр ангилсан SAE-ийн түвшин",
    safetyTopEventsTitle:"Түгээмэл гаж нөлөө (эхний 10)",
    safetyEmpty:"Одоогоор нэгтгэх мэдээлэл байхгүй байна.",
    safetyLoadingMsg:"Статистик уншиж байна…",
    safetyErrorMsg:"Статистик мэдээллийг татахад алдаа гарлаа.",
    safetyCasesUnit:"тохиолдол",
    footerTitle:"Судалгааны сүлжээнд нэгдэх",
    footerSub:"Эрх: Эмчийн зөвшөөрөлтэй эсвэл эрүүл мэндийн байгууллагатай холбоотой судлаач\nБаталгаажуулалт ихэвчлэн 3–5 ажлын өдрийн дотор",
    footerBtn:"Гишүүнд элсэх хүсэлт",
    footerCopy:"© 2026 SC-NET Japan　│　Зөвхөн эмч нарт зориулсан хаалттай платформ　│　Macro Bio Co., Ltd. хяналт",
    // Тунгийн мэдээлэл (нийтлэг)
    dosageTitle:"Үүдэл эсийн ялгаралт хийх мэдээлэл",
    dosageSub:"Хийсэн ялгаралтын дэлгэрэнгүйг бүртгэнэ үү",
    dailyDose:"1. Өдрийн тун (мл)", dailyDosePh:"жнь: 90",
    treatmentPeriod:"2. Эмчилгээний хугацаа ба удаа", treatmentPeriodPh:"жнь: 4 долоо хоног, долоо хоногт 1 удаа, нийт 4 удаа",
    serumSource:"3. Ашигласан ялгаралтын эх үүсвэр ба тун",
    serumRow1:"Сүүн шүднээс гаралтай (мл)", serumRow1Ph:"жнь: 30",
    serumRow2:"Хүйснээс гаралтай (мл)",     serumRow2Ph:"жнь: 30",
    serumRow3:"Яс чөмгөөс гаралтай (мл)",   serumRow3Ph:"жнь: 30",
    serumOther:"Бусад эх үүсвэр / дэлгэрэнгүй", serumOtherPh:"жнь: Өөхний эдэд суурилсан 20мл",
    caseAuthor:"Зохиогч", caseDate:"Огноо",
    authLoginTitle:"Нэвтрэх", authEmailLabel:"И-мэйл", authPasswordLabel:"Нууц үг",
    authLoginBtn:"Нэвтрэх", authLoginError:"И-мэйл эсвэл нууц үг буруу байна",
    authNoAccount:"Бүртгэлгүй юу?", authGoApply:"Энд хүсэлт гаргах",
    authHaveAccount:"Бүртгэлтэй юу?", authGoLogin:"Энд нэвтрэх",
    authForgotPassword:"Нууц үгээ мартсан уу?", authLogoutBtn:"Гарах",
    authApplyTitle:"Шинэ хүсэлт", authDisplayNameLabel:"Овог нэр", authInstitutionLabel:"Байгууллага",
    authApplySubmitBtn:"Хүсэлт илгээх", authApplySuccess:"Таны хүсэлтийг хүлээн авлаа. Баталгаажуулалтыг хүлээнэ үү.",
    authResetRequestTitle:"Нууц үг сэргээх",
    authResetRequestDesc:"Бүртгэлтэй и-мэйл хаягаа оруулна уу. Бид танд сэргээх холбоос илгээнэ.",
    authResetRequestSubmitBtn:"Илгээх",
    authResetRequestSent:"И-мэйл илгээгдлээ. Холбоосоор орж нууц үгээ сэргээнэ үү.",
    authChangePasswordTitle:"Нууц үг солих", authMustChangeNotice:"Анх удаа нэвтэрсэн тул нууц үгээ солих шаардлагатай.",
    authCurrentPasswordLabel:"Одоогийн нууц үг", authNewPasswordLabel:"Шинэ нууц үг",
    authNewPasswordConfirmLabel:"Шинэ нууц үг (баталгаажуулах)", authChangePasswordSubmitBtn:"Солих",
    authChangePasswordSuccess:"Нууц үг амжилттай солигдлоо.", authPasswordMismatch:"Шинэ нууц үг таарахгүй байна",
    authResetConfirmTitle:"Шинэ нууц үг тохируулах", authResetConfirmSubmitBtn:"Тохируулах",
    authResetConfirmSuccess:"Нууц үг сэргээгдлээ. Нэвтэрнэ үү.",
    authResetConfirmInvalid:"Холбоос хүчингүй эсвэл хугацаа дууссан байна. Дахин хүсэлт гаргана уу.",
    authLoginRequiredTitle:"Нэвтрэх шаардлагатай",
    authLoginRequiredDesc:"Судалгааны мэдээлэл оруулахын тулд нэвтэрсэн байх шаардлагатай. Бүртгэлгүй бол хүсэлт гаргана уу.",
    authMineLoginRequired:"\"Миний мэдээлэл\"-ийг харахын тулд нэвтэрсэн байх шаардлагатай.", authWelcome:"Тавтай морил",
    prefectureLabel:"Мужийн нэр (Япон)", prefecturePh:"Сонгоно уу",
    dashboardDeleteMineBtn:"Миний мэдээллийг устгах",
    dashboardDeleteMineConfirm:"Таны оруулсан бүх судалгааны мэдээллийг устгана. Үргэлжлүүлэх үү?",
  },
  th: {
    siteName: "SC-NET Japan",
    siteTagline: "เครือข่ายวิจัยทางคลินิกด้วยสารสกัดจากเซลล์ต้นกำเนิด",
    login: "เข้าสู่ระบบ", register: "สมัครสมาชิก",
    heroTitle1: "สารสกัดจากเซลล์ต้นกำเนิดมนุษย์", heroTitleItalic: "การให้ทางหลอดเลือดดำ",
    heroTitle2: "เครือข่ายแชร์ข้อมูลวิจัยทางคลินิก",
    heroSub: "พาร์กินสัน / อัลไซเมอร์ / ALS / มะเร็ง — ออกแบบตาม Macro Bio",
    btnInput: "+ บันทึกข้อมูลผู้ป่วย", btnView: "ดูข้อมูลผู้ป่วย",
    tab1:"ข้อมูลผู้ป่วย", tab2:"✏ บันทึกผู้ป่วย", tab3:"โปรโตคอลและเอกสาร", tab4:"ข้อมูลความปลอดภัย",
    tabDashboard:"📊 แดชบอร์ด",
    dashboardTitle:"📊 แดชบอร์ด", dashboardSub:"การแสดงและวิเคราะห์ข้อมูลผู้ป่วยที่บันทึก",
    totalCases:"จำนวนผู้ป่วยทั้งหมด", byDisease:"ตามโรค", casesPerDisease:"จำนวนผู้ป่วยต่อโรค",
    timelineTitle:"แนวโน้มตามเวลา", filterByDisease:"กรองตามโรค", allDiseases:"ทุกโรค",
    selectCase:"เลือกผู้ป่วย", selectCasePh:"— เลือกผู้ป่วย —",
    histogramTitle:"การกระจายของตัวชี้วัดหลัก", noData:"ยังไม่มีข้อมูล กรอกข้อมูลผู้ป่วยแล้วจะแสดงที่นี่",
    exportCSV:"ส่งออก CSV", reseedSample:"โหลดข้อมูลตัวอย่างใหม่", clearAll:"ลบข้อมูลทั้งหมด",
    confirmClear:"คุณแน่ใจหรือว่าต้องการลบข้อมูลทั้งหมด? การกระทำนี้ไม่สามารถยกเลิกได้",
    confirmReseed:"การกระทำนี้จะเขียนทับข้อมูลปัจจุบันด้วยข้อมูลตัวอย่าง ดำเนินการต่อ?",
    timelineNeedMore:"📈 กราฟแนวโน้มต้องมีข้อมูลจากจุดเวลา 2 จุดขึ้นไปสำหรับ ID ผู้ป่วยเดียวกัน",
    savedCaseInfo:"ผู้ป่วยที่บันทึก",
    subTabMine:"📁 ข้อมูลของฉัน", subTabPublic:"🌐 กรณีศึกษาสาธารณะ",
    publicSub:"กรณีศึกษาที่ผ่านการตรวจสอบและเปิดเผยต่อสาธารณะจากสถาบันอื่น",
    searchPlaceholder:"ค้นหาตาม ID ผู้ป่วย สถาบัน หรือโรค…",
    sortNewest:"ใหม่ก่อน", sortOldest:"เก่าก่อน",
    matchedCases:"ผู้ป่วยที่ตรง", noMatch:"ไม่มีผู้ป่วยที่ตรงกับเงื่อนไข",
    confirmDeleteCase:"ลบบันทึกผู้ป่วยนี้?", delete:"ลบ",
    edit:"แก้ไข", editFormTitle:"แก้ไขผู้ป่วย", editingMode:"กำลังแก้ไข",
    errorTitle:"มีข้อผิดพลาดในข้อมูลที่กรอก", errorHint:"มีช่องที่จำเป็นต้องกรอกหรือค่าอยู่นอกช่วงที่กำหนด กรุณาตรวจสอบ",
    errorRequired:"จำเป็นต้องกรอก", errorNumeric:"ต้องเป็นตัวเลข", errorRange:"นอกช่วง",
    exportPDF:"ส่งออก PDF",
    trendSectionTitle:"แนวโน้มข้อมูลรายบุคคล",
    trendIndividual:"รายละเอียด", trendCompare:"เปรียบเทียบ", trendMetric:"โฟกัสตัวชี้วัด", trendMean:"ค่าเฉลี่ยโรค", trendGrid:"ทั้งหมด",
    selectMetric:"เลือกตัวชี้วัด", selectCasesToCompare:"เลือกผู้ป่วยเพื่อเปรียบเทียบ (สูงสุด 8)",
    compareTitle:"กราฟเปรียบเทียบ", compareHint:"เลือกผู้ป่วยเพื่อซ้อนกราฟ", compareNeedSelection:"กรุณาเลือกผู้ป่วยอย่างน้อย 1 ราย",
    individualBreakdown:"กราฟแยกตามตัวชี้วัด",
    metricFocusSub:"ผู้ป่วยทั้งหมดที่มีตัวชี้วัดนี้", meanTitle:"แนวโน้มเฉลี่ย", meanSub:"แดง=ค่าเฉลี่ย, เทา=รายบุคคล", meanLabel:"ค่าเฉลี่ย",
    gridSub:"กราฟย่อต่อผู้ป่วย", casesUnit:" ราย", pointsUnit:" จุด", recordsUnit:" เรคคอร์ด",
    noMetricData:"ไม่มีข้อมูลสำหรับตัวชี้วัดนี้", noDataForMetric:"ยังไม่มีข้อมูลสำหรับโรค/ตัวชี้วัดนี้",
    caseCount:"จำนวนผู้ป่วยที่ลงทะเบียน", cases:"ราย", btnAddCase:"+ บันทึกผู้ป่วย",
    selectDisease:"เลือกโรค",
    selectDiseaseSub:"แบบฟอร์มที่เป็นไปตามการออกแบบข้อมูลทางคลินิกของ Macro Bio จะปรากฏขึ้น",
    backDisease:"← เลือกโรคใหม่", formTitle:"แบบฟอร์มบันทึกผู้ป่วย",
    notice:"📋 เป็นไปตามการออกแบบของ Macro Bio Co., Ltd. ข้อมูลผู้ป่วยต้องผ่านการ", noticeStrong:"ไม่ระบุชื่อ", noticeEnd:"ก่อนกรอก",
    noticeAD:"⚠ โรคอัลไซเมอร์ต้องการการประเมินเป็นเวลา", noticeADStrong:"อย่างน้อย 3–6 เดือน", noticeADEnd:"",
    noticeALS:"⚠ ALS ต้องการ", noticeALSStrong:"การติดตามระยะยาวอย่างน้อย 6–12 เดือน", noticeALSEnd:" ให้ความสำคัญกับอัตราการลดลงของ ALSFRS-R",
    done:"บันทึกข้อมูลผู้ป่วยเรียบร้อยแล้ว", doneSub:"ทีมผู้ตรวจสอบจะอัปเดตสถานะหลังการยืนยัน (โดยปกติภายใน 3–5 วันทำการ)", doneBtn:"บันทึกผู้ป่วยเพิ่มเติม",
    prev:"← ก่อนหน้า", next:"ถัดไป →", submit:"✓ ลงทะเบียน", step:"ขั้นตอน",
    caseId:"รหัสผู้ป่วย (ไม่ระบุชื่อ)", caseIdPh:"เช่น PD-2026-001",
    institution:"สถาบัน", institutionPh:"เช่น โรงพยาบาลมหาวิทยาลัย",
    physician:"แพทย์ผู้รับผิดชอบ", physicianPh:"เช่น เซอิจิ ทานากะ",
    startDate:"วันเริ่มการรักษา", timePoint:"จุดเวลาประเมิน",
    age:"อายุ", agePh:"เช่น 68",
    sex:"เพศ", male:"ชาย", female:"หญิง", other:"อื่นๆ",
    required:"*จำเป็น",
    safetyTitle:"การประเมินความปลอดภัย (CTCAE v5.0)",
    aeLabel:"อาการไม่พึงประสงค์ (เลือกได้หลายข้อ)",
    gradeLabel:"เลือกระดับ", saeLabel:"มีเหตุการณ์ไม่พึงประสงค์ร้ายแรง (SAE)",
    saeWarn:"⚠ กรุณาส่งแบบฟอร์มรายงาน SAE โดยเร็ว สำนักงานจะได้รับแจ้งโดยอัตโนมัติ",
    notesLabel:"หมายเหตุ", notesPh:"บันทึกข้อสังเกตพิเศษ",
    pdLabel:"โรคพาร์กินสัน", pdSub:"MDS-UPDRS / Hoehn&Yahr / PDQ-39",
    pdStep1:"ข้อมูลพื้นฐาน", pdStep2:"ความรุนแรง & ยา", pdStep3:"MDS-UPDRS", pdStep4:"QOL & ความปลอดภัย",
    hyTitle:"การจำแนกประเภท Hoehn & Yahr", hySub:"กำหนดสถานะพื้นฐานของผู้ป่วยให้ชัดเจน",
    medTitle:"การจัดการยา (ON/OFF)", medSub:"บันทึกอย่างละเอียดเพื่อแยกแยะผลของยา",
    medLabel:"ยาที่ใช้อยู่ในปัจจุบัน", medPh:"เช่น L-dopa 300mg/วัน",
    ldopaTime:"นาทีก่อนหน้าที่รับประทานยา", ldopaPh:"60",
    onOffLabel:"สถานะ ON/OFF ณ เวลาประเมิน",
    on:"สถานะ ON (ยาออกฤทธิ์)", off:"สถานะ OFF (ยาหมดฤทธิ์)", partialOn:"ON บางส่วน", unknown:"ไม่ทราบ",
    updrsTitle:"MDS-UPDRS ส่วน III (การทำงานของมอเตอร์)", updrsSub:"[สำคัญที่สุด] การเปรียบเทียบก่อนและหลังการรักษา (0–132)",
    updrsB:"คะแนนก่อนการรักษา", updrsA:"คะแนนปัจจุบัน", updrsBPh:"เช่น 42", updrsAPh:"เช่น 35",
    changeRate:"อัตราการเปลี่ยนแปลง (คำนวณอัตโนมัติ)",
    pdq39:"PDQ-39 รวม (0–100, น้อยกว่าดีกว่า)", pdq39Ph:"เช่น 42",
    duration:"ระยะเวลาที่เป็นโรค (ปี)", durationPh:"5",
    adLabel:"โรคอัลไซเมอร์", adSub:"MMSE / CDR-SB / ADAS-Cog / ADL",
    adStep1:"ข้อมูลพื้นฐาน", adStep2:"การรับรู้", adStep3:"ADL & พฤติกรรม", adStep4:"ความปลอดภัย",
    diagName:"การวินิจฉัยที่ยืนยันแล้ว", mriCt:"ผล MRI/CT", mriPh:"เช่น ฮิปโปแคมปัสฝ่อ",
    proxy:"ได้รับความยินยอมจากตัวแทน (ครอบครัว/ผู้แทนตามกฎหมาย)",
    cdrTitle:"ระดับความรุนแรง (CDR / CDR-SB)", cdrSub:"[สำคัญที่สุด] การประเมินรวมการรับรู้และการทำงานประจำวัน",
    cdrLabel:"CDR", cdrsb:"CDR-SB (0–18)", cdrsbPh:"เช่น 4.5",
    mmse:"MMSE (0–30)", mmsePh:"เช่น 22",
    moca:"MoCA (0–30)", mocaPh:"เช่น 20",
    adasCog:"ADAS-Cog (0–70, มากกว่าแย่กว่า)", adasPh:"เช่น 18",
    dasc21:"DASC-21 (21–84)", dasc21Ph:"เช่น 38",
    npiQ:"NPI-Q ความรุนแรงรวม (0–36)", npiPh:"เช่น 6",
    careNote:"การประเมินของผู้ดูแล", careNotePh:"เช่น ผู้ป่วยตอบสนองต่อการเรียกชื่อมากขึ้น",
    alsLabel:"ALS (Amyotrophic Lateral Sclerosis)", alsSub:"ALSFRS-R / FVC / เหตุการณ์การลุกลาม",
    alsStep1:"ข้อมูลพื้นฐาน", alsStep2:"ALSFRS-R", alsStep3:"การหายใจ & โภชนาการ", alsStep4:"ความปลอดภัย",
    onsetSite:"ตำแหน่งเริ่มต้น", diagCriteria:"เกณฑ์การวินิจฉัย",
    niv:"ใช้ NIV (เครื่องช่วยหายใจ)", gastrostomy:"ทำการให้อาหารทางสายยาง", tracheostomy:"ทำการเจาะคอ", wheelChair:"ใช้รถเข็น",
    alsfrsTitle:"ALSFRS-R (0–4 × 12 รายการ = สูงสุด 48)", alfssrsSub:"[สำคัญที่สุด] ตรวจสอบว่าอัตราการลดลงช้าลงหรือไม่",
    alsfrsTotal:"ALSFRS-R รวม", alsfrsMax:"/ 48",
    fvc:"FVC (ความจุปอดสูงสุด, %)", fvcPh:"72.4",
    svc:"SVC (ความจุปอดช้า, %)", svcPh:"68.0",
    spo2:"SpO₂ (ขณะพัก, %)", spo2Ph:"97",
    fvcSevere:"⚠ ลดลงรุนแรง (พิจารณา NIV)", fvcWarn:"ระวัง (เพิ่มการติดตาม)", fvcNormal:"อยู่ในเกณฑ์ปกติ",
    weight:"น้ำหนัก (กก.)", bmi:"BMI",
    progressEvents:"บันทึกเหตุการณ์การลุกลาม",
    nivDate:"เริ่ม NIV", trachDate:"เจาะคอ", pegDate:"ให้อาหารทางสาย",
    cancerLabel:"การรักษามะเร็ง", cancerSub:"RECIST v1.1 / PFS / OS / CTCAE",
    cancerStep1:"พื้นฐาน & เป้าหมาย", cancerStep2:"ภาพ & RECIST", cancerStep3:"ตัวบ่งชี้ & การรอดชีวิต", cancerStep4:"ความปลอดภัย",
    cancerType:"ชนิดมะเร็ง", stage:"ระยะโรค", treatmentLine:"สายการรักษา", ps:"สถานะสมรรถภาพ (PS)",
    imageType:"ชนิดของภาพ", imageDate:"วันที่ถ่ายภาพ",
    sumBaseline:"ผลรวมรอยโรค: ค่าพื้นฐาน (มม.)", sumCurrent:"ผลรวมรอยโรค: ปัจจุบัน (มม.)",
    recistFinal:"การประเมิน RECIST v1.1 ขั้นสุดท้าย", autoCalc:"คำนวณอัตโนมัติ",
    cea:"CEA (ng/mL)", ca199:"CA19-9 (U/mL)",
    pfsWeeks:"ระยะเวลา PFS (สัปดาห์)", pfsWPh:"เช่น 24",
    osStatus:"สถานะ OS", alive:"มีชีวิตอยู่", dead:"เสียชีวิต", lost:"ติดตามไม่ได้",
    // Stroke
    strokeLabel:"โรคหลอดเลือดสมอง (Stroke)", strokeSub:"NIHSS / mRS / Barthel Index",
    strokeStep1:"พื้นฐาน & การวินิจฉัย", strokeStep2:"การประเมินทางระบบประสาท", strokeStep3:"ADL & QOL", strokeStep4:"ความปลอดภัย",
    onsetDate:"วันที่เริ่มมีอาการ", strokeType:"ชนิด",
    strokeLacunar:"Lacunar", strokeAthero:"Atherothrombotic", strokeCardio:"Cardioembolic", strokeOther:"อื่นๆ",
    onsetTime:"เวลาที่ผ่านไปตั้งแต่เริ่มมีอาการ",
    tpaUsed:"ได้รับ t-PA (rt-PA)",
    nihssTitle:"NIHSS (NIH Stroke Scale)", nihssSub:"[สำคัญ] 0–42 ยิ่งสูงยิ่งรุนแรง",
    nihssScore:"คะแนนรวม NIHSS", nihssPh:"เช่น 8",
    mrsTitle:"mRS (modified Rankin Scale)", mrsSub:"การประเมินผลลัพธ์การทำงาน 0–6",
    mrsScore:"คะแนน mRS",
    mrsOpt0:"0: ไม่มีอาการ", mrsOpt1:"1: อาการเล็กน้อย", mrsOpt2:"2: เล็กน้อย", mrsOpt3:"3: ปานกลาง", mrsOpt4:"4: ปานกลาง–รุนแรง", mrsOpt5:"5: รุนแรง", mrsOpt6:"6: เสียชีวิต",
    barthelTitle:"Barthel Index (ADL)", barthelSub:"กิจวัตรประจำวัน 0–100",
    barthelScore:"Barthel Index", barthelPh:"เช่น 65",
    fimScore:"FIM (ทางเลือก)", fimPh:"เช่น 90",
    recurrence:"กลับเป็นซ้ำ", hemorrhage:"การเปลี่ยนเป็นเลือดออก",
    // SCI
    sciLabel:"การบาดเจ็บไขสันหลัง", sciSub:"AIS / ISNCSCI / SCIM-III",
    sciStep1:"พื้นฐาน & การวินิจฉัย", sciStep2:"การเคลื่อนไหว & ความรู้สึก", sciStep3:"ADL & ความเกร็ง", sciStep4:"ความปลอดภัย",
    injuryDate:"วันที่บาดเจ็บ", injuryLevel:"ระดับการบาดเจ็บ",
    levelCervical:"คอ (C1–C8)", levelThoracic:"อก (T1–T12)", levelLumbar:"เอว (L1–L5)", levelSacral:"กระเบนเหน็บ (S1–S5)",
    aisGrade:"AIS (ASIA Impairment Scale)",
    aisA:"A: อัมพาตสมบูรณ์", aisB:"B: ความรู้สึกไม่สมบูรณ์", aisC:"C: การเคลื่อนไหวไม่สมบูรณ์ (<3)", aisD:"D: การเคลื่อนไหวไม่สมบูรณ์ (≥3)", aisE:"E: ปกติ",
    isncsciTitle:"ISNCSCI การเคลื่อนไหว & ความรู้สึก", isncsciSub:"[สำคัญ] การประเมินมาตรฐาน",
    motorUE:"คะแนน Motor - แขน (0–50)", motorUEPh:"เช่น 35",
    motorLE:"คะแนน Motor - ขา (0–50)", motorLEPh:"เช่น 20",
    sensoryPP:"Sensory - Pin Prick (0–112)", sensoryPPPh:"เช่น 80",
    sensoryLT:"Sensory - Light Touch (0–112)", sensoryLTPh:"เช่น 85",
    scimTitle:"SCIM-III (ความเป็นอิสระจากการบาดเจ็บไขสันหลัง)", scimSub:"ความเป็นอิสระใน ADL 0–100",
    scimScore:"คะแนน SCIM-III รวม", scimPh:"เช่น 55",
    ashworthTitle:"Modified Ashworth Scale (ความเกร็ง)",
    ashworthScore:"MAS (0–4)",
    ashworth0:"0: ปกติ", ashworth1:"1: เล็กน้อย", ashworth1p:"1+: เล็กน้อย–ปานกลาง", ashworth2:"2: เพิ่มขึ้นชัดเจน", ashworth3:"3: มาก", ashworth4:"4: แข็งเกร็ง",
    autonomicDys:"การเริ่มต้นของ Autonomic dysreflexia", pressureUlcer:"การเกิดแผลกดทับ",
    // DM
    dmLabel:"โรคเบาหวาน", dmSub:"HbA1c / น้ำตาลในเลือด / ภาวะแทรกซ้อน",
    dmStep1:"พื้นฐาน & การวินิจฉัย", dmStep2:"การควบคุมน้ำตาล", dmStep3:"การประเมินภาวะแทรกซ้อน", dmStep4:"ความปลอดภัย",
    dmType:"ชนิดของเบาหวาน",
    dm1:"ชนิดที่ 1", dm2:"ชนิดที่ 2", dmGes:"เบาหวานขณะตั้งครรภ์", dmOther:"ชนิดอื่น",
    dmDuration:"ระยะเวลาของโรค (ปี)", dmDurationPh:"เช่น 10",
    bmi:"BMI", bmiPh:"เช่น 24.5",
    complications:"ภาวะแทรกซ้อนที่ทราบ",
    compNephro:"โรคไต", compNeuro:"โรคเส้นประสาท", compRetino:"โรคจอประสาทตา", compCardio:"โรคหัวใจและหลอดเลือด", compNone:"ไม่มี",
    hba1cTitle:"ตัวชี้วัดการควบคุมน้ำตาล", hba1cSub:"[สำคัญ] แนวโน้ม HbA1c และน้ำตาล",
    hba1c:"HbA1c (%)", hba1cPh:"เช่น 7.2",
    fpg:"น้ำตาลขณะอดอาหาร (mg/dL)", fpgPh:"เช่น 130",
    ppg2h:"น้ำตาล 2 ชม. หลังอาหาร (mg/dL)", ppg2hPh:"เช่น 180",
    timeInRange:"TIR (CGM time in range, %, ทางเลือก)", tirPh:"เช่น 65",
    nephroTitle:"การประเมินโรคไต", egfr:"eGFR (mL/min/1.73m²)", egfrPh:"เช่น 65",
    urineAlb:"Urine albumin (mg/gCr)", urineAlbPh:"เช่น 45",
    neuroVAS:"VAS โรคเส้นประสาท (ชา/ปวด)", neuroVASPh:"0–10",
    retinoStage:"ระยะของจอประสาทตา",
    retino0:"ไม่มี", retino1:"แบบธรรมดา", retino2:"ก่อนงอกใหม่", retino3:"แบบงอกใหม่",
    hypoEvents:"จำนวนครั้งภาวะน้ำตาลต่ำ (4 สัปดาห์)", hypoPh:"เช่น 2",
    // Other
    otherLabel:"โรคอื่นๆ", otherSub:"แบบฟอร์มทั่วไป (กรอกอิสระ)",
    otherStep1:"พื้นฐาน & การวินิจฉัย", otherStep2:"การประเมินหลัก", otherStep3:"QOL & ADL", otherStep4:"ความปลอดภัย",
    diseaseName:"ชื่อโรค (กรอกอิสระ)", diseaseNamePh:"เช่น โรคปอดอักเสบไฟโบรซิส",
    icd10:"รหัส ICD-10 (ทางเลือก)", icd10Ph:"เช่น J84.1",
    diagDate:"วันที่วินิจฉัย / เริ่มต้น",
    primaryEval:"การประเมินหลัก (สูงสุด 3 ข้อ)",
    evalName:"ชื่อรายการ", evalNamePh:"เช่น ระยะทางเดิน 6 นาที",
    evalValue:"ค่า / ผลลัพธ์", evalValuePh:"เช่น 320",
    evalUnit:"หน่วย", evalUnitPh:"เช่น ม.",
    evalSupp:"ข้อมูลเพิ่มเติม", evalSuppPh:"ชื่อสเกล ค่าอ้างอิง หมายเหตุ",
    qolDesc:"QOL & ADL (กรอกอิสระ)", qolDescPh:"เช่น SF-36 PCS 45 / EQ-5D 0.72 / ส่วนใหญ่ทำเองได้",
    protocolTitle:"การจัดการโปรโตคอลและเอกสาร",
    docLoginMsg:"กรุณาเข้าสู่ระบบเพื่อใช้งานฟีเจอร์นี้",
    docDownloadBtn:"⬇ ดาวน์โหลด",
    docUploadedLabel:"อัปโหลดเมื่อ",
    docEmpty:"ยังไม่มีเอกสารที่เผยแพร่",
    docLoadingMsg:"กำลังโหลด…",
    docErrorMsg:"ไม่สามารถโหลดรายการเอกสารได้",
    saeTitle:"การรายงาน SAE (เหตุการณ์ไม่พึงประสงค์ร้ายแรง)",
    saeDesc:"กรุณาส่งแบบฟอร์มรายงาน SAE โดยเร็วที่สุดหากเกิดเหตุการณ์ไม่พึงประสงค์ร้ายแรง",
    saeBtn:"รายงาน SAE",
    safetyStatsTitle:"สรุปสถิติความปลอดภัย",
    safetyTotalCases:"จำนวนผู้ป่วยทั้งหมด",
    safetySaeCount:"จำนวน SAE",
    safetyOverallRate:"อัตรา SAE โดยรวม",
    safetyByDiseaseTitle:"อัตรา SAE แยกตามโรค",
    safetyTopEventsTitle:"เหตุการณ์ไม่พึงประสงค์ที่พบบ่อย (10 อันดับแรก)",
    safetyEmpty:"ยังไม่มีข้อมูลสำหรับสรุปผล",
    safetyLoadingMsg:"กำลังโหลดสถิติ…",
    safetyErrorMsg:"ไม่สามารถโหลดข้อมูลสถิติได้",
    safetyCasesUnit:"ราย",
    footerTitle:"เข้าร่วมเครือข่ายวิจัย",
    footerSub:"คุณสมบัติ: แพทย์ที่มีใบอนุญาตหรือนักวิจัยในสถาบันทางการแพทย์\nการอนุมัติมักจะอยู่ภายใน 3–5 วันทำการ",
    footerBtn:"สมัครสมาชิก",
    footerCopy:"© 2026 SC-NET Japan　│　แพลตฟอร์มปิดสำหรับแพทย์เท่านั้น　│　ดูแลโดย Macro Bio Co., Ltd.",
    // ข้อมูลการให้ยา (ร่วม)
    dosageTitle:"ข้อมูลการให้สารสกัด",
    dosageSub:"กรุณาบันทึกรายละเอียดของสารสกัดที่ให้",
    dailyDose:"1. ปริมาณต่อวัน (ml)", dailyDosePh:"เช่น 90",
    treatmentPeriod:"2. ระยะเวลาและจำนวนครั้งที่ให้", treatmentPeriodPh:"เช่น 4 สัปดาห์ สัปดาห์ละ 1 ครั้ง รวม 4 ครั้ง",
    serumSource:"3. แหล่งที่มาและปริมาณสารสกัดที่ใช้",
    serumRow1:"จากเนื้อเยื่อฟันน้ำนม (ml)", serumRow1Ph:"เช่น 30",
    serumRow2:"จากสายสะดือ (ml)",           serumRow2Ph:"เช่น 30",
    serumRow3:"จากไขกระดูก (ml)",           serumRow3Ph:"เช่น 30",
    serumOther:"แหล่งอื่น / รายละเอียด",    serumOtherPh:"เช่น จากเนื้อเยื่อไขมัน 20ml",
    caseAuthor:"ผู้เขียน", caseDate:"วันที่",
    authLoginTitle:"เข้าสู่ระบบ", authEmailLabel:"อีเมล", authPasswordLabel:"รหัสผ่าน",
    authLoginBtn:"เข้าสู่ระบบ", authLoginError:"อีเมลหรือรหัสผ่านไม่ถูกต้อง",
    authNoAccount:"ยังไม่มีบัญชี?", authGoApply:"สมัครที่นี่",
    authHaveAccount:"มีบัญชีอยู่แล้ว?", authGoLogin:"เข้าสู่ระบบที่นี่",
    authForgotPassword:"ลืมรหัสผ่าน?", authLogoutBtn:"ออกจากระบบ",
    authApplyTitle:"สมัครใหม่", authDisplayNameLabel:"ชื่อ-นามสกุล", authInstitutionLabel:"สถาบัน/หน่วยงาน",
    authApplySubmitBtn:"สมัคร", authApplySuccess:"ได้รับใบสมัครของคุณแล้ว กรุณารอการอนุมัติ",
    authResetRequestTitle:"รีเซ็ตรหัสผ่าน",
    authResetRequestDesc:"กรอกอีเมลที่ลงทะเบียนไว้ เราจะส่งลิงก์รีเซ็ตให้คุณ",
    authResetRequestSubmitBtn:"ส่ง",
    authResetRequestSent:"ส่งอีเมลแล้ว กรุณาใช้ลิงก์เพื่อรีเซ็ตรหัสผ่าน",
    authChangePasswordTitle:"เปลี่ยนรหัสผ่าน", authMustChangeNotice:"คุณต้องเปลี่ยนรหัสผ่านเมื่อเข้าสู่ระบบครั้งแรก",
    authCurrentPasswordLabel:"รหัสผ่านปัจจุบัน", authNewPasswordLabel:"รหัสผ่านใหม่",
    authNewPasswordConfirmLabel:"ยืนยันรหัสผ่านใหม่", authChangePasswordSubmitBtn:"เปลี่ยน",
    authChangePasswordSuccess:"เปลี่ยนรหัสผ่านเรียบร้อยแล้ว", authPasswordMismatch:"รหัสผ่านใหม่ไม่ตรงกัน",
    authResetConfirmTitle:"ตั้งรหัสผ่านใหม่", authResetConfirmSubmitBtn:"ตั้งรหัสผ่าน",
    authResetConfirmSuccess:"รีเซ็ตรหัสผ่านเรียบร้อยแล้ว กรุณาเข้าสู่ระบบ",
    authResetConfirmInvalid:"ลิงก์ไม่ถูกต้องหรือหมดอายุ กรุณาขอลิงก์รีเซ็ตใหม่",
    authLoginRequiredTitle:"ต้องเข้าสู่ระบบ",
    authLoginRequiredDesc:"คุณต้องเข้าสู่ระบบเพื่อป้อนข้อมูลผู้ป่วย หากยังไม่มีบัญชีกรุณาสมัคร",
    authMineLoginRequired:"ต้องเข้าสู่ระบบเพื่อดู \"ข้อมูลของฉัน\"", authWelcome:"ยินดีต้อนรับ",
    prefectureLabel:"จังหวัด (ประเทศญี่ปุ่น)", prefecturePh:"เลือก",
    dashboardDeleteMineBtn:"ลบข้อมูลของฉัน",
    dashboardDeleteMineConfirm:"การดำเนินการนี้จะลบข้อมูลผู้ป่วยทั้งหมดที่คุณป้อนไว้ ดำเนินการต่อหรือไม่?",
  },
};

/* ── インライン国旗SVG ─────────────────────── */
function FlagJP() {
  return (
    <svg viewBox="0 0 30 20" width="22" height="15" style={{ display:"block", borderRadius:2, border:"1px solid rgba(255,255,255,.2)" }}>
      <rect width="30" height="20" fill="#fff"/>
      <circle cx="15" cy="10" r="6" fill="#BC002D"/>
    </svg>
  );
}
function FlagGB() {
  return (
    <svg viewBox="0 0 60 30" width="22" height="15" style={{ display:"block", borderRadius:2, border:"1px solid rgba(255,255,255,.2)" }}>
      <rect width="60" height="30" fill="#012169"/>
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="7"/>
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="4"/>
      <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="11"/>
      <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="7"/>
    </svg>
  );
}
function FlagMN() {
  return (
    <svg viewBox="0 0 30 20" width="22" height="15" style={{ display:"block", borderRadius:2, border:"1px solid rgba(255,255,255,.2)" }}>
      <rect width="30" height="20" fill="#C4272F"/>
      <rect x="10" width="10" height="20" fill="#015197"/>
      <circle cx="5" cy="7" r="2" fill="#F9CF02" opacity=".9"/>
      <rect x="3.5" y="10" width="3" height="1.2" fill="#F9CF02" opacity=".9"/>
      <rect x="3.5" y="12.2" width="3" height="1.2" fill="#F9CF02" opacity=".9"/>
      <rect x="3.5" y="14.4" width="3" height="1.2" fill="#F9CF02" opacity=".9"/>
    </svg>
  );
}
function FlagTH() {
  return (
    <svg viewBox="0 0 30 20" width="22" height="15" style={{ display:"block", borderRadius:2, border:"1px solid rgba(255,255,255,.2)" }}>
      <rect width="30" height="20" fill="#A51931"/>
      <rect y="3.33" width="30" height="3.33" fill="#F4F5F8"/>
      <rect y="6.67" width="30" height="6.67" fill="#2E2A4C"/>
      <rect y="13.33" width="30" height="3.33" fill="#F4F5F8"/>
    </svg>
  );
}
const FLAG_MAP = { ja: FlagJP, en: FlagGB, mn: FlagMN, th: FlagTH };

const LANG_META = {
  ja: { label:"日本語" },
  en: { label:"English" },
  mn: { label:"Монгол" },
  th: { label:"ไทย" },
};

/* ══════════════════════════════════════════════
   共通UIコンポーネント
══════════════════════════════════════════════ */
const S = {
  input: { width:"100%", padding:"10px 12px", border:"1.5px solid #d8d2c8", background:"white", fontFamily:"inherit", fontSize:13.5, color:"#1a1a2e", outline:"none" },
  select: { width:"100%", padding:"10px 12px", border:"1.5px solid #d8d2c8", background:"white", fontFamily:"inherit", fontSize:13.5, outline:"none", cursor:"pointer" },
};
const focus = e => e.target.style.borderColor="#2c6e8a";
const blur  = e => e.target.style.borderColor="#d8d2c8";

function Lbl({ children, req, t }) {
  return <label style={{ display:"block", fontSize:12, fontWeight:700, color:"#3a4a5a", marginBottom:4 }}>
    {children}{req && <span style={{ color:"#c0392b", fontSize:10, marginLeft:3 }}>{t.required}</span>}
  </label>;
}
function Inp({ value, onChange, placeholder, type="text" }) {
  return <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={S.input} onFocus={focus} onBlur={blur} />;
}
function Sel({ value, onChange, options, ph }) {
  return <select value={value} onChange={onChange} style={{ ...S.select, color:value?"#1a1a2e":"#9aa" }} onFocus={focus} onBlur={blur}>
    <option value="">{ph||"—"}</option>
    {options.map(o=><option key={o} value={o}>{o}</option>)}
  </select>;
}
function Row({ children, cols=2 }) {
  return <div className="sc-row" style={{ display:"grid", gridTemplateColumns:`repeat(${cols},1fr)`, gap:12, marginBottom:12 }}>{children}</div>;
}
function Blk({ children }) {
  return <div style={{ background:"white", border:"1px solid #e5e0d8", padding:"20px 22px", marginBottom:12 }}>{children}</div>;
}
function Sec({ icon, title, sub }) {
  return <div style={{ borderLeft:"4px solid #2c6e8a", paddingLeft:12, marginBottom:14 }}>
    <div style={{ fontSize:14.5, fontWeight:700, color:"#1b3a52" }}>{icon} {title}</div>
    {sub && <div style={{ fontSize:11.5, color:"#7a8a9a", marginTop:2 }}>{sub}</div>}
  </div>;
}
function Steps({ steps, current, onStep }) {
  return <div className="sc-steps" style={{ display:"flex", marginBottom:20, overflowX:"auto" }}>
    {steps.map((s,i)=>(
      <div key={s} onClick={()=>onStep(i+1)} style={{
        flex:1, minWidth:60, textAlign:"center", padding:"8px 2px",
        borderBottom:current===i+1?"3px solid #2c6e8a":"3px solid #e0dbd2",
        color:current===i+1?"#1b3a52":current>i+1?"#2c6e8a":"#9aa",
        fontSize:11, fontWeight:current===i+1?700:400, cursor:"pointer", whiteSpace:"nowrap"
      }}><span style={{ marginRight:2 }}>{current>i+1?"✓":i+1}</span>{s}</div>
    ))}
  </div>;
}
function NavBtns({ step, total, onBack, onNext, onSubmit, t }) {
  return <div className="sc-navbtns" style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12 }}>
    <button onClick={onBack} disabled={step===1} style={{ background:"white", border:"1.5px solid #d8d2c8", padding:"9px 20px", fontFamily:"inherit", fontSize:13, cursor:step===1?"not-allowed":"pointer", color:step===1?"#ccc":"#3a4a5a", opacity:step===1?.5:1 }}>{t.prev}</button>
    <span style={{ fontSize:11, color:"#9aabb5" }}>{t.step} {step}/{total}</span>
    {step<total
      ? <button onClick={onNext} style={{ background:"#1b3a52", color:"white", border:"none", padding:"10px 24px", fontFamily:"inherit", fontSize:13, cursor:"pointer", fontWeight:600 }}>{t.next}</button>
      : <button onClick={onSubmit} style={{ background:"#2d6a4f", color:"white", border:"none", padding:"10px 28px", fontFamily:"inherit", fontSize:13, cursor:"pointer", fontWeight:700 }}>{t.submit}</button>}
  </div>;
}
/* ══════════════════════════════════════════════
   バリデーション
══════════════════════════════════════════════ */
/* 各疾患・stepごとの必須フィールド定義 */
const REQUIRED_FIELDS = {
  pd:     { 1:["caseId","prefecture"], 2:["hy"], 3:["updrs3a"], 4:[] },
  ad:     { 1:["caseId","prefecture"], 2:["mmse"], 3:[], 4:[] },
  als:    { 1:["caseId","prefecture"], 2:[], 3:[], 4:[] },
  cancer: { 1:["caseId","prefecture","cancerType"], 2:[], 3:[], 4:[] },
  stroke: { 1:["caseId","prefecture","strokeType"], 2:["nihss","mrs"], 3:["barthel"], 4:[] },
  sci:    { 1:["caseId","prefecture","injuryLevel","aisGrade"], 2:["motorUE","motorLE"], 3:["scim"], 4:[] },
  dm:     { 1:["caseId","prefecture","dmType"], 2:["hba1c"], 3:[], 4:[] },
  other:  { 1:["caseId","prefecture","diseaseName"], 2:[], 3:[], 4:[] },
};

/* 数値範囲チェック定義 (key: [min, max, ラベル]) */
const NUMERIC_RANGES = {
  age:[0,120,"年齢"], updrs3a:[0,132,"MDS-UPDRS III"], updrs3b:[0,132,"MDS-UPDRS III"], pdq39:[0,100,"PDQ-39"],
  mmse:[0,30,"MMSE"], moca:[0,30,"MoCA"], cdrsb:[0,18,"CDR-SB"], adasCog:[0,70,"ADAS-Cog"],
  fvc:[0,200,"FVC"], spo2:[50,100,"SpO2"],
  nihss:[0,42,"NIHSS"], barthel:[0,100,"Barthel"], fim:[18,126,"FIM"],
  motorUE:[0,50,"Motor UE"], motorLE:[0,50,"Motor LE"], sensoryPP:[0,112,"Pin Prick"], sensoryLT:[0,112,"Light Touch"], scim:[0,100,"SCIM-III"],
  hba1c:[0,20,"HbA1c"], fpg:[0,800,"FPG"], ppg2h:[0,800,"PPG2h"], tir:[0,100,"TIR"],
  egfr:[0,200,"eGFR"], urineAlb:[0,5000,"Urine Alb"], neuroVAS:[0,10,"VAS"], hypoEvents:[0,200,"Hypo events"],
  bmi:[10,60,"BMI"], dmDuration:[0,80,"罹病期間"],
  dailyDose:[0,10000,"投与量"], serumRow1:[0,10000,"乳歯髄"], serumRow2:[0,10000,"臍帯"], serumRow3:[0,10000,"骨髄"],
};

function validateForm(disease, step, f, t) {
  const errors = {};
  const required = (REQUIRED_FIELDS[disease] || {})[step] || [];
  // 必須チェック
  required.forEach(k => {
    const v = f[k];
    if (v === "" || v === null || v === undefined || (Array.isArray(v) && v.length === 0)) {
      errors[k] = t.errorRequired;
    }
  });
  // 数値範囲チェック (空でない場合のみ)
  Object.keys(f).forEach(k => {
    const v = f[k];
    if (v === "" || v === null || v === undefined) return;
    const range = NUMERIC_RANGES[k];
    if (!range) return;
    const num = parseFloat(v);
    if (isNaN(num)) { errors[k] = t.errorNumeric; return; }
    if (num < range[0] || num > range[1]) {
      errors[k] = `${t.errorRange} (${range[0]}〜${range[1]})`;
    }
  });
  return errors;
}

function ErrorBanner({ errors, t }) {
  const keys = Object.keys(errors);
  if (keys.length === 0) return null;
  return <div style={{ background:"#fde8e8", border:"1px solid #f5b8b8", color:"#922b21", padding:"10px 14px", marginBottom:12, fontSize:12.5 }}>
    <strong style={{ display:"block", marginBottom:4 }}>⚠ {t.errorTitle}</strong>
    <div style={{ fontSize:12, color:"#7a3a3a" }}>{t.errorHint}</div>
  </div>;
}

/* ══════════════════════════════════════════════
   ストレージ層 (localStorage)
══════════════════════════════════════════════ */
/* ══════════════════════════════════════════════
   バックエンドAPI連携層（フェーズ2）
   症例データはlocalStorageではなくsc-net-backend（PostgreSQL）で管理する。
   JWTは症例データのlocalStorageキー（旧sc-net-cases）とは別キーで保存する。
══════════════════════════════════════════════ */
const API_BASE = (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost")
  ? "http://127.0.0.1:8007" : "";
const AUTH_TOKEN_KEY = "sc-net-auth-token";
const AUTH_USER_KEY = "sc-net-auth-user";

function getAuthToken() {
  try { return localStorage.getItem(AUTH_TOKEN_KEY); } catch (e) { return null; }
}
function setAuthToken(token) {
  try {
    if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
    else localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch (e) {}
}
function getAuthUser() {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}
function setAuthUser(user) {
  try {
    if (user) localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(AUTH_USER_KEY);
  } catch (e) {}
}
/* ログイン・ログアウトの両方で使う共通クリア処理 */
function clearAuth() {
  setAuthToken(null);
  setAuthUser(null);
}

/* 共通APIリクエスト関数。JWTがあれば自動でAuthorizationヘッダーを付与し、
   401（トークン無効・期限切れ）時は自動ログアウトしてイベントで通知する。 */
async function apiFetch(path, options = {}) {
  const token = getAuthToken();
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  } catch (e) {
    throw new Error("サーバーに接続できませんでした。ネットワーク状態をご確認ください。");
  }

  if (res.status === 401) {
    clearAuth();
    window.dispatchEvent(new Event("sc-net-auth-expired"));
  }

  let body = null;
  try { body = await res.json(); } catch (e) {}

  if (!res.ok) {
    let message = `エラーが発生しました（HTTP ${res.status}）`;
    if (body && body.detail) {
      message = Array.isArray(body.detail) ? body.detail.map(d => d.msg).join(", ") : body.detail;
    }
    const err = new Error(message);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

/* 都道府県一覧（未ログイン時の施設名マスキング表示用に独立項目として入力する） */
const PREFECTURES = [
  "北海道","青森県","岩手県","宮城県","秋田県","山形県","福島県",
  "茨城県","栃木県","群馬県","埼玉県","千葉県","東京都","神奈川県",
  "新潟県","富山県","石川県","福井県","山梨県","長野県","岐阜県","静岡県","愛知県",
  "三重県","滋賀県","京都府","大阪府","兵庫県","奈良県","和歌山県",
  "鳥取県","島根県","岡山県","広島県","山口県",
  "徳島県","香川県","愛媛県","高知県",
  "福岡県","佐賀県","長崎県","熊本県","大分県","宮崎県","鹿児島県","沖縄県",
];

/* バックエンドのcasesレコード（列＋dataのJSONB）を、既存フロントが前提とする
   フラットな症例オブジェクト（App.jsのあらゆるコンポーネントが参照する形）に変換する */
function normalizeApiCase(row) {
  return {
    id: row.id,
    disease: row.disease,
    caseId: row.case_id,
    institution: row.institution,
    prefecture: row.prefecture,
    timePoint: row.time_point,
    age: row.age,
    sex: row.sex,
    savedAt: row.created_at,
    updatedAt: row.updated_at,
    is_owner: !!row.is_owner,
    ...(row.data || {}),
  };
}

/* フラットな症例フォームデータを、POST/PUT /api/cases が期待するペイロード形式に変換する */
function denormalizeToApiPayload(disease, f) {
  const { caseId, institution, prefecture, timePoint, age, sex, id, is_owner, savedAt, updatedAt, ...rest } = f;
  return {
    case_id: caseId || "",
    disease,
    institution: institution || "",
    prefecture: prefecture || "",
    time_point: timePoint || "",
    age: age || "",
    sex: sex || "",
    data: rest,
  };
}

/* フォーム送信時にAPI呼び出しを行う共通ヘルパー。
   initial.id があれば更新(PUT)、無ければ新規作成(POST)。
   返り値はDone/CaseDetail等が期待するフラット形式（normalizeApiCase後）。 */
async function persistCaseRecord(disease, f, initial) {
  const payload = denormalizeToApiPayload(disease, f);
  if (initial && initial.id) {
    await apiFetch(`/api/cases/${initial.id}`, { method:"PUT", body: JSON.stringify(payload) });
    return normalizeApiCase({
      id: initial.id, case_id: payload.case_id, disease: payload.disease,
      institution: payload.institution, prefecture: payload.prefecture,
      time_point: payload.time_point, age: payload.age, sex: payload.sex,
      data: payload.data, created_at: initial.savedAt, updated_at: new Date().toISOString(),
      is_owner: true,
    });
  }
  const res = await apiFetch("/api/cases", { method:"POST", body: JSON.stringify(payload) });
  return normalizeApiCase({
    id: res.id, case_id: payload.case_id, disease: payload.disease,
    institution: payload.institution, prefecture: payload.prefecture,
    time_point: payload.time_point, age: payload.age, sex: payload.sex,
    data: payload.data, created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
    is_owner: true,
  });
}

/* タイムポイント順序の正規化(言語非依存の並びに変換) */
const TP_ORDER = {
  "ベースライン":0,"Baseline":0,"Суурь":0,"ค่าพื้นฐาน":0,
  "1週後":1,"1 week":1,"1 долоо хоног":1,"1 สัปดาห์":1,
  "4週後":2,"4 weeks":2,"4 долоо хоног":2,"4 สัปดาห์":2,
  "8週後":3,"8 weeks":3,"8 долоо хоног":3,"8 สัปดาห์":3,
  "12週後":4,"12 weeks":4,"12 долоо хоног":4,"12 สัปดาห์":4,
  "24週後":5,"24 weeks":5,"24 долоо хоног":5,"24 สัปดาห์":5,
  "52週後":6,"52 weeks":6,"52 долоо хоног":6,"52 สัปดาห์":6,
};
const tpOrder = (tp) => TP_ORDER[tp] !== undefined ? TP_ORDER[tp] : 99;

/* ══════════════════════════════════════════════
   主要指標の定義(疾患別)
══════════════════════════════════════════════ */
const KEY_METRICS = {
  pd:     [ { key:"updrs3a", label:"MDS-UPDRS Part III", max:132, lowerIsBetter:true }, { key:"pdq39", label:"PDQ-39", max:100, lowerIsBetter:true } ],
  ad:     [ { key:"mmse", label:"MMSE", max:30, lowerIsBetter:false }, { key:"moca", label:"MoCA", max:30, lowerIsBetter:false }, { key:"cdrsb", label:"CDR-SB", max:18, lowerIsBetter:true }, { key:"adasCog", label:"ADAS-Cog", max:70, lowerIsBetter:true } ],
  als:    [ { key:"alsfrsTotal", label:"ALSFRS-R", max:48, lowerIsBetter:false }, { key:"fvc", label:"FVC (%)", max:100, lowerIsBetter:false } ],
  cancer: [ { key:"rate", label:"腫瘍径変化率 (%)", max:50, lowerIsBetter:true } ],
  stroke: [ { key:"nihss", label:"NIHSS", max:42, lowerIsBetter:true }, { key:"barthel", label:"Barthel Index", max:100, lowerIsBetter:false } ],
  sci:    [ { key:"motorUE", label:"運動・上肢", max:50, lowerIsBetter:false }, { key:"motorLE", label:"運動・下肢", max:50, lowerIsBetter:false }, { key:"scim", label:"SCIM-III", max:100, lowerIsBetter:false } ],
  dm:     [ { key:"hba1c", label:"HbA1c (%)", max:14, lowerIsBetter:true }, { key:"fpg", label:"空腹時血糖", max:300, lowerIsBetter:true } ],
  other:  [],
};

/* ══════════════════════════════════════════════
   グラフ部品
══════════════════════════════════════════════ */
function TimelineChart({ records, disease, title }) {
  const metrics = KEY_METRICS[disease] || [];
  if (metrics.length === 0 || records.length === 0) return null;
  const data = [...records].sort((a,b)=>tpOrder(a.timePoint)-tpOrder(b.timePoint))
    .map(r => {
      const row = { timePoint: r.timePoint || "—" };
      metrics.forEach(m => { const v = parseFloat(r[m.key]); if (!isNaN(v)) row[m.label] = v; });
      return row;
    });
  const colors = ["#2c6e8a","#c0392b","#2d6a4f","#d35400","#6a4c9c"];
  return <div style={{ background:"white", border:"1px solid #e5e0d8", padding:"16px 18px", marginBottom:14 }}>
    <div style={{ fontSize:13, fontWeight:700, color:"#1b3a52", marginBottom:10 }}>📈 {title}</div>
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data} margin={{ top:5, right:20, left:0, bottom:5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey="timePoint" tick={{ fontSize:11 }} />
        <YAxis tick={{ fontSize:11 }} />
        <Tooltip contentStyle={{ fontSize:12 }} />
        <Legend wrapperStyle={{ fontSize:12 }} />
        {metrics.map((m,i)=>(<Line key={m.key} type="monotone" dataKey={m.label} stroke={colors[i%colors.length]} strokeWidth={2} dot={{ r:4 }} />))}
      </LineChart>
    </ResponsiveContainer>
  </div>;
}

function DiseasePieChart({ data, title }) {
  if (!data || data.length === 0) return null;
  const colors = { pd:"#2c6e8a", ad:"#6a4c9c", als:"#c0392b", cancer:"#2d6a4f", stroke:"#d35400", sci:"#7f8c8d", dm:"#b8336a", other:"#4a6572" };
  return <div style={{ background:"white", border:"1px solid #e5e0d8", padding:"16px 18px", marginBottom:14 }}>
    <div style={{ fontSize:13, fontWeight:700, color:"#1b3a52", marginBottom:10 }}>🎯 {title}</div>
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({name,value})=>`${name}: ${value}`}>
          {data.map((d,i)=>(<Cell key={i} fill={colors[d.id] || "#888"} />))}
        </Pie>
        <Tooltip contentStyle={{ fontSize:12 }} />
      </PieChart>
    </ResponsiveContainer>
  </div>;
}

function DiseaseBarChart({ data, title }) {
  if (!data || data.length === 0) return null;
  return <div style={{ background:"white", border:"1px solid #e5e0d8", padding:"16px 18px", marginBottom:14 }}>
    <div style={{ fontSize:13, fontWeight:700, color:"#1b3a52", marginBottom:10 }}>📊 {title}</div>
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top:5, right:20, left:0, bottom:5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey="name" tick={{ fontSize:11 }} />
        <YAxis tick={{ fontSize:11 }} allowDecimals={false} />
        <Tooltip contentStyle={{ fontSize:12 }} />
        <Bar dataKey="value" fill="#2c6e8a" />
      </BarChart>
    </ResponsiveContainer>
  </div>;
}

function HistogramChart({ records, disease, metricKey, metricLabel, title }) {
  const values = records.filter(r=>r.disease===disease).map(r=>parseFloat(r[metricKey])).filter(v=>!isNaN(v));
  if (values.length === 0) return null;
  // 簡易ビン分割(5ビン)
  const min = Math.min(...values), max = Math.max(...values);
  const binCount = 5, step = (max-min)/binCount || 1;
  const bins = Array.from({length:binCount},(_,i)=>({
    name: `${(min+i*step).toFixed(1)}-${(min+(i+1)*step).toFixed(1)}`,
    value: 0
  }));
  values.forEach(v=>{ const idx = Math.min(binCount-1, Math.floor((v-min)/step)); bins[idx].value++; });
  return <div style={{ background:"white", border:"1px solid #e5e0d8", padding:"16px 18px", marginBottom:14 }}>
    <div style={{ fontSize:13, fontWeight:700, color:"#1b3a52", marginBottom:4 }}>📉 {title}</div>
    <div style={{ fontSize:11, color:"#6a7a8a", marginBottom:8 }}>n={values.length}, 平均={(values.reduce((a,b)=>a+b,0)/values.length).toFixed(2)}</div>
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={bins}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey="name" tick={{ fontSize:10 }} />
        <YAxis tick={{ fontSize:11 }} allowDecimals={false} />
        <Tooltip contentStyle={{ fontSize:12 }} />
        <Bar dataKey="value" fill="#6a4c9c" />
      </BarChart>
    </ResponsiveContainer>
  </div>;
}

/* ══════════════════════════════════════════════
   拡張グラフ部品 (ダッシュボード個人推移用)
══════════════════════════════════════════════ */

/* 1. 個別詳細: 1症例 × 各指標を別グラフで個別表示 */
function DetailedCaseCharts({ records, disease, t }) {
  const metrics = KEY_METRICS[disease] || [];
  if (metrics.length === 0 || records.length === 0) return null;
  const sorted = [...records].sort((a,b)=>tpOrder(a.timePoint)-tpOrder(b.timePoint));
  const colors = ["#2c6e8a","#c0392b","#2d6a4f","#d35400","#6a4c9c"];
  return <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(380px, 1fr))", gap:12 }}>
    {metrics.map((m, idx) => {
      const data = sorted.map(r => {
        const v = parseFloat(r[m.key]);
        return { timePoint: r.timePoint || "—", value: isNaN(v) ? null : v };
      }).filter(d=>d.value !== null);
      if (data.length === 0) return null;
      return <div key={m.key} style={{ background:"white", border:"1px solid #e5e0d8", padding:"14px 16px" }}>
        <div style={{ fontSize:12.5, fontWeight:700, color:"#1b3a52", marginBottom:8 }}>{m.label}</div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data} margin={{ top:5, right:10, left:0, bottom:5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="timePoint" tick={{ fontSize:10 }} />
            <YAxis tick={{ fontSize:10 }} domain={[0, m.max]} />
            <Tooltip contentStyle={{ fontSize:11 }} />
            <Line type="monotone" dataKey="value" stroke={colors[idx%colors.length]} strokeWidth={2.5} dot={{ r:5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>;
    })}
  </div>;
}

/* 2. 症例比較: 複数症例を1つのグラフに重ねて表示 (指標1つ選択) */
function CompareCasesChart({ allCases, disease, selectedCaseIds, metricKey, metricLabel, t }) {
  if (!selectedCaseIds || selectedCaseIds.length === 0) return null;
  // 全症例の評価時点を集約してX軸に
  const allTimePoints = new Set();
  selectedCaseIds.forEach(cid => {
    allCases.filter(c=>c.disease===disease && c.caseId===cid)
      .forEach(c=>{ if(c.timePoint) allTimePoints.add(c.timePoint); });
  });
  const tpList = Array.from(allTimePoints).sort((a,b)=>tpOrder(a)-tpOrder(b));
  // 各時点で各症例のスコアを並べる
  const data = tpList.map(tp => {
    const row = { timePoint: tp };
    selectedCaseIds.forEach(cid => {
      const rec = allCases.find(c=>c.disease===disease && c.caseId===cid && c.timePoint===tp);
      if (rec) {
        const v = parseFloat(rec[metricKey]);
        if (!isNaN(v)) row[cid] = v;
      }
    });
    return row;
  });
  const colors = ["#2c6e8a","#c0392b","#2d6a4f","#d35400","#6a4c9c","#b8336a","#7f8c8d","#4a6572"];
  return <div style={{ background:"white", border:"1px solid #e5e0d8", padding:"16px 18px" }}>
    <div style={{ fontSize:13, fontWeight:700, color:"#1b3a52", marginBottom:10 }}>🔀 {metricLabel} — {t.compareTitle}</div>
    <ResponsiveContainer width="100%" height={320}>
      <LineChart data={data} margin={{ top:5, right:20, left:0, bottom:5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey="timePoint" tick={{ fontSize:11 }} />
        <YAxis tick={{ fontSize:11 }} />
        <Tooltip contentStyle={{ fontSize:12 }} />
        <Legend wrapperStyle={{ fontSize:11 }} />
        {selectedCaseIds.map((cid, i) => (
          <Line key={cid} type="monotone" dataKey={cid} stroke={colors[i%colors.length]} strokeWidth={2} dot={{ r:4 }} connectNulls />
        ))}
      </LineChart>
    </ResponsiveContainer>
  </div>;
}

/* 3. 指標フォーカス: 1指標を選び、その指標を持つ全症例を1グラフに重ねる */
function MetricFocusChart({ allCases, disease, metricKey, metricLabel, t }) {
  const cases = allCases.filter(c=>c.disease===disease);
  const caseIds = Array.from(new Set(cases.map(c=>c.caseId).filter(Boolean)));
  // 指標を持つ症例のみ
  const validIds = caseIds.filter(cid => cases.some(c=>c.caseId===cid && c[metricKey] !== "" && c[metricKey] !== undefined));
  if (validIds.length === 0) return <div style={{ background:"#fff8e1", border:"1px solid #f0c040", padding:"16px", fontSize:12.5, color:"#7a5c00" }}>
    {t.noDataForMetric}
  </div>;
  const allTimePoints = new Set();
  cases.forEach(c => { if (c.timePoint) allTimePoints.add(c.timePoint); });
  const tpList = Array.from(allTimePoints).sort((a,b)=>tpOrder(a)-tpOrder(b));
  const data = tpList.map(tp => {
    const row = { timePoint: tp };
    validIds.forEach(cid => {
      const rec = cases.find(c=>c.caseId===cid && c.timePoint===tp);
      if (rec) { const v = parseFloat(rec[metricKey]); if (!isNaN(v)) row[cid] = v; }
    });
    return row;
  });
  const colors = ["#2c6e8a","#c0392b","#2d6a4f","#d35400","#6a4c9c","#b8336a","#7f8c8d","#4a6572","#1b3a52","#856404"];
  return <div style={{ background:"white", border:"1px solid #e5e0d8", padding:"16px 18px" }}>
    <div style={{ fontSize:13, fontWeight:700, color:"#1b3a52", marginBottom:4 }}>📊 {metricLabel}</div>
    <div style={{ fontSize:11, color:"#6a7a8a", marginBottom:8 }}>{t.metricFocusSub}: n={validIds.length}</div>
    <ResponsiveContainer width="100%" height={340}>
      <LineChart data={data} margin={{ top:5, right:20, left:0, bottom:5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey="timePoint" tick={{ fontSize:11 }} />
        <YAxis tick={{ fontSize:11 }} />
        <Tooltip contentStyle={{ fontSize:11 }} />
        <Legend wrapperStyle={{ fontSize:10 }} />
        {validIds.map((cid, i) => (
          <Line key={cid} type="monotone" dataKey={cid} stroke={colors[i%colors.length]} strokeWidth={1.8} dot={{ r:3 }} connectNulls />
        ))}
      </LineChart>
    </ResponsiveContainer>
  </div>;
}

/* 4. 疾患平均: 疾患の平均値推移 + 個別症例を薄く重ねる */
function DiseaseMeanChart({ allCases, disease, metricKey, metricLabel, t }) {
  const cases = allCases.filter(c=>c.disease===disease);
  const allTimePoints = new Set();
  cases.forEach(c => { if (c.timePoint) allTimePoints.add(c.timePoint); });
  const tpList = Array.from(allTimePoints).sort((a,b)=>tpOrder(a)-tpOrder(b));
  if (tpList.length === 0) return null;
  const caseIds = Array.from(new Set(cases.map(c=>c.caseId).filter(Boolean)));
  const data = tpList.map(tp => {
    const row = { timePoint: tp };
    const vals = [];
    caseIds.forEach(cid => {
      const rec = cases.find(c=>c.caseId===cid && c.timePoint===tp);
      if (rec) { const v = parseFloat(rec[metricKey]); if (!isNaN(v)) { row[cid] = v; vals.push(v); }}
    });
    row.__mean = vals.length > 0 ? (vals.reduce((a,b)=>a+b,0)/vals.length) : null;
    row.__n = vals.length;
    return row;
  });
  return <div style={{ background:"white", border:"1px solid #e5e0d8", padding:"16px 18px" }}>
    <div style={{ fontSize:13, fontWeight:700, color:"#1b3a52", marginBottom:4 }}>📐 {metricLabel} — {t.meanTitle}</div>
    <div style={{ fontSize:11, color:"#6a7a8a", marginBottom:8 }}>{t.meanSub}: n={caseIds.length}</div>
    <ResponsiveContainer width="100%" height={320}>
      <LineChart data={data} margin={{ top:5, right:20, left:0, bottom:5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey="timePoint" tick={{ fontSize:11 }} />
        <YAxis tick={{ fontSize:11 }} />
        <Tooltip contentStyle={{ fontSize:11 }} />
        <Legend wrapperStyle={{ fontSize:11 }} />
        {caseIds.map((cid) => (
          <Line key={cid} type="monotone" dataKey={cid} stroke="#cbd6df" strokeWidth={1.2} dot={{ r:2 }} connectNulls legendType="none" />
        ))}
        <Line type="monotone" dataKey="__mean" name={t.meanLabel} stroke="#c0392b" strokeWidth={3.5} dot={{ r:5 }} connectNulls />
      </LineChart>
    </ResponsiveContainer>
  </div>;
}

/* 5. 全症例一覧: ミニグラフを格子状に並べる */
function CaseGridView({ allCases, disease, metricKey, metricLabel, t, onSelect }) {
  const cases = allCases.filter(c=>c.disease===disease);
  const caseIds = Array.from(new Set(cases.map(c=>c.caseId).filter(Boolean)));
  if (caseIds.length === 0) return <div style={{ background:"#fff8e1", border:"1px solid #f0c040", padding:"16px", fontSize:12.5, color:"#7a5c00" }}>
    {t.noDataForMetric}
  </div>;
  return <div>
    <div style={{ fontSize:11.5, color:"#6a7a8a", marginBottom:10 }}>📐 {metricLabel} — {t.gridSub}: {caseIds.length}{t.casesUnit}</div>
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px, 1fr))", gap:10 }}>
      {caseIds.map(cid => {
        const records = cases.filter(c=>c.caseId===cid).sort((a,b)=>tpOrder(a.timePoint)-tpOrder(b.timePoint));
        const data = records.map(r => {
          const v = parseFloat(r[metricKey]);
          return { tp: (r.timePoint || "—").slice(0,6), value: isNaN(v) ? null : v };
        }).filter(d=>d.value !== null);
        const hasData = data.length > 0;
        return <div key={cid} onClick={()=>onSelect && onSelect(cid)}
          style={{ background:"white", border:"1px solid #e5e0d8", padding:"10px 12px", cursor:onSelect?"pointer":"default", transition:"all .15s" }}
          onMouseEnter={e=>{if(onSelect){e.currentTarget.style.borderColor="#2c6e8a";e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,.06)";}}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor="#e5e0d8";e.currentTarget.style.boxShadow="none";}}>
          <div style={{ fontSize:11, fontFamily:"monospace", color:"#1b3a52", fontWeight:700, marginBottom:4 }}>{cid}</div>
          {hasData ? <>
            <ResponsiveContainer width="100%" height={80}>
              <LineChart data={data} margin={{ top:2, right:2, left:2, bottom:2 }}>
                <XAxis dataKey="tp" tick={{ fontSize:8 }} interval={0} />
                <YAxis hide />
                <Tooltip contentStyle={{ fontSize:10 }} />
                <Line type="monotone" dataKey="value" stroke="#2c6e8a" strokeWidth={2} dot={{ r:2 }} />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ fontSize:10, color:"#6a7a8a", marginTop:2 }}>{data.length}{t.pointsUnit} / {records.length}{t.recordsUnit}</div>
          </> : <div style={{ fontSize:10.5, color:"#aaa", padding:"24px 0", textAlign:"center" }}>{t.noMetricData}</div>}
        </div>;
      })}
    </div>
  </div>;
}

/* ══════════════════════════════════════════════
   CSVエクスポート
══════════════════════════════════════════════ */
function exportCSV(cases) {
  if (!cases || cases.length === 0) { alert("データがありません"); return; }
  const keys = Array.from(cases.reduce((s,c)=>{Object.keys(c).forEach(k=>s.add(k));return s},new Set()));
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = Array.isArray(v) ? v.join(";") : (typeof v === "object" ? JSON.stringify(v) : String(v));
    return /[",\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s;
  };
  const csv = [keys.join(","), ...cases.map(c => keys.map(k => escape(c[k])).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type:"text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `sc-net-cases-${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Done({ t, onReset, savedRecord, disease, lang }) {
  const [records, setRecords] = useState([]);
  useEffect(() => {
    if (!savedRecord) { setRecords([]); return; }
    let cancelled = false;
    apiFetch("/api/cases/mine")
      .then(res => {
        if (cancelled) return;
        const all = (res.cases || []).map(normalizeApiCase);
        setRecords(all.filter(c => c.disease === disease && c.caseId === savedRecord.caseId)
          .sort((a, b) => (a.timePoint || "").localeCompare(b.timePoint || "")));
      })
      .catch(() => { if (!cancelled) setRecords([]); });
    return () => { cancelled = true; };
  }, [savedRecord, disease]);
  const hasTimeline = records.length >= 2;
  return <div style={{ padding:"32px 20px" }}>
    <div style={{ textAlign:"center", marginBottom:24 }}>
      <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
      <div style={{ fontSize:18, fontWeight:700, color:"#1b3a52", marginBottom:6 }}>{t.done}</div>
      <div style={{ color:"#6a7a8a", marginBottom:18, fontSize:13 }}>{t.doneSub}</div>
      {savedRecord && <div style={{ fontSize:12, color:"#5a7a8a", marginBottom:6 }}>
        {t.savedCaseInfo}: <strong>{savedRecord.caseId}</strong> / {savedRecord.timePoint || "—"}
      </div>}
    </div>
    {hasTimeline && <TimelineChart records={records} disease={disease} title={`${savedRecord.caseId} ${t.timelineTitle}`} />}
    {!hasTimeline && savedRecord && <div style={{ background:"#fff8e1", border:"1px solid #f0c040", padding:"12px 16px", marginBottom:14, fontSize:12.5, color:"#7a5c00", textAlign:"center" }}>
      {t.timelineNeedMore}
    </div>}
    <div style={{ textAlign:"center", marginTop:18 }}>
      <button onClick={onReset} style={{ background:"#1b3a52", color:"white", border:"none", padding:"11px 28px", fontFamily:"inherit", fontSize:13, cursor:"pointer" }}>{t.doneBtn}</button>
    </div>
  </div>;
}

const AE_GRADES_FN = (t) => ["Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5"];

function SafetyBlock({ aeList, form, setForm, t }) {
  const toggle = ae => { const cur=form.ae||[],ex=cur.find(x=>x.name===ae); setForm(f=>({...f,ae:ex?cur.filter(x=>x.name!==ae):[...cur,{name:ae,grade:""}]})); };
  const setGrade = (ae,grade) => setForm(f=>({...f,ae:(f.ae||[]).map(x=>x.name===ae?{...x,grade}:x)}));
  const ae = form.ae||[];
  return <Blk>
    <Sec icon="🛡️" title={t.safetyTitle} />
    <Lbl t={t}>{t.aeLabel}</Lbl>
    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:6, marginBottom:12 }}>
      {aeList.map(a=>{
        const sel=ae.find(x=>x.name===a);
        return <button key={a} onClick={()=>toggle(a)} style={{ padding:"5px 12px", border:"1.5px solid", borderRadius:20, cursor:"pointer", fontFamily:"inherit", fontSize:12, background:sel?"#1b3a52":"white", color:sel?"white":"#5a6a7a", borderColor:sel?"#1b3a52":"#d8d2c8" }}>{a}</button>;
      })}
    </div>
    {ae.length>0 && <div style={{ background:"#f8f5f0", padding:"12px 16px", border:"1px solid #e5dfd5" }}>
      {ae.map(a=><div key={a.name} style={{ display:"flex", alignItems:"center", gap:12, marginBottom:8, flexWrap:"wrap" }}>
        <div style={{ minWidth:100, fontSize:13, fontWeight:600 }}>⚠ {a.name}</div>
        <div style={{ flex:1, minWidth:180 }}>
          <select value={a.grade} onChange={e=>setGrade(a.name,e.target.value)} style={{ ...S.select, color:a.grade?"#1a1a2e":"#9aa" }}>
            <option value="">{t.gradeLabel}</option>
            {["Grade 1","Grade 2","Grade 3","Grade 4","Grade 5"].map(g=><option key={g} value={g}>{g}</option>)}
          </select>
        </div>
      </div>)}
    </div>}
    <label style={{ display:"flex", alignItems:"center", gap:8, marginTop:12, cursor:"pointer", fontSize:13, color:"#c0392b", fontWeight:700 }}>
      <input type="checkbox" checked={form.sae||false} onChange={e=>setForm(f=>({...f,sae:e.target.checked}))} style={{ width:14, height:14 }} />
      {t.saeLabel}
    </label>
    {form.sae && <div style={{ background:"#fde8e8", border:"1px solid #e99", padding:"10px 14px", marginTop:8, fontSize:13, color:"#7a1111" }}>{t.saeWarn}</div>}
    <div style={{ marginTop:12 }}><Lbl t={t}>{t.notesLabel}</Lbl>
      <textarea value={form.notes||""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={3}
        style={{ ...S.input, resize:"vertical", lineHeight:1.6 }} placeholder={t.notesPh} onFocus={focus} onBlur={blur} /></div>
  </Blk>;
}

/* ── 共通：上清液投与情報ブロック ────────────── */
function DosageBlock({ f, set, t }) {
  return <Blk>
    <Sec icon="💉" title={t.dosageTitle} sub={t.dosageSub} />
    {/* 1. 投与量/日 */}
    <div style={{ marginBottom:14 }}>
      <Lbl t={t} req>{t.dailyDose}</Lbl>
      <Inp type="number" value={f.dailyDose||""} onChange={e=>set("dailyDose",e.target.value)} placeholder={t.dailyDosePh} />
    </div>
    {/* 2. 投与期間及び回数 */}
    <div style={{ marginBottom:14 }}>
      <Lbl t={t} req>{t.treatmentPeriod}</Lbl>
      <Inp value={f.treatmentPeriod||""} onChange={e=>set("treatmentPeriod",e.target.value)} placeholder={t.treatmentPeriodPh} />
    </div>
    {/* 3. 使用上清液由来及び投与量 */}
    <div>
      <Lbl t={t} req>{t.serumSource}</Lbl>
      <div style={{ background:"#f0f4f7", border:"1px solid #d8e4eb", padding:"14px 16px", marginTop:6 }}>
        <Row cols={3}>
          <div><Lbl t={t}>{t.serumRow1}</Lbl><Inp type="number" value={f.serumRow1||""} onChange={e=>set("serumRow1",e.target.value)} placeholder={t.serumRow1Ph} /></div>
          <div><Lbl t={t}>{t.serumRow2}</Lbl><Inp type="number" value={f.serumRow2||""} onChange={e=>set("serumRow2",e.target.value)} placeholder={t.serumRow2Ph} /></div>
          <div><Lbl t={t}>{t.serumRow3}</Lbl><Inp type="number" value={f.serumRow3||""} onChange={e=>set("serumRow3",e.target.value)} placeholder={t.serumRow3Ph} /></div>
        </Row>
        <div style={{ marginTop:4 }}>
          <Lbl t={t}>{t.serumOther}</Lbl>
          <Inp value={f.serumOther||""} onChange={e=>set("serumOther",e.target.value)} placeholder={t.serumOtherPh} />
        </div>
        {/* 合計表示 */}
        {(f.serumRow1||f.serumRow2||f.serumRow3) && (
          <div style={{ marginTop:10, background:"#eaf4f8", border:"1px solid #b3d4e0", padding:"8px 14px", fontSize:13 }}>
            <span style={{ color:"#5a7a8a" }}>合計投与量：</span>
            <strong style={{ color:"#1b3a52", fontSize:15 }}>
              {((parseFloat(f.serumRow1)||0)+(parseFloat(f.serumRow2)||0)+(parseFloat(f.serumRow3)||0))} ml
            </strong>
          </div>
        )}
      </div>
    </div>
  </Blk>;
}


const HY_OPTIONS = {
  ja:["Stage 1（片側のみ）","Stage 1.5","Stage 2（両側、姿勢反射障害なし）","Stage 2.5","Stage 3（姿勢反射障害あり）","Stage 4","Stage 5（車椅子・寝たきり）"],
  en:["Stage 1 (Unilateral)","Stage 1.5","Stage 2 (Bilateral, no balance issues)","Stage 2.5","Stage 3 (Balance impaired)","Stage 4","Stage 5 (Wheelchair/bedridden)"],
  mn:["Stage 1 (Нэг талд)","Stage 1.5","Stage 2 (Хоёр талд)","Stage 2.5","Stage 3","Stage 4","Stage 5 (Тэргэнцэр/хэвтрийн)"],
  th:["Stage 1 (ข้างเดียว)","Stage 1.5","Stage 2 (สองข้าง)","Stage 2.5","Stage 3","Stage 4","Stage 5 (รถเข็น/ติดเตียง)"],
};
const PD_AE = {
  ja:["血圧低下","ふらつき","転倒","眠気","悪心","幻覚","その他"],
  en:["Hypotension","Dizziness","Falls","Drowsiness","Nausea","Hallucinations","Other"],
  mn:["Цусны даралт буурах","Эргэлт","Унах","Нойрсомтгой","Дотор муухайрах","Хөрвөс","Бусад"],
  th:["ความดันต่ำ","เวียนศีรษะ","ล้ม","ง่วงนอน","คลื่นไส้","ประสาทหลอน","อื่นๆ"],
};
function PDForm({ t, lang, initial, onCancelEdit }) {
  const [step,setStep]=useState(1);const [done,setDone]=useState(false);
  const [savedRec,setSavedRec]=useState(null);
  const [f,setF]=useState(initial ? {...initial} : {caseId:"",institution:"",prefecture:"",timePoint:"",age:"",sex:"",duration:"",
    dailyDose:"",treatmentPeriod:"",serumRow1:"",serumRow2:"",serumRow3:"",serumOther:"",
    hy:"",medication:"",ldopaTime:"",onOff:"",updrs3b:"",updrs3a:"",pdq39:"",ae:[],sae:false,notes:""});
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const chg=f.updrs3b&&f.updrs3a?(((parseFloat(f.updrs3a)-parseFloat(f.updrs3b))/parseFloat(f.updrs3b))*100).toFixed(1):null;
  const STEPS=[t.pdStep1,t.pdStep2,t.pdStep3,t.pdStep4];
  const TP={ja:["治療前（ベースライン）","治療直後","24時間後","1週間後","4週間後","12週間後"],en:["Pre-treatment (Baseline)","Immediately after","24 hours","1 week","4 weeks","12 weeks"],mn:["Эмчилгээний өмнө","Шууд дараа","24 цагийн дараа","1 долоо хоногийн дараа","4 долоо хоногийн дараа","12 долоо хоногийн дараа"],th:["ก่อนการรักษา","ทันทีหลังการรักษา","24 ชั่วโมง","1 สัปดาห์","4 สัปดาห์","12 สัปดาห์"]};
  const [errors,setErrors]=useState({});const validateAll=()=>{const allErrors={};for(let s=1;s<=4;s++){Object.assign(allErrors,validateForm("pd",s,f,t));}return allErrors;};const [submitting,setSubmitting]=useState(false);const handleSubmit=async()=>{const e=validateAll();if(Object.keys(e).length>0){setErrors(e);alert(t.errorTitle+"\n"+Object.entries(e).map(([k,v])=>k+": "+v).join("\n"));return;}setErrors({});setSubmitting(true);try{const rec=await persistCaseRecord("pd",f,initial);setSavedRec(rec);setDone(true);}catch(err){alert(err.message);}finally{setSubmitting(false);}};
  if(done) return <Done t={t} onReset={()=>{setDone(false);setSavedRec(null);setF({ae:[],sae:false,notes:""});setStep(1);if(onCancelEdit)onCancelEdit();}} savedRecord={savedRec} disease="pd" lang={lang} />;
  return <div>
    <Steps steps={STEPS} current={step} onStep={setStep} />
    <ErrorBanner errors={errors} t={t} />
    {step===1 && <><Blk><Sec icon="📋" title={t.pdStep1} />
      <Row><div><Lbl t={t} req>{t.prefectureLabel}</Lbl><Sel value={f.prefecture} onChange={e=>set("prefecture",e.target.value)} options={PREFECTURES} ph={t.prefecturePh} /></div><div></div></Row>
      <Row><div><Lbl t={t} req>{t.caseId}</Lbl><Inp value={f.caseId} onChange={e=>set("caseId",e.target.value)} placeholder={t.caseIdPh} /></div>
        <div><Lbl t={t}>{t.institution}</Lbl><Inp value={f.institution} onChange={e=>set("institution",e.target.value)} placeholder={t.institutionPh} /></div></Row>
      <Row cols={3}><div><Lbl t={t}>{t.age}</Lbl><Inp type="number" value={f.age} onChange={e=>set("age",e.target.value)} placeholder={t.agePh} /></div>
        <div><Lbl t={t}>{t.sex}</Lbl><Sel value={f.sex} onChange={e=>set("sex",e.target.value)} options={[t.male,t.female,t.other]} /></div>
        <div><Lbl t={t}>{t.duration}</Lbl><Inp type="number" value={f.duration} onChange={e=>set("duration",e.target.value)} placeholder={t.durationPh} /></div></Row>
      <Row><div><Lbl t={t}>{t.timePoint}</Lbl><Sel value={f.timePoint} onChange={e=>set("timePoint",e.target.value)} options={TP[lang]} /></div><div></div></Row>
    </Blk>
    <DosageBlock f={f} set={set} t={t} />
    </>}
    {step===2 && <><Blk><Sec icon="⚖️" title={t.hyTitle} sub={t.hySub} />
      <Sel value={f.hy} onChange={e=>set("hy",e.target.value)} options={HY_OPTIONS[lang]} /></Blk>
    <Blk><Sec icon="💊" title={t.medTitle} sub={t.medSub} />
      <div style={{ marginBottom:12 }}><Lbl t={t}>{t.medLabel}</Lbl>
        <textarea value={f.medication} onChange={e=>set("medication",e.target.value)} rows={2}
          style={{ ...S.input, resize:"vertical" }} placeholder={t.medPh} onFocus={focus} onBlur={blur} /></div>
      <Row><div><Lbl t={t}>{t.ldopaTime}</Lbl><Inp type="number" value={f.ldopaTime} onChange={e=>set("ldopaTime",e.target.value)} placeholder={t.ldopaPh} /></div>
        <div><Lbl t={t} req>{t.onOffLabel}</Lbl>
          <div style={{ display:"flex", gap:8, marginTop:6, flexWrap:"wrap" }}>
            {[t.on,t.off,t.partialOn,t.unknown].map(o=><label key={o} style={{ display:"flex", alignItems:"center", gap:4, cursor:"pointer", fontSize:12.5 }}>
              <input type="radio" checked={f.onOff===o} onChange={()=>set("onOff",o)} />{o}
            </label>)}
          </div></div></Row>
    </Blk></>}
    {step===3 && <Blk><Sec icon="🏃" title={t.updrsTitle} sub={t.updrsSub} />
      <Row><div><Lbl t={t} req>{t.updrsB}</Lbl><Inp type="number" value={f.updrs3b} onChange={e=>set("updrs3b",e.target.value)} placeholder={t.updrsBPh} />
          <div style={{ background:"#eee",height:7,borderRadius:4,marginTop:6 }}><div style={{ background:"#e67e22",height:"100%",borderRadius:4,width:`${Math.min(100,(parseFloat(f.updrs3b)||0)/132*100)}%` }} /></div>
        </div>
        <div><Lbl t={t} req>{t.updrsA}</Lbl><Inp type="number" value={f.updrs3a} onChange={e=>set("updrs3a",e.target.value)} placeholder={t.updrsAPh} />
          <div style={{ background:"#eee",height:7,borderRadius:4,marginTop:6 }}><div style={{ background:"#2d6a4f",height:"100%",borderRadius:4,width:`${Math.min(100,(parseFloat(f.updrs3a)||0)/132*100)}%` }} /></div>
        </div></Row>
      {chg!==null && <div style={{ background:"#eaf4f8",border:"1px solid #b3d4e0",padding:"12px 16px",marginBottom:12 }}>
        <span style={{ fontSize:11.5,color:"#5a7a8a" }}>{t.changeRate}：</span>
        <span style={{ fontSize:24,fontWeight:700,color:parseFloat(chg)<0?"#2d6a4f":"#c0392b",marginLeft:8 }}>{parseFloat(chg)>0?"+":""}{chg}%</span>
      </div>}
      <div style={{ marginTop:8 }}><Lbl t={t}>{t.pdq39}</Lbl><Inp type="number" value={f.pdq39} onChange={e=>set("pdq39",e.target.value)} placeholder={t.pdq39Ph} /></div>
    </Blk>}
    {step===4 && <SafetyBlock aeList={PD_AE[lang]} form={f} setForm={setF} t={t} />}
    <NavBtns step={step} total={STEPS.length} t={t} onBack={()=>setStep(s=>Math.max(1,s-1))} onNext={()=>setStep(s=>Math.min(STEPS.length,s+1))} onSubmit={submitting ? undefined : handleSubmit} />
  </div>;
}

/* ── AD ─────────────────────────────────────── */
const AD_AE={ja:["転倒","せん妄","発熱","食欲不振","不眠","興奮","その他"],en:["Falls","Delirium","Fever","Appetite loss","Insomnia","Agitation","Other"],mn:["Унах","Мэдрэлийн хямрал","Халуурах","Хоол идэхгүй болох","Нойргүйдэл","Түгшүүр","Бусад"],th:["ล้ม","เพ้อคลั่ง","มีไข้","เบื่ออาหาร","นอนไม่หลับ","ก้าวร้าว","อื่นๆ"]};
const CDR_OPT={ja:["0（健常）","0.5（MCI）","1（軽度）","2（中等度）","3（重度）"],en:["0 (Normal)","0.5 (MCI)","1 (Mild)","2 (Moderate)","3 (Severe)"],mn:["0 (Хэвийн)","0.5 (MCI)","1 (Хөнгөн)","2 (Дунд)","3 (Хүнд)"],th:["0 (ปกติ)","0.5 (MCI)","1 (ระดับน้อย)","2 (ระดับปานกลาง)","3 (ระดับรุนแรง)"]};
const DIAG_OPT={ja:["アルツハイマー型認知症（AD）","MCI due to AD","混合型認知症","レビー小体型認知症","その他"],en:["Alzheimer's disease (AD)","MCI due to AD","Mixed dementia","Lewy body dementia","Other"],mn:["Альцгеймерийн өвчин","MCI","Холимог","Леви биетийн","Бусад"],th:["โรคอัลไซเมอร์ (AD)","MCI due to AD","สมองเสื่อมผสม","สมองเสื่อม Lewy body","อื่นๆ"]};
function ADForm({ t, lang, initial, onCancelEdit }) {
  const [step,setStep]=useState(1);const [done,setDone]=useState(false);
  const [savedRec,setSavedRec]=useState(null);
  const [f,setF]=useState(initial ? {...initial} : {caseId:"",institution:"",prefecture:"",timePoint:"",age:"",diag:"",mriCt:"",
    dailyDose:"",treatmentPeriod:"",serumRow1:"",serumRow2:"",serumRow3:"",serumOther:"",
    mmse:"",moca:"",cdr:"",cdrsb:"",adasCog:"",dasc21:"",npiQ:"",careNote:"",proxy:false,ae:[],sae:false,notes:""});
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const STEPS=[t.adStep1,t.adStep2,t.adStep3,t.adStep4];
  const TP={ja:["ベースライン","1週後","4週後","12週後","24週後","52週後"],en:["Baseline","1 week","4 weeks","12 weeks","24 weeks","52 weeks"],mn:["Суурь","1 долоо хоног","4 долоо хоног","12 долоо хоног","24 долоо хоног","52 долоо хоног"],th:["ค่าพื้นฐาน","1 สัปดาห์","4 สัปดาห์","12 สัปดาห์","24 สัปดาห์","52 สัปดาห์"]};
  const [errors,setErrors]=useState({});const validateAll=()=>{const allErrors={};for(let s=1;s<=4;s++){Object.assign(allErrors,validateForm("ad",s,f,t));}return allErrors;};const [submitting,setSubmitting]=useState(false);const handleSubmit=async()=>{const e=validateAll();if(Object.keys(e).length>0){setErrors(e);alert(t.errorTitle+"\n"+Object.entries(e).map(([k,v])=>k+": "+v).join("\n"));return;}setErrors({});setSubmitting(true);try{const rec=await persistCaseRecord("ad",f,initial);setSavedRec(rec);setDone(true);}catch(err){alert(err.message);}finally{setSubmitting(false);}};
  if(done) return <Done t={t} onReset={()=>{setDone(false);setSavedRec(null);setF({ae:[],sae:false,notes:"",proxy:false});setStep(1);if(onCancelEdit)onCancelEdit();}} savedRecord={savedRec} disease="ad" lang={lang} />;
  return <div>
    <div style={{ background:"#eaf4f8",border:"1px solid #b3d4e0",padding:"9px 14px",marginBottom:12,fontSize:12.5,color:"#1b3a52" }}>
      {t.noticeAD}<strong>{t.noticeADStrong}</strong>{t.noticeADEnd}
    </div>
    <Steps steps={STEPS} current={step} onStep={setStep} />
    <ErrorBanner errors={errors} t={t} />
    {step===1 && <><Blk><Sec icon="📋" title={t.adStep1} />
      <Row><div><Lbl t={t} req>{t.prefectureLabel}</Lbl><Sel value={f.prefecture} onChange={e=>set("prefecture",e.target.value)} options={PREFECTURES} ph={t.prefecturePh} /></div><div></div></Row>
      <Row><div><Lbl t={t} req>{t.caseId}</Lbl><Inp value={f.caseId} onChange={e=>set("caseId",e.target.value)} placeholder="AD-2026-001" /></div>
        <div><Lbl t={t}>{t.age}</Lbl><Inp type="number" value={f.age} onChange={e=>set("age",e.target.value)} placeholder={t.agePh} /></div></Row>
      <Row><div><Lbl t={t} req>{t.diagName}</Lbl><Sel value={f.diag} onChange={e=>set("diag",e.target.value)} options={DIAG_OPT[lang]} /></div>
        <div><Lbl t={t}>{t.timePoint}</Lbl><Sel value={f.timePoint} onChange={e=>set("timePoint",e.target.value)} options={TP[lang]} /></div></Row>
      <div style={{ marginBottom:12 }}><Lbl t={t}>{t.mriCt}</Lbl>
        <textarea value={f.mriCt} onChange={e=>set("mriCt",e.target.value)} rows={2} style={{ ...S.input,resize:"vertical" }} placeholder={t.mriPh} onFocus={focus} onBlur={blur} /></div>
      <label style={{ display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13 }}>
        <input type="checkbox" checked={f.proxy} onChange={e=>set("proxy",e.target.checked)} /><strong>{t.proxy}</strong></label>
    </Blk>
    <DosageBlock f={f} set={set} t={t} />
    </>}
    {step===2 && <><Blk><Sec icon="⚖️" title={t.cdrTitle} sub={t.cdrSub} />
      <Row><div><Lbl t={t} req>{t.cdrLabel}</Lbl><Sel value={f.cdr} onChange={e=>set("cdr",e.target.value)} options={CDR_OPT[lang]} /></div>
        <div><Lbl t={t} req>{t.cdrsb}</Lbl><Inp type="number" value={f.cdrsb} onChange={e=>set("cdrsb",e.target.value)} placeholder={t.cdrsbPh} />
          <div style={{ background:"#eee",height:7,borderRadius:4,marginTop:5 }}><div style={{ background:"#1b6080",height:"100%",borderRadius:4,width:`${Math.min(100,(parseFloat(f.cdrsb)||0)/18*100)}%` }} /></div></div></Row>
    </Blk>
    <Blk><Sec icon="🧠" title={t.adStep2} />
      <Row><div><Lbl t={t}>{t.mmse}</Lbl><Inp type="number" value={f.mmse} onChange={e=>set("mmse",e.target.value)} placeholder={t.mmsePh} />
          <div style={{ background:"#eee",height:7,borderRadius:4,marginTop:5 }}><div style={{ background:"#2c6e8a",height:"100%",borderRadius:4,width:`${Math.min(100,(parseFloat(f.mmse)||0)/30*100)}%` }} /></div></div>
        <div><Lbl t={t}>{t.moca}</Lbl><Inp type="number" value={f.moca} onChange={e=>set("moca",e.target.value)} placeholder={t.mocaPh} />
          <div style={{ background:"#eee",height:7,borderRadius:4,marginTop:5 }}><div style={{ background:"#2c6e8a",height:"100%",borderRadius:4,width:`${Math.min(100,(parseFloat(f.moca)||0)/30*100)}%` }} /></div></div></Row>
      <div><Lbl t={t}>{t.adasCog}</Lbl><Inp type="number" value={f.adasCog} onChange={e=>set("adasCog",e.target.value)} placeholder={t.adasPh} /></div>
    </Blk></>}
    {step===3 && <><Blk><Sec icon="🏠" title={t.adStep3} />
      <Row><div><Lbl t={t}>{t.dasc21}</Lbl><Inp type="number" value={f.dasc21} onChange={e=>set("dasc21",e.target.value)} placeholder={t.dasc21Ph} /></div>
        <div><Lbl t={t}>{t.npiQ}</Lbl><Inp type="number" value={f.npiQ} onChange={e=>set("npiQ",e.target.value)} placeholder={t.npiPh} /></div></Row>
    </Blk>
    <Blk><Sec icon="👨‍👩‍👧" title={t.careNote} />
      <textarea value={f.careNote} onChange={e=>set("careNote",e.target.value)} rows={3}
        style={{ ...S.input,resize:"vertical" }} placeholder={t.careNotePh} onFocus={focus} onBlur={blur} /></Blk></>}
    {step===4 && <SafetyBlock aeList={AD_AE[lang]} form={f} setForm={setF} t={t} />}
    <NavBtns step={step} total={STEPS.length} t={t} onBack={()=>setStep(s=>Math.max(1,s-1))} onNext={()=>setStep(s=>Math.min(STEPS.length,s+1))} onSubmit={submitting ? undefined : handleSubmit} />
  </div>;
}

/* ── ALS ─────────────────────────────────────── */
const ALS_AE={ja:["呼吸不全","誤嚥性肺炎","感染症","転倒","倦怠感","その他"],en:["Respiratory failure","Aspiration pneumonia","Infection","Falls","Fatigue","Other"],mn:["Амьсгалын дутагдал","Залгихад уушигны хатгалгаа","Халдвар","Унах","Ядаргаа","Бусад"],th:["ระบบหายใจล้มเหลว","ปอดบวมจากการสำลัก","การติดเชื้อ","ล้ม","อ่อนเพลีย","อื่นๆ"]};
const ALSFRS_ITEMS_FN=(lang)=>[
  {k:"speech",l:{ja:"1.言語",en:"1.Speech",mn:"1.Яриа",th:"1.การพูด"}},
  {k:"salivation",l:{ja:"2.流涎",en:"2.Salivation",mn:"2.Шүлс",th:"2.น้ำลาย"}},
  {k:"swallowing",l:{ja:"3.嚥下",en:"3.Swallowing",mn:"3.Залгих",th:"3.การกลืน"}},
  {k:"handwriting",l:{ja:"4.書字",en:"4.Handwriting",mn:"4.Бичих",th:"4.การเขียน"}},
  {k:"cutFood",l:{ja:"5.食事",en:"5.Cutting food",mn:"5.Хоол идэх",th:"5.การรับประทาน"}},
  {k:"dressing",l:{ja:"6.更衣",en:"6.Dressing",mn:"6.Хувцаслах",th:"6.การแต่งกาย"}},
  {k:"turningBed",l:{ja:"7.寝返り",en:"7.Turning in bed",mn:"7.Ортоо эргэх",th:"7.การพลิกตัว"}},
  {k:"walking",l:{ja:"8.歩行",en:"8.Walking",mn:"8.Явах",th:"8.การเดิน"}},
  {k:"climbingStairs",l:{ja:"9.階段",en:"9.Stairs",mn:"9.Шат авирах",th:"9.การขึ้นบันได"}},
  {k:"dyspnea",l:{ja:"10.呼吸困難",en:"10.Dyspnea",mn:"10.Амьсгаадалт",th:"10.หายใจลำบาก"}},
  {k:"orthopnea",l:{ja:"11.起座呼吸",en:"11.Orthopnea",mn:"11.Ортопноэ",th:"11.หายใจลำบากนอนราบ"}},
  {k:"respirInsuff",l:{ja:"12.呼吸不全",en:"12.Resp. insufficiency",mn:"12.Амьсгалын дутагдал",th:"12.ระบบหายใจบกพร่อง"}},
];
const ONSET_OPT={ja:["上肢発症","下肢発症","球麻痺発症","体幹発症","不明"],en:["Upper limb onset","Lower limb onset","Bulbar onset","Trunk onset","Unknown"],mn:["Дээд мөч","Доод мөч","Булбар","Их бие","Тодорхойгүй"],th:["แขนส่วนบน","ขาส่วนล่าง","bulbar","ลำตัว","ไม่ทราบ"]};
function ALSForm({ t, lang, initial, onCancelEdit }) {
  const [step,setStep]=useState(1);const [done,setDone]=useState(false);
  const [savedRec,setSavedRec]=useState(null);
  const items=ALSFRS_ITEMS_FN(lang);
  const init=items.reduce((a,i)=>({...a,[i.k]:""}),{});
  const [f,setF]=useState(initial ? {...initial} : {caseId:"",institution:"",prefecture:"",timePoint:"",age:"",onset:"",
    dailyDose:"",treatmentPeriod:"",serumRow1:"",serumRow2:"",serumRow3:"",serumOther:"",
    fvc:"",svc:"",spo2:"",weight:"",bmi:"",niv:false,gastrostomy:false,tracheostomy:false,wheelChair:false,...init,ae:[],sae:false,notes:""});
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const total=items.reduce((s,i)=>s+(parseFloat(f[i.k])||0),0);
  const valid=items.every(i=>f[i.k]!=="");
  const STEPS=[t.alsStep1,t.alsStep2,t.alsStep3,t.alsStep4];
  const TP={ja:["ベースライン","2週後","4週後","8週後","12週後","24週後","52週後"],en:["Baseline","2 weeks","4 weeks","8 weeks","12 weeks","24 weeks","52 weeks"],mn:["Суурь","2 долоо хоног","4 долоо хоног","8 долоо хоног","12 долоо хоног","24 долоо хоног","52 долоо хоног"],th:["ค่าพื้นฐาน","2 สัปดาห์","4 สัปดาห์","8 สัปดาห์","12 สัปดาห์","24 สัปดาห์","52 สัปดาห์"]};
  const [errors,setErrors]=useState({});const validateAll=()=>{const allErrors={};for(let s=1;s<=4;s++){Object.assign(allErrors,validateForm("als",s,f,t));}return allErrors;};const [submitting,setSubmitting]=useState(false);const handleSubmit=async()=>{const e=validateAll();if(Object.keys(e).length>0){setErrors(e);alert(t.errorTitle+"\n"+Object.entries(e).map(([k,v])=>k+": "+v).join("\n"));return;}setErrors({});setSubmitting(true);try{const rec=await persistCaseRecord("als",{...f,alsfrsTotal:total},initial);setSavedRec(rec);setDone(true);}catch(err){alert(err.message);}finally{setSubmitting(false);}};
  if(done) return <Done t={t} onReset={()=>{setDone(false);setSavedRec(null);setF({ae:[],sae:false,notes:"",...init});setStep(1);if(onCancelEdit)onCancelEdit();}} savedRecord={savedRec} disease="als" lang={lang} />;
  return <div>
    <div style={{ background:"#fff8e1",border:"1px solid #f0c040",padding:"9px 14px",marginBottom:12,fontSize:12.5,color:"#7a5c00" }}>
      {t.noticeALS}<strong>{t.noticeALSStrong}</strong>{t.noticeALSEnd}
    </div>
    <Steps steps={STEPS} current={step} onStep={setStep} />
    <ErrorBanner errors={errors} t={t} />
    {step===1 && <><Blk><Sec icon="🔬" title={t.alsStep1} />
      <Row><div><Lbl t={t} req>{t.prefectureLabel}</Lbl><Sel value={f.prefecture} onChange={e=>set("prefecture",e.target.value)} options={PREFECTURES} ph={t.prefecturePh} /></div><div></div></Row>
      <Row><div><Lbl t={t} req>{t.caseId}</Lbl><Inp value={f.caseId} onChange={e=>set("caseId",e.target.value)} placeholder="ALS-2026-001" /></div>
        <div><Lbl t={t}>{t.age}</Lbl><Inp type="number" value={f.age} onChange={e=>set("age",e.target.value)} placeholder={t.agePh} /></div></Row>
      <Row><div><Lbl t={t}>{t.onsetSite}</Lbl><Sel value={f.onset} onChange={e=>set("onset",e.target.value)} options={ONSET_OPT[lang]} /></div>
        <div><Lbl t={t}>{t.timePoint}</Lbl><Sel value={f.timePoint} onChange={e=>set("timePoint",e.target.value)} options={TP[lang]} /></div></Row>
      <div style={{ display:"flex",gap:14,flexWrap:"wrap",marginTop:6 }}>
        {[[t.niv,"niv"],[t.gastrostomy,"gastrostomy"],[t.tracheostomy,"tracheostomy"],[t.wheelChair,"wheelChair"]].map(([l,k])=>(
          <label key={k} style={{ display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:12.5 }}>
            <input type="checkbox" checked={f[k]||false} onChange={e=>set(k,e.target.checked)} />{l}</label>
        ))}
      </div>
    </Blk>
    <DosageBlock f={f} set={set} t={t} />
    </>}
    {step===2 && <Blk><Sec icon="📊" title={t.alsfrsTitle} sub={t.alfssrsSub} />
      <div style={{ display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10,marginBottom:14 }}>
        {items.map(item=>(
          <div key={item.k} style={{ background:"#f8f5f0",padding:"10px 12px",border:"1px solid #e5dfd5" }}>
            <div style={{ fontSize:12,fontWeight:700,color:"#1b3a52",marginBottom:6 }}>{item.l[lang]||item.l.ja}</div>
            <div style={{ display:"flex",gap:4,flexWrap:"wrap" }}>
              {[4,3,2,1,0].map(v=>(
                <label key={v} style={{ display:"flex",alignItems:"center",gap:3,cursor:"pointer",fontSize:12 }}>
                  <input type="radio" checked={f[item.k]===v.toString()} onChange={()=>set(item.k,v.toString())} />{v}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      {valid && <div style={{ background:"#eaf4f8",border:"1px solid #b3d4e0",padding:"14px 18px" }}>
        <span style={{ fontSize:12,color:"#5a7a8a" }}>{t.alsfrsTotal}：</span>
        <span style={{ fontSize:28,fontWeight:700,color:"#1b3a52",marginLeft:8 }}>{total}</span>
        <span style={{ fontSize:13,color:"#6a7a8a" }}> {t.alsfrsMax}</span>
        <div style={{ background:"#eee",height:8,borderRadius:4,marginTop:8 }}><div style={{ background:"#2c6e8a",height:"100%",borderRadius:4,width:`${total/48*100}%`,transition:"width .4s" }} /></div>
      </div>}
    </Blk>}
    {step===3 && <><Blk><Sec icon="🫁" title={t.alsStep3} />
      <Row cols={3}>
        <div><Lbl t={t} req>{t.fvc}</Lbl><Inp type="number" value={f.fvc} onChange={e=>set("fvc",e.target.value)} placeholder={t.fvcPh} />
          {f.fvc&&<div style={{ fontSize:11.5,marginTop:3,color:parseFloat(f.fvc)<50?"#c0392b":parseFloat(f.fvc)<70?"#e67e22":"#2d6a4f",fontWeight:600 }}>
            {parseFloat(f.fvc)<50?t.fvcSevere:parseFloat(f.fvc)<70?t.fvcWarn:t.fvcNormal}</div>}</div>
        <div><Lbl t={t}>{t.svc}</Lbl><Inp type="number" value={f.svc} onChange={e=>set("svc",e.target.value)} placeholder={t.svcPh} /></div>
        <div><Lbl t={t}>{t.spo2}</Lbl><Inp type="number" value={f.spo2} onChange={e=>set("spo2",e.target.value)} placeholder={t.spo2Ph} /></div>
      </Row>
    </Blk>
    <Blk><Sec icon="🍽️" title={t.weight+" / "+t.bmi} />
      <Row cols={2}><div><Lbl t={t}>{t.weight}</Lbl><Inp type="number" value={f.weight} onChange={e=>set("weight",e.target.value)} /></div>
        <div><Lbl t={t}>{t.bmi}</Lbl><Inp type="number" value={f.bmi} onChange={e=>set("bmi",e.target.value)} /></div></Row>
      <div><Lbl t={t}>{t.progressEvents}</Lbl>
        {[[t.nivDate,"nivDate"],[t.trachDate,"trachDate"],[t.pegDate,"pegDate"]].map(([l,k])=>(
          <div key={k} style={{ display:"flex",alignItems:"center",gap:10,marginTop:8 }}>
            <span style={{ fontSize:12.5,minWidth:90 }}>📍 {l}</span>
            <Inp type="date" value={f[k]||""} onChange={e=>set(k,e.target.value)} />
          </div>
        ))}
      </div>
    </Blk></>}
    {step===4 && <SafetyBlock aeList={ALS_AE[lang]} form={f} setForm={setF} t={t} />}
    <NavBtns step={step} total={STEPS.length} t={t} onBack={()=>setStep(s=>Math.max(1,s-1))} onNext={()=>setStep(s=>Math.min(STEPS.length,s+1))} onSubmit={submitting ? undefined : handleSubmit} />
  </div>;
}

/* ── Cancer ──────────────────────────────────── */
const CANCER_AE={ja:["発熱","倦怠感","悪心","食欲不振","骨髄抑制","肝機能障害","その他"],en:["Fever","Fatigue","Nausea","Appetite loss","Bone marrow suppression","Liver dysfunction","Other"],mn:["Халуурах","Ядаргаа","Дотор муухайрах","Хоол идэхгүй болох","Ясны чөмгийн дарангуйлал","Элэгний эмгэг","Бусад"],th:["มีไข้","อ่อนเพลีย","คลื่นไส้","เบื่ออาหาร","ไขกระดูกถูกกด","ตับบกพร่อง","อื่นๆ"]};
const RECIST_OPT={ja:["CR（完全奏効）","PR（部分奏効）","SD（安定）","PD（進行）","評価不能"],en:["CR (Complete Response)","PR (Partial Response)","SD (Stable Disease)","PD (Progressive Disease)","Not evaluable"],mn:["CR (Бүрэн хариу)","PR (Хэсэгчилсэн хариу)","SD (Тогтвортой)","PD (Дэвшилтгэй)","Үнэлгээ боломжгүй"],th:["CR (ตอบสนองสมบูรณ์)","PR (ตอบสนองบางส่วน)","SD (โรคคงที่)","PD (โรคลุกลาม)","ไม่สามารถประเมินได้"]};
const CANCER_TYPES_FN=(lang)=>({ja:["肺癌","胃癌","大腸癌","乳癌","膵癌","肝癌","その他"],en:["Lung","Gastric","Colorectal","Breast","Pancreatic","Liver","Other"],mn:["Уушигны хорт хавдар","Ходоодны","Гэдэсний","Хөхний","Нойр булчирхайн","Элэгний","Бусад"],th:["มะเร็งปอด","มะเร็งกระเพาะ","มะเร็งลำไส้","มะเร็งเต้านม","มะเร็งตับอ่อน","มะเร็งตับ","อื่นๆ"]}[lang]);
function CancerForm({ t, lang, initial, onCancelEdit }) {
  const [step,setStep]=useState(1);const [done,setDone]=useState(false);
  const [savedRec,setSavedRec]=useState(null);
  const [f,setF]=useState(initial ? {...initial} : {caseId:"",institution:"",prefecture:"",timePoint:"",cancerType:"",stage:"",line:"",ps:"",
    dailyDose:"",treatmentPeriod:"",serumRow1:"",serumRow2:"",serumRow3:"",serumOther:"",
    imageType:"",imageDate:"",sumB:"",sumC:"",rate:"",recist:"",cea:"",ca199:"",pfs:"",os:"",ae:[],sae:false,notes:""});
  const set=(k,v)=>setF(p=>{
    const n={...p,[k]:v};
    if(k==="sumB"||k==="sumC"){const b=parseFloat(k==="sumB"?v:n.sumB),c=parseFloat(k==="sumC"?v:n.sumC);
      if(!isNaN(b)&&!isNaN(c)&&b>0){const r=(((c-b)/b)*100).toFixed(1);n.rate=r;n.recist=RECIST_OPT[lang][parseFloat(r)<=-100?0:parseFloat(r)<=-30?1:parseFloat(r)<20?2:3];}}
    return n;
  });
  const STEPS=[t.cancerStep1,t.cancerStep2,t.cancerStep3,t.cancerStep4];
  const STAGE_OPT={ja:["Stage I","Stage II","Stage III","Stage IV","再発","転移あり"],en:["Stage I","Stage II","Stage III","Stage IV","Recurrence","Metastasis"],mn:["I үе шат","II үе шат","III үе шат","IV үе шат","Дахилт","Үржилт"],th:["Stage I","Stage II","Stage III","Stage IV","กลับมาเป็นซ้ำ","มีการแพร่กระจาย"]};
  const LINE_OPT={ja:["初回治療","2次治療","3次治療以降","標準治療終了後"],en:["1st line","2nd line","3rd line+","After standard treatment"],mn:["1-р эгнээ","2-р эгнээ","3-р эгнээ","Стандарт эмчилгээний дараа"],th:["แนวที่ 1","แนวที่ 2","แนวที่ 3+","หลังการรักษามาตรฐาน"]};
  const IMG_OPT={ja:["CT","MRI","PET-CT","超音波"],en:["CT","MRI","PET-CT","Ultrasound"],mn:["КТ","МРТ","ПЭТ-КТ","Хэт авиан"],th:["CT","MRI","PET-CT","อัลตราซาวนด์"]};
  const TP={ja:["ベースライン","8週後","12週後","24週後"],en:["Baseline","8 weeks","12 weeks","24 weeks"],mn:["Суурь","8 долоо хоног","12 долоо хоног","24 долоо хоног"],th:["ค่าพื้นฐาน","8 สัปดาห์","12 สัปดาห์","24 สัปดาห์"]};
  const [errors,setErrors]=useState({});const validateAll=()=>{const allErrors={};for(let s=1;s<=4;s++){Object.assign(allErrors,validateForm("cancer",s,f,t));}return allErrors;};const [submitting,setSubmitting]=useState(false);const handleSubmit=async()=>{const e=validateAll();if(Object.keys(e).length>0){setErrors(e);alert(t.errorTitle+"\n"+Object.entries(e).map(([k,v])=>k+": "+v).join("\n"));return;}setErrors({});setSubmitting(true);try{const rec=await persistCaseRecord("cancer",f,initial);setSavedRec(rec);setDone(true);}catch(err){alert(err.message);}finally{setSubmitting(false);}};
  if(done) return <Done t={t} onReset={()=>{setDone(false);setSavedRec(null);setF({ae:[],sae:false,notes:""});setStep(1);if(onCancelEdit)onCancelEdit();}} savedRecord={savedRec} disease="cancer" lang={lang} />;
  return <div>
    <Steps steps={STEPS} current={step} onStep={setStep} />
    <ErrorBanner errors={errors} t={t} />
    {step===1 && <><Blk><Sec icon="📋" title={t.cancerStep1} />
      <Row><div><Lbl t={t} req>{t.prefectureLabel}</Lbl><Sel value={f.prefecture} onChange={e=>set("prefecture",e.target.value)} options={PREFECTURES} ph={t.prefecturePh} /></div><div></div></Row>
      <Row><div><Lbl t={t} req>{t.caseId}</Lbl><Inp value={f.caseId} onChange={e=>set("caseId",e.target.value)} placeholder="CA-2026-001" /></div>
        <div><Lbl t={t}>{t.institution}</Lbl><Inp value={f.institution} onChange={e=>set("institution",e.target.value)} placeholder={t.institutionPh} /></div></Row>
    </Blk>
    <Blk><Sec icon="🎯" title={t.cancerStep1} />
      <Row cols={3}><div><Lbl t={t} req>{t.cancerType}</Lbl><Sel value={f.cancerType} onChange={e=>set("cancerType",e.target.value)} options={CANCER_TYPES_FN(lang)} /></div>
        <div><Lbl t={t} req>{t.stage}</Lbl><Sel value={f.stage} onChange={e=>set("stage",e.target.value)} options={STAGE_OPT[lang]} /></div>
        <div><Lbl t={t} req>{t.treatmentLine}</Lbl><Sel value={f.line} onChange={e=>set("line",e.target.value)} options={LINE_OPT[lang]} /></div></Row>
      <Row><div><Lbl t={t}>{t.timePoint}</Lbl><Sel value={f.timePoint} onChange={e=>set("timePoint",e.target.value)} options={TP[lang]} /></div><div></div></Row>
    </Blk>
    <DosageBlock f={f} set={set} t={t} />
    </>}
    {step===2 && <Blk><Sec icon="🔬" title={t.cancerStep2} />
      <Row><div><Lbl t={t}>{t.imageType}</Lbl><Sel value={f.imageType} onChange={e=>set("imageType",e.target.value)} options={IMG_OPT[lang]} /></div>
        <div><Lbl t={t}>{t.imageDate}</Lbl><Inp type="date" value={f.imageDate} onChange={e=>set("imageDate",e.target.value)} /></div></Row>
      <Row><div><Lbl t={t} req>{t.sumBaseline}</Lbl><Inp type="number" value={f.sumB} onChange={e=>set("sumB",e.target.value)} /></div>
        <div><Lbl t={t} req>{t.sumCurrent}</Lbl><Inp type="number" value={f.sumC} onChange={e=>set("sumC",e.target.value)} /></div></Row>
      {f.rate&&<div style={{ background:"#eaf4f8",border:"1px solid #b3d4e0",padding:"12px 16px",marginBottom:12,display:"flex",alignItems:"center",gap:20 }}>
        <div><span style={{ fontSize:11,color:"#5a7a8a" }}>{t.changeRate}：</span>
          <div style={{ fontSize:24,fontWeight:700,color:parseFloat(f.rate)<0?"#2d6a4f":"#c0392b" }}>{parseFloat(f.rate)>0?"+":""}{f.rate}%</div></div>
        <div><span style={{ fontSize:11,color:"#5a7a8a" }}>{t.autoCalc}：</span>
          <div style={{ marginTop:3,fontSize:13,padding:"3px 12px",borderRadius:20,background:"#e8f5e9",color:"#1b5e20",fontWeight:700,display:"inline-block" }}>{f.recist}</div></div>
      </div>}
      <div><Lbl t={t} req>{t.recistFinal}</Lbl><Sel value={f.recist} onChange={e=>set("recist",e.target.value)} options={RECIST_OPT[lang]} /></div>
    </Blk>}
    {step===3 && <><Blk><Sec icon="🧪" title={t.cancerStep3} />
      <Row cols={2}><div><Lbl t={t}>{t.cea}</Lbl><Inp type="number" value={f.cea} onChange={e=>set("cea",e.target.value)} /></div>
        <div><Lbl t={t}>{t.ca199}</Lbl><Inp type="number" value={f.ca199} onChange={e=>set("ca199",e.target.value)} /></div></Row>
    </Blk>
    <Blk><Sec icon="📈" title="PFS / OS" />
      <Row><div><Lbl t={t}>{t.pfsWeeks}</Lbl><Inp type="number" value={f.pfs} onChange={e=>set("pfs",e.target.value)} placeholder={t.pfsWPh} /></div>
        <div><Lbl t={t}>{t.osStatus}</Lbl><Sel value={f.os} onChange={e=>set("os",e.target.value)} options={[t.alive,t.dead,t.lost]} /></div></Row>
    </Blk></>}
    {step===4 && <SafetyBlock aeList={CANCER_AE[lang]} form={f} setForm={setF} t={t} />}
    <NavBtns step={step} total={STEPS.length} t={t} onBack={()=>setStep(s=>Math.max(1,s-1))} onNext={()=>setStep(s=>Math.min(STEPS.length,s+1))} onSubmit={submitting ? undefined : handleSubmit} />
  </div>;
}

/* ══════════════════════════════════════════════
   StrokeForm (脳梗塞)
══════════════════════════════════════════════ */
const STROKE_AE={ja:["頭痛","めまい","出血","発熱","感染症","痙攣","その他"],en:["Headache","Dizziness","Bleeding","Fever","Infection","Seizure","Other"],mn:["Толгой өвдөх","Толгой эргэх","Цусархаг","Халуурах","Халдвар","Татвалзах","Бусад"],th:["ปวดหัว","วิงเวียน","เลือดออก","มีไข้","ติดเชื้อ","ชัก","อื่นๆ"]};
function StrokeForm({ t, lang, initial, onCancelEdit }) {
  const [step,setStep]=useState(1);const [done,setDone]=useState(false);
  const [savedRec,setSavedRec]=useState(null);
  const [f,setF]=useState(initial ? {...initial} : {caseId:"",institution:"",prefecture:"",timePoint:"",age:"",sex:"",onsetDate:"",strokeType:"",onsetTime:"",tpa:false,
    dailyDose:"",treatmentPeriod:"",serumRow1:"",serumRow2:"",serumRow3:"",serumOther:"",
    nihss:"",mrs:"",barthel:"",fim:"",recurrence:false,hemorrhage:false,ae:[],sae:false,notes:""});
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const STEPS=[t.strokeStep1,t.strokeStep2,t.strokeStep3,t.strokeStep4];
  const TYPE_OPT=[t.strokeLacunar,t.strokeAthero,t.strokeCardio,t.strokeOther];
  const TP={ja:["ベースライン","4週後","12週後","24週後","52週後"],en:["Baseline","4 weeks","12 weeks","24 weeks","52 weeks"],mn:["Суурь","4 долоо хоног","12 долоо хоног","24 долоо хоног","52 долоо хоног"],th:["ค่าพื้นฐาน","4 สัปดาห์","12 สัปดาห์","24 สัปดาห์","52 สัปดาห์"]};
  const MRS_OPT=[t.mrsOpt0,t.mrsOpt1,t.mrsOpt2,t.mrsOpt3,t.mrsOpt4,t.mrsOpt5,t.mrsOpt6];
  const [errors,setErrors]=useState({});const validateAll=()=>{const allErrors={};for(let s=1;s<=4;s++){Object.assign(allErrors,validateForm("stroke",s,f,t));}return allErrors;};const [submitting,setSubmitting]=useState(false);const handleSubmit=async()=>{const e=validateAll();if(Object.keys(e).length>0){setErrors(e);alert(t.errorTitle+"\n"+Object.entries(e).map(([k,v])=>k+": "+v).join("\n"));return;}setErrors({});setSubmitting(true);try{const rec=await persistCaseRecord("stroke",f,initial);setSavedRec(rec);setDone(true);}catch(err){alert(err.message);}finally{setSubmitting(false);}};
  if(done) return <Done t={t} onReset={()=>{setDone(false);setSavedRec(null);setF({ae:[],sae:false,notes:"",recurrence:false,hemorrhage:false,tpa:false});setStep(1);if(onCancelEdit)onCancelEdit();}} savedRecord={savedRec} disease="stroke" lang={lang} />;
  return <div>
    <Steps steps={STEPS} current={step} onStep={setStep} />
    <ErrorBanner errors={errors} t={t} />
    {step===1 && <><Blk><Sec icon="📋" title={t.strokeStep1} />
      <Row><div><Lbl t={t} req>{t.prefectureLabel}</Lbl><Sel value={f.prefecture} onChange={e=>set("prefecture",e.target.value)} options={PREFECTURES} ph={t.prefecturePh} /></div><div></div></Row>
      <Row><div><Lbl t={t} req>{t.caseId}</Lbl><Inp value={f.caseId} onChange={e=>set("caseId",e.target.value)} placeholder="STR-2026-001" /></div>
        <div><Lbl t={t}>{t.institution}</Lbl><Inp value={f.institution} onChange={e=>set("institution",e.target.value)} placeholder={t.institutionPh} /></div></Row>
      <Row cols={3}><div><Lbl t={t}>{t.age}</Lbl><Inp type="number" value={f.age} onChange={e=>set("age",e.target.value)} placeholder={t.agePh} /></div>
        <div><Lbl t={t}>{t.sex}</Lbl><Sel value={f.sex} onChange={e=>set("sex",e.target.value)} options={[t.male,t.female,t.other]} /></div>
        <div><Lbl t={t}>{t.timePoint}</Lbl><Sel value={f.timePoint} onChange={e=>set("timePoint",e.target.value)} options={TP[lang]} /></div></Row>
      <Row><div><Lbl t={t}>{t.onsetDate}</Lbl><Inp type="date" value={f.onsetDate} onChange={e=>set("onsetDate",e.target.value)} /></div>
        <div><Lbl t={t} req>{t.strokeType}</Lbl><Sel value={f.strokeType} onChange={e=>set("strokeType",e.target.value)} options={TYPE_OPT} /></div></Row>
      <div><Lbl t={t}>{t.onsetTime}</Lbl><Inp value={f.onsetTime} onChange={e=>set("onsetTime",e.target.value)} placeholder="例：4.5h" /></div>
      <label style={{ display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13,marginTop:8 }}>
        <input type="checkbox" checked={f.tpa} onChange={e=>set("tpa",e.target.checked)} /><strong>{t.tpaUsed}</strong></label>
    </Blk>
    <DosageBlock f={f} set={set} t={t} />
    </>}
    {step===2 && <><Blk><Sec icon="🧠" title={t.nihssTitle} sub={t.nihssSub} />
      <Row><div><Lbl t={t} req>{t.nihssScore}</Lbl><Inp type="number" value={f.nihss} onChange={e=>set("nihss",e.target.value)} placeholder={t.nihssPh} />
          <div style={{ background:"#eee",height:7,borderRadius:4,marginTop:5 }}><div style={{ background:"#c0392b",height:"100%",borderRadius:4,width:`${Math.min(100,(parseFloat(f.nihss)||0)/42*100)}%` }} /></div></div>
        <div></div></Row>
    </Blk>
    <Blk><Sec icon="⚖️" title={t.mrsTitle} sub={t.mrsSub} />
      <Lbl t={t} req>{t.mrsScore}</Lbl>
      <Sel value={f.mrs} onChange={e=>set("mrs",e.target.value)} options={MRS_OPT} />
    </Blk></>}
    {step===3 && <Blk><Sec icon="🏠" title={t.barthelTitle} sub={t.barthelSub} />
      <Row><div><Lbl t={t} req>{t.barthelScore}</Lbl><Inp type="number" value={f.barthel} onChange={e=>set("barthel",e.target.value)} placeholder={t.barthelPh} />
          <div style={{ background:"#eee",height:7,borderRadius:4,marginTop:5 }}><div style={{ background:"#2d6a4f",height:"100%",borderRadius:4,width:`${Math.min(100,(parseFloat(f.barthel)||0)/100*100)}%` }} /></div></div>
        <div><Lbl t={t}>{t.fimScore}</Lbl><Inp type="number" value={f.fim} onChange={e=>set("fim",e.target.value)} placeholder={t.fimPh} /></div></Row>
      <div style={{ display:"flex",gap:14,flexWrap:"wrap",marginTop:10 }}>
        <label style={{ display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:12.5 }}>
          <input type="checkbox" checked={f.recurrence} onChange={e=>set("recurrence",e.target.checked)} />{t.recurrence}</label>
        <label style={{ display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:12.5 }}>
          <input type="checkbox" checked={f.hemorrhage} onChange={e=>set("hemorrhage",e.target.checked)} />{t.hemorrhage}</label>
      </div>
    </Blk>}
    {step===4 && <SafetyBlock aeList={STROKE_AE[lang]} form={f} setForm={setF} t={t} />}
    <NavBtns step={step} total={STEPS.length} t={t} onBack={()=>setStep(s=>Math.max(1,s-1))} onNext={()=>setStep(s=>Math.min(STEPS.length,s+1))} onSubmit={submitting ? undefined : handleSubmit} />
  </div>;
}

/* ══════════════════════════════════════════════
   SCIForm (脊髄損傷)
══════════════════════════════════════════════ */
const SCI_AE={ja:["褥瘡","尿路感染","痙縮悪化","自律神経反射異常","深部静脈血栓","その他"],en:["Pressure ulcer","UTI","Spasticity worsening","Autonomic dysreflexia","DVT","Other"],mn:["Хэвтрийн шарх","Шээсний замын халдвар","Чангарал муудах","Вегетатив рефлексийн эмгэг","Гүн судасны бөглөрөл","Бусад"],th:["แผลกดทับ","ติดเชื้อทางเดินปัสสาวะ","ความเกร็งแย่ลง","Autonomic dysreflexia","DVT","อื่นๆ"]};
function SCIForm({ t, lang, initial, onCancelEdit }) {
  const [step,setStep]=useState(1);const [done,setDone]=useState(false);
  const [savedRec,setSavedRec]=useState(null);
  const [f,setF]=useState(initial ? {...initial} : {caseId:"",institution:"",prefecture:"",timePoint:"",age:"",sex:"",injuryDate:"",injuryLevel:"",aisGrade:"",
    dailyDose:"",treatmentPeriod:"",serumRow1:"",serumRow2:"",serumRow3:"",serumOther:"",
    motorUE:"",motorLE:"",sensoryPP:"",sensoryLT:"",scim:"",ashworth:"",autonomic:false,ulcer:false,ae:[],sae:false,notes:""});
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const STEPS=[t.sciStep1,t.sciStep2,t.sciStep3,t.sciStep4];
  const LEVEL_OPT=[t.levelCervical,t.levelThoracic,t.levelLumbar,t.levelSacral];
  const AIS_OPT=[t.aisA,t.aisB,t.aisC,t.aisD,t.aisE];
  const ASH_OPT=[t.ashworth0,t.ashworth1,t.ashworth1p,t.ashworth2,t.ashworth3,t.ashworth4];
  const TP={ja:["ベースライン","4週後","12週後","24週後","52週後"],en:["Baseline","4 weeks","12 weeks","24 weeks","52 weeks"],mn:["Суурь","4 долоо хоног","12 долоо хоног","24 долоо хоног","52 долоо хоног"],th:["ค่าพื้นฐาน","4 สัปดาห์","12 สัปดาห์","24 สัปดาห์","52 สัปดาห์"]};
  const [errors,setErrors]=useState({});const validateAll=()=>{const allErrors={};for(let s=1;s<=4;s++){Object.assign(allErrors,validateForm("sci",s,f,t));}return allErrors;};const [submitting,setSubmitting]=useState(false);const handleSubmit=async()=>{const e=validateAll();if(Object.keys(e).length>0){setErrors(e);alert(t.errorTitle+"\n"+Object.entries(e).map(([k,v])=>k+": "+v).join("\n"));return;}setErrors({});setSubmitting(true);try{const rec=await persistCaseRecord("sci",f,initial);setSavedRec(rec);setDone(true);}catch(err){alert(err.message);}finally{setSubmitting(false);}};
  if(done) return <Done t={t} onReset={()=>{setDone(false);setSavedRec(null);setF({ae:[],sae:false,notes:"",autonomic:false,ulcer:false});setStep(1);if(onCancelEdit)onCancelEdit();}} savedRecord={savedRec} disease="sci" lang={lang} />;
  return <div>
    <Steps steps={STEPS} current={step} onStep={setStep} />
    <ErrorBanner errors={errors} t={t} />
    {step===1 && <><Blk><Sec icon="📋" title={t.sciStep1} />
      <Row><div><Lbl t={t} req>{t.prefectureLabel}</Lbl><Sel value={f.prefecture} onChange={e=>set("prefecture",e.target.value)} options={PREFECTURES} ph={t.prefecturePh} /></div><div></div></Row>
      <Row><div><Lbl t={t} req>{t.caseId}</Lbl><Inp value={f.caseId} onChange={e=>set("caseId",e.target.value)} placeholder="SCI-2026-001" /></div>
        <div><Lbl t={t}>{t.institution}</Lbl><Inp value={f.institution} onChange={e=>set("institution",e.target.value)} placeholder={t.institutionPh} /></div></Row>
      <Row cols={3}><div><Lbl t={t}>{t.age}</Lbl><Inp type="number" value={f.age} onChange={e=>set("age",e.target.value)} placeholder={t.agePh} /></div>
        <div><Lbl t={t}>{t.sex}</Lbl><Sel value={f.sex} onChange={e=>set("sex",e.target.value)} options={[t.male,t.female,t.other]} /></div>
        <div><Lbl t={t}>{t.timePoint}</Lbl><Sel value={f.timePoint} onChange={e=>set("timePoint",e.target.value)} options={TP[lang]} /></div></Row>
      <Row><div><Lbl t={t}>{t.injuryDate}</Lbl><Inp type="date" value={f.injuryDate} onChange={e=>set("injuryDate",e.target.value)} /></div>
        <div><Lbl t={t} req>{t.injuryLevel}</Lbl><Sel value={f.injuryLevel} onChange={e=>set("injuryLevel",e.target.value)} options={LEVEL_OPT} /></div></Row>
      <div><Lbl t={t} req>{t.aisGrade}</Lbl><Sel value={f.aisGrade} onChange={e=>set("aisGrade",e.target.value)} options={AIS_OPT} /></div>
    </Blk>
    <DosageBlock f={f} set={set} t={t} />
    </>}
    {step===2 && <Blk><Sec icon="💪" title={t.isncsciTitle} sub={t.isncsciSub} />
      <Row><div><Lbl t={t} req>{t.motorUE}</Lbl><Inp type="number" value={f.motorUE} onChange={e=>set("motorUE",e.target.value)} placeholder={t.motorUEPh} />
          <div style={{ background:"#eee",height:7,borderRadius:4,marginTop:5 }}><div style={{ background:"#2c6e8a",height:"100%",borderRadius:4,width:`${Math.min(100,(parseFloat(f.motorUE)||0)/50*100)}%` }} /></div></div>
        <div><Lbl t={t} req>{t.motorLE}</Lbl><Inp type="number" value={f.motorLE} onChange={e=>set("motorLE",e.target.value)} placeholder={t.motorLEPh} />
          <div style={{ background:"#eee",height:7,borderRadius:4,marginTop:5 }}><div style={{ background:"#2c6e8a",height:"100%",borderRadius:4,width:`${Math.min(100,(parseFloat(f.motorLE)||0)/50*100)}%` }} /></div></div></Row>
      <Row><div><Lbl t={t}>{t.sensoryPP}</Lbl><Inp type="number" value={f.sensoryPP} onChange={e=>set("sensoryPP",e.target.value)} placeholder={t.sensoryPPPh} /></div>
        <div><Lbl t={t}>{t.sensoryLT}</Lbl><Inp type="number" value={f.sensoryLT} onChange={e=>set("sensoryLT",e.target.value)} placeholder={t.sensoryLTPh} /></div></Row>
    </Blk>}
    {step===3 && <><Blk><Sec icon="🏠" title={t.scimTitle} sub={t.scimSub} />
      <Row><div><Lbl t={t} req>{t.scimScore}</Lbl><Inp type="number" value={f.scim} onChange={e=>set("scim",e.target.value)} placeholder={t.scimPh} />
          <div style={{ background:"#eee",height:7,borderRadius:4,marginTop:5 }}><div style={{ background:"#2d6a4f",height:"100%",borderRadius:4,width:`${Math.min(100,(parseFloat(f.scim)||0)/100*100)}%` }} /></div></div>
        <div></div></Row>
    </Blk>
    <Blk><Sec icon="🧪" title={t.ashworthTitle} />
      <Lbl t={t}>{t.ashworthScore}</Lbl>
      <Sel value={f.ashworth} onChange={e=>set("ashworth",e.target.value)} options={ASH_OPT} />
      <div style={{ display:"flex",gap:14,flexWrap:"wrap",marginTop:10 }}>
        <label style={{ display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:12.5 }}>
          <input type="checkbox" checked={f.autonomic} onChange={e=>set("autonomic",e.target.checked)} />{t.autonomicDys}</label>
        <label style={{ display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:12.5 }}>
          <input type="checkbox" checked={f.ulcer} onChange={e=>set("ulcer",e.target.checked)} />{t.pressureUlcer}</label>
      </div>
    </Blk></>}
    {step===4 && <SafetyBlock aeList={SCI_AE[lang]} form={f} setForm={setF} t={t} />}
    <NavBtns step={step} total={STEPS.length} t={t} onBack={()=>setStep(s=>Math.max(1,s-1))} onNext={()=>setStep(s=>Math.min(STEPS.length,s+1))} onSubmit={submitting ? undefined : handleSubmit} />
  </div>;
}

/* ══════════════════════════════════════════════
   DMForm (糖尿病)
══════════════════════════════════════════════ */
const DM_AE={ja:["低血糖","高血糖","感染症","皮膚反応","胃腸障害","その他"],en:["Hypoglycemia","Hyperglycemia","Infection","Skin reaction","GI disorder","Other"],mn:["Бага сахартлалт","Их сахартлалт","Халдвар","Арьсны урвал","Хоол боловсруулалт","Бусад"],th:["น้ำตาลต่ำ","น้ำตาลสูง","ติดเชื้อ","ผิวหนัง","ระบบทางเดินอาหาร","อื่นๆ"]};
function DMForm({ t, lang, initial, onCancelEdit }) {
  const [step,setStep]=useState(1);const [done,setDone]=useState(false);
  const [savedRec,setSavedRec]=useState(null);
  const [f,setF]=useState(initial ? {...initial,complications:initial.complications||[]} : {caseId:"",institution:"",prefecture:"",timePoint:"",age:"",sex:"",dmType:"",dmDuration:"",bmi:"",
    complications:[],
    dailyDose:"",treatmentPeriod:"",serumRow1:"",serumRow2:"",serumRow3:"",serumOther:"",
    hba1c:"",fpg:"",ppg2h:"",tir:"",egfr:"",urineAlb:"",neuroVAS:"",retinoStage:"",hypoEvents:"",ae:[],sae:false,notes:""});
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const toggleComp=(c)=>setF(p=>({...p,complications:p.complications.includes(c)?p.complications.filter(x=>x!==c):[...p.complications,c]}));
  const STEPS=[t.dmStep1,t.dmStep2,t.dmStep3,t.dmStep4];
  const DM_TYPE=[t.dm1,t.dm2,t.dmGes,t.dmOther];
  const COMP_LIST=[t.compNephro,t.compNeuro,t.compRetino,t.compCardio,t.compNone];
  const RETINO_OPT=[t.retino0,t.retino1,t.retino2,t.retino3];
  const TP={ja:["ベースライン","4週後","12週後","24週後","52週後"],en:["Baseline","4 weeks","12 weeks","24 weeks","52 weeks"],mn:["Суурь","4 долоо хоног","12 долоо хоног","24 долоо хоног","52 долоо хоног"],th:["ค่าพื้นฐาน","4 สัปดาห์","12 สัปดาห์","24 สัปดาห์","52 สัปดาห์"]};
  const [errors,setErrors]=useState({});const validateAll=()=>{const allErrors={};for(let s=1;s<=4;s++){Object.assign(allErrors,validateForm("dm",s,f,t));}return allErrors;};const [submitting,setSubmitting]=useState(false);const handleSubmit=async()=>{const e=validateAll();if(Object.keys(e).length>0){setErrors(e);alert(t.errorTitle+"\n"+Object.entries(e).map(([k,v])=>k+": "+v).join("\n"));return;}setErrors({});setSubmitting(true);try{const rec=await persistCaseRecord("dm",f,initial);setSavedRec(rec);setDone(true);}catch(err){alert(err.message);}finally{setSubmitting(false);}};
  if(done) return <Done t={t} onReset={()=>{setDone(false);setSavedRec(null);setF({ae:[],sae:false,notes:"",complications:[]});setStep(1);if(onCancelEdit)onCancelEdit();}} savedRecord={savedRec} disease="dm" lang={lang} />;
  return <div>
    <Steps steps={STEPS} current={step} onStep={setStep} />
    <ErrorBanner errors={errors} t={t} />
    {step===1 && <><Blk><Sec icon="📋" title={t.dmStep1} />
      <Row><div><Lbl t={t} req>{t.prefectureLabel}</Lbl><Sel value={f.prefecture} onChange={e=>set("prefecture",e.target.value)} options={PREFECTURES} ph={t.prefecturePh} /></div><div></div></Row>
      <Row><div><Lbl t={t} req>{t.caseId}</Lbl><Inp value={f.caseId} onChange={e=>set("caseId",e.target.value)} placeholder="DM-2026-001" /></div>
        <div><Lbl t={t}>{t.institution}</Lbl><Inp value={f.institution} onChange={e=>set("institution",e.target.value)} placeholder={t.institutionPh} /></div></Row>
      <Row cols={3}><div><Lbl t={t}>{t.age}</Lbl><Inp type="number" value={f.age} onChange={e=>set("age",e.target.value)} placeholder={t.agePh} /></div>
        <div><Lbl t={t}>{t.sex}</Lbl><Sel value={f.sex} onChange={e=>set("sex",e.target.value)} options={[t.male,t.female,t.other]} /></div>
        <div><Lbl t={t}>{t.timePoint}</Lbl><Sel value={f.timePoint} onChange={e=>set("timePoint",e.target.value)} options={TP[lang]} /></div></Row>
      <Row cols={3}><div><Lbl t={t} req>{t.dmType}</Lbl><Sel value={f.dmType} onChange={e=>set("dmType",e.target.value)} options={DM_TYPE} /></div>
        <div><Lbl t={t}>{t.dmDuration}</Lbl><Inp type="number" value={f.dmDuration} onChange={e=>set("dmDuration",e.target.value)} placeholder={t.dmDurationPh} /></div>
        <div><Lbl t={t}>{t.bmi}</Lbl><Inp type="number" value={f.bmi} onChange={e=>set("bmi",e.target.value)} placeholder={t.bmiPh} /></div></Row>
      <Lbl t={t}>{t.complications}</Lbl>
      <div style={{ display:"flex",gap:10,flexWrap:"wrap",marginTop:6 }}>
        {COMP_LIST.map(c=>(
          <label key={c} style={{ display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:12.5,padding:"5px 10px",border:"1px solid #d8d2c8",background:f.complications.includes(c)?"#eaf4f8":"white" }}>
            <input type="checkbox" checked={f.complications.includes(c)} onChange={()=>toggleComp(c)} />{c}</label>
        ))}
      </div>
    </Blk>
    <DosageBlock f={f} set={set} t={t} />
    </>}
    {step===2 && <Blk><Sec icon="🩸" title={t.hba1cTitle} sub={t.hba1cSub} />
      <Row><div><Lbl t={t} req>{t.hba1c}</Lbl><Inp type="number" value={f.hba1c} onChange={e=>set("hba1c",e.target.value)} placeholder={t.hba1cPh} /></div>
        <div><Lbl t={t}>{t.fpg}</Lbl><Inp type="number" value={f.fpg} onChange={e=>set("fpg",e.target.value)} placeholder={t.fpgPh} /></div></Row>
      <Row><div><Lbl t={t}>{t.ppg2h}</Lbl><Inp type="number" value={f.ppg2h} onChange={e=>set("ppg2h",e.target.value)} placeholder={t.ppg2hPh} /></div>
        <div><Lbl t={t}>{t.timeInRange}</Lbl><Inp type="number" value={f.tir} onChange={e=>set("tir",e.target.value)} placeholder={t.tirPh} /></div></Row>
    </Blk>}
    {step===3 && <><Blk><Sec icon="🫘" title={t.nephroTitle} />
      <Row><div><Lbl t={t}>{t.egfr}</Lbl><Inp type="number" value={f.egfr} onChange={e=>set("egfr",e.target.value)} placeholder={t.egfrPh} /></div>
        <div><Lbl t={t}>{t.urineAlb}</Lbl><Inp type="number" value={f.urineAlb} onChange={e=>set("urineAlb",e.target.value)} placeholder={t.urineAlbPh} /></div></Row>
    </Blk>
    <Blk><Sec icon="👁️" title={t.retinoStage} />
      <Row><div><Lbl t={t}>{t.retinoStage}</Lbl><Sel value={f.retinoStage} onChange={e=>set("retinoStage",e.target.value)} options={RETINO_OPT} /></div>
        <div><Lbl t={t}>{t.neuroVAS}</Lbl><Inp type="number" value={f.neuroVAS} onChange={e=>set("neuroVAS",e.target.value)} placeholder={t.neuroVASPh} /></div></Row>
      <div><Lbl t={t}>{t.hypoEvents}</Lbl><Inp type="number" value={f.hypoEvents} onChange={e=>set("hypoEvents",e.target.value)} placeholder={t.hypoPh} /></div>
    </Blk></>}
    {step===4 && <SafetyBlock aeList={DM_AE[lang]} form={f} setForm={setF} t={t} />}
    <NavBtns step={step} total={STEPS.length} t={t} onBack={()=>setStep(s=>Math.max(1,s-1))} onNext={()=>setStep(s=>Math.min(STEPS.length,s+1))} onSubmit={submitting ? undefined : handleSubmit} />
  </div>;
}

/* ══════════════════════════════════════════════
   OtherForm (その他疾患・汎用)
══════════════════════════════════════════════ */
const OTHER_AE={ja:["発熱","倦怠感","頭痛","食欲不振","アレルギー反応","感染症","その他"],en:["Fever","Fatigue","Headache","Appetite loss","Allergic reaction","Infection","Other"],mn:["Халуурах","Ядаргаа","Толгой өвдөх","Хоолны дуршил буурах","Харшил","Халдвар","Бусад"],th:["มีไข้","อ่อนเพลีย","ปวดหัว","เบื่ออาหาร","แพ้","ติดเชื้อ","อื่นๆ"]};
function OtherForm({ t, lang, initial, onCancelEdit }) {
  const [step,setStep]=useState(1);const [done,setDone]=useState(false);
  const [savedRec,setSavedRec]=useState(null);
  const [f,setF]=useState(initial ? {...initial} : {caseId:"",institution:"",prefecture:"",timePoint:"",age:"",sex:"",diseaseName:"",icd10:"",diagDate:"",
    dailyDose:"",treatmentPeriod:"",serumRow1:"",serumRow2:"",serumRow3:"",serumOther:"",
    eval1Name:"",eval1Val:"",eval1Unit:"",eval2Name:"",eval2Val:"",eval2Unit:"",eval3Name:"",eval3Val:"",eval3Unit:"",evalSupp:"",
    qolDesc:"",ae:[],sae:false,notes:""});
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const STEPS=[t.otherStep1,t.otherStep2,t.otherStep3,t.otherStep4];
  const TP={ja:["ベースライン","4週後","12週後","24週後","52週後"],en:["Baseline","4 weeks","12 weeks","24 weeks","52 weeks"],mn:["Суурь","4 долоо хоног","12 долоо хоног","24 долоо хоног","52 долоо хоног"],th:["ค่าพื้นฐาน","4 สัปดาห์","12 สัปดาห์","24 สัปดาห์","52 สัปดาห์"]};
  const [errors,setErrors]=useState({});const validateAll=()=>{const allErrors={};for(let s=1;s<=4;s++){Object.assign(allErrors,validateForm("other",s,f,t));}return allErrors;};const [submitting,setSubmitting]=useState(false);const handleSubmit=async()=>{const e=validateAll();if(Object.keys(e).length>0){setErrors(e);alert(t.errorTitle+"\n"+Object.entries(e).map(([k,v])=>k+": "+v).join("\n"));return;}setErrors({});setSubmitting(true);try{const rec=await persistCaseRecord("other",f,initial);setSavedRec(rec);setDone(true);}catch(err){alert(err.message);}finally{setSubmitting(false);}};
  if(done) return <Done t={t} onReset={()=>{setDone(false);setSavedRec(null);setF({ae:[],sae:false,notes:""});setStep(1);if(onCancelEdit)onCancelEdit();}} savedRecord={savedRec} disease="other" lang={lang} />;
  return <div>
    <Steps steps={STEPS} current={step} onStep={setStep} />
    <ErrorBanner errors={errors} t={t} />
    {step===1 && <><Blk><Sec icon="📋" title={t.otherStep1} />
      <Row><div><Lbl t={t} req>{t.prefectureLabel}</Lbl><Sel value={f.prefecture} onChange={e=>set("prefecture",e.target.value)} options={PREFECTURES} ph={t.prefecturePh} /></div><div></div></Row>
      <Row><div><Lbl t={t} req>{t.caseId}</Lbl><Inp value={f.caseId} onChange={e=>set("caseId",e.target.value)} placeholder="OTH-2026-001" /></div>
        <div><Lbl t={t}>{t.institution}</Lbl><Inp value={f.institution} onChange={e=>set("institution",e.target.value)} placeholder={t.institutionPh} /></div></Row>
      <Row><div><Lbl t={t} req>{t.diseaseName}</Lbl><Inp value={f.diseaseName} onChange={e=>set("diseaseName",e.target.value)} placeholder={t.diseaseNamePh} /></div>
        <div><Lbl t={t}>{t.icd10}</Lbl><Inp value={f.icd10} onChange={e=>set("icd10",e.target.value)} placeholder={t.icd10Ph} /></div></Row>
      <Row cols={3}><div><Lbl t={t}>{t.age}</Lbl><Inp type="number" value={f.age} onChange={e=>set("age",e.target.value)} placeholder={t.agePh} /></div>
        <div><Lbl t={t}>{t.sex}</Lbl><Sel value={f.sex} onChange={e=>set("sex",e.target.value)} options={[t.male,t.female,t.other]} /></div>
        <div><Lbl t={t}>{t.timePoint}</Lbl><Sel value={f.timePoint} onChange={e=>set("timePoint",e.target.value)} options={TP[lang]} /></div></Row>
      <div><Lbl t={t}>{t.diagDate}</Lbl><Inp type="date" value={f.diagDate} onChange={e=>set("diagDate",e.target.value)} /></div>
    </Blk>
    <DosageBlock f={f} set={set} t={t} />
    </>}
    {step===2 && <Blk><Sec icon="📊" title={t.primaryEval} />
      {[1,2,3].map(n=>(
        <div key={n} style={{ background:"#f8f5f0",padding:"10px 12px",border:"1px solid #e5dfd5",marginBottom:10 }}>
          <div style={{ fontSize:12,fontWeight:700,color:"#1b3a52",marginBottom:6 }}>#{n}</div>
          <Row cols={3}>
            <div><Lbl t={t}>{t.evalName}</Lbl><Inp value={f["eval"+n+"Name"]} onChange={e=>set("eval"+n+"Name",e.target.value)} placeholder={t.evalNamePh} /></div>
            <div><Lbl t={t}>{t.evalValue}</Lbl><Inp value={f["eval"+n+"Val"]} onChange={e=>set("eval"+n+"Val",e.target.value)} placeholder={t.evalValuePh} /></div>
            <div><Lbl t={t}>{t.evalUnit}</Lbl><Inp value={f["eval"+n+"Unit"]} onChange={e=>set("eval"+n+"Unit",e.target.value)} placeholder={t.evalUnitPh} /></div>
          </Row>
        </div>
      ))}
      <div><Lbl t={t}>{t.evalSupp}</Lbl>
        <textarea value={f.evalSupp} onChange={e=>set("evalSupp",e.target.value)} rows={2} style={{ ...S.input,resize:"vertical" }} placeholder={t.evalSuppPh} onFocus={focus} onBlur={blur} /></div>
    </Blk>}
    {step===3 && <Blk><Sec icon="🏠" title={t.qolDesc} />
      <textarea value={f.qolDesc} onChange={e=>set("qolDesc",e.target.value)} rows={4} style={{ ...S.input,resize:"vertical" }} placeholder={t.qolDescPh} onFocus={focus} onBlur={blur} />
    </Blk>}
    {step===4 && <SafetyBlock aeList={OTHER_AE[lang]} form={f} setForm={setF} t={t} />}
    <NavBtns step={step} total={STEPS.length} t={t} onBack={()=>setStep(s=>Math.max(1,s-1))} onNext={()=>setStep(s=>Math.min(STEPS.length,s+1))} onSubmit={submitting ? undefined : handleSubmit} />
  </div>;
}

/* ══════════════════════════════════════════════
   認証まわりの画面（フェーズ2）
══════════════════════════════════════════════ */
const AUTH_ERR_BOX = { background:"#fde8e8", border:"1px solid #f5b8b8", color:"#922b21", padding:"10px 14px", marginBottom:12, fontSize:12.5 };
const AUTH_SUBMIT_BTN = (bg) => ({ width:"100%", background:bg, color:"white", border:"none", padding:"11px", fontFamily:"inherit", fontSize:14, fontWeight:600 });
const AUTH_LINK_BTN = { background:"none", border:"none", color:"#2c6e8a", cursor:"pointer", fontFamily:"inherit", fontWeight:700, textDecoration:"underline", fontSize:12.5 };

function LoginScreen({ t, onLoginSuccess, onSwitchToApply, onSwitchToReset }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await apiFetch("/api/login", { method:"POST", body: JSON.stringify({ email, password }) });
      setAuthToken(res.token);
      const user = { email, display_name: res.display_name, must_change_password: res.must_change_password };
      setAuthUser(user);
      onLoginSuccess(user);
    } catch (err) {
      setError(err.message || t.authLoginError);
    } finally { setLoading(false); }
  };

  return <div style={{ maxWidth:420, margin:"40px auto", padding:"0 20px" }}>
    <Blk>
      <Sec icon="🔐" title={t.authLoginTitle} />
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom:14 }}>
          <Lbl t={t} req>{t.authEmailLabel}</Lbl>
          <Inp type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" />
        </div>
        <div style={{ marginBottom:14 }}>
          <Lbl t={t} req>{t.authPasswordLabel}</Lbl>
          <Inp type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" />
        </div>
        {error && <div style={AUTH_ERR_BOX}>{error}</div>}
        <button type="submit" disabled={loading} style={{ ...AUTH_SUBMIT_BTN("#1b3a52"), cursor: loading ? "wait" : "pointer" }}>
          {loading ? "…" : t.authLoginBtn}
        </button>
      </form>
      <div style={{ marginTop:16, textAlign:"center" }}>
        <button onClick={onSwitchToReset} style={AUTH_LINK_BTN}>{t.authForgotPassword}</button>
      </div>
      <div style={{ marginTop:10, textAlign:"center", fontSize:12.5, color:"#6a7a8a" }}>
        {t.authNoAccount}　<button onClick={onSwitchToApply} style={AUTH_LINK_BTN}>{t.authGoApply}</button>
      </div>
    </Blk>
  </div>;
}

function ApplyScreen({ t, onSwitchToLogin }) {
  const [f, setF] = useState({ email:"", displayName:"", institution:"" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k,v) => setF(p=>({ ...p, [k]:v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await apiFetch("/api/apply", { method:"POST", body: JSON.stringify({
        email: f.email, display_name: f.displayName, institution: f.institution || null,
      }) });
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  if (done) return <div style={{ maxWidth:420, margin:"40px auto", padding:"0 20px" }}>
    <Blk>
      <div style={{ textAlign:"center", padding:"20px 0" }}>
        <div style={{ fontSize:40, marginBottom:10 }}>✅</div>
        <div style={{ fontSize:13.5, color:"#3a4a5a", marginBottom:20 }}>{t.authApplySuccess}</div>
        <button onClick={onSwitchToLogin} style={{ background:"#1b3a52", color:"white", border:"none", padding:"9px 24px", fontFamily:"inherit", fontSize:13, cursor:"pointer" }}>{t.authGoLogin}</button>
      </div>
    </Blk>
  </div>;

  return <div style={{ maxWidth:420, margin:"40px auto", padding:"0 20px" }}>
    <Blk>
      <Sec icon="📝" title={t.authApplyTitle} />
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom:14 }}><Lbl t={t} req>{t.authDisplayNameLabel}</Lbl><Inp value={f.displayName} onChange={e=>set("displayName",e.target.value)} /></div>
        <div style={{ marginBottom:14 }}><Lbl t={t} req>{t.authEmailLabel}</Lbl><Inp type="email" value={f.email} onChange={e=>set("email",e.target.value)} /></div>
        <div style={{ marginBottom:14 }}><Lbl t={t}>{t.authInstitutionLabel}</Lbl><Inp value={f.institution} onChange={e=>set("institution",e.target.value)} /></div>
        {error && <div style={AUTH_ERR_BOX}>{error}</div>}
        <button type="submit" disabled={loading} style={{ ...AUTH_SUBMIT_BTN("#2d6a4f"), cursor: loading ? "wait" : "pointer" }}>
          {loading ? "…" : t.authApplySubmitBtn}
        </button>
      </form>
      <div style={{ marginTop:16, textAlign:"center", fontSize:12.5, color:"#6a7a8a" }}>
        {t.authHaveAccount}　<button onClick={onSwitchToLogin} style={AUTH_LINK_BTN}>{t.authGoLogin}</button>
      </div>
    </Blk>
  </div>;
}

function ChangePasswordScreen({ t, mustChange, onChanged, onCancel }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError(t.authPasswordMismatch); return; }
    setLoading(true);
    try {
      await apiFetch("/api/change-password", { method:"POST", body: JSON.stringify({ current_password: current, new_password: next }) });
      const user = getAuthUser();
      if (user) setAuthUser({ ...user, must_change_password: false });
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  return <div style={{ maxWidth:420, margin:"40px auto", padding:"0 20px" }}>
    <Blk>
      <Sec icon="🔑" title={t.authChangePasswordTitle} />
      {mustChange && <div style={{ background:"#fff8e1", border:"1px solid #f0c040", padding:"10px 14px", marginBottom:14, fontSize:12.5, color:"#7a5c00" }}>{t.authMustChangeNotice}</div>}
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom:14 }}><Lbl t={t} req>{t.authCurrentPasswordLabel}</Lbl><Inp type="password" value={current} onChange={e=>setCurrent(e.target.value)} /></div>
        <div style={{ marginBottom:14 }}><Lbl t={t} req>{t.authNewPasswordLabel}</Lbl><Inp type="password" value={next} onChange={e=>setNext(e.target.value)} /></div>
        <div style={{ marginBottom:14 }}><Lbl t={t} req>{t.authNewPasswordConfirmLabel}</Lbl><Inp type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} /></div>
        {error && <div style={AUTH_ERR_BOX}>{error}</div>}
        <button type="submit" disabled={loading} style={{ ...AUTH_SUBMIT_BTN("#1b3a52"), cursor: loading ? "wait" : "pointer" }}>
          {loading ? "…" : t.authChangePasswordSubmitBtn}
        </button>
      </form>
      {!mustChange && <div style={{ marginTop:14, textAlign:"center" }}>
        <button onClick={onCancel} style={{ ...AUTH_LINK_BTN, color:"#6a7a8a", fontWeight:400 }}>{t.prev}</button>
      </div>}
    </Blk>
  </div>;
}

function ResetRequestScreen({ t, onSwitchToLogin }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await apiFetch("/api/password-reset/request", { method:"POST", body: JSON.stringify({ email }) });
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  return <div style={{ maxWidth:420, margin:"40px auto", padding:"0 20px" }}>
    <Blk>
      <Sec icon="✉️" title={t.authResetRequestTitle} />
      {sent ? <div style={{ textAlign:"center", padding:"10px 0" }}>
        <div style={{ fontSize:36, marginBottom:10 }}>📨</div>
        <div style={{ fontSize:13, color:"#3a4a5a", marginBottom:18 }}>{t.authResetRequestSent}</div>
        <button onClick={onSwitchToLogin} style={{ background:"#1b3a52", color:"white", border:"none", padding:"9px 24px", fontFamily:"inherit", fontSize:13, cursor:"pointer" }}>{t.authGoLogin}</button>
      </div> : <form onSubmit={handleSubmit}>
        <div style={{ fontSize:12.5, color:"#6a7a8a", marginBottom:14 }}>{t.authResetRequestDesc}</div>
        <div style={{ marginBottom:14 }}><Lbl t={t} req>{t.authEmailLabel}</Lbl><Inp type="email" value={email} onChange={e=>setEmail(e.target.value)} /></div>
        {error && <div style={AUTH_ERR_BOX}>{error}</div>}
        <button type="submit" disabled={loading} style={{ ...AUTH_SUBMIT_BTN("#1b3a52"), cursor: loading ? "wait" : "pointer" }}>
          {loading ? "…" : t.authResetRequestSubmitBtn}
        </button>
      </form>}
      {!sent && <div style={{ marginTop:14, textAlign:"center" }}>
        <button onClick={onSwitchToLogin} style={{ ...AUTH_LINK_BTN, color:"#6a7a8a", fontWeight:400 }}>{t.authGoLogin}</button>
      </div>}
    </Blk>
  </div>;
}

function ResetConfirmScreen({ t, token, onDone }) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError(t.authPasswordMismatch); return; }
    setLoading(true);
    try {
      await apiFetch("/api/password-reset/confirm", { method:"POST", body: JSON.stringify({ token, new_password: next }) });
      setDone(true);
    } catch (err) {
      setError(err.message || t.authResetConfirmInvalid);
    } finally { setLoading(false); }
  };

  return <div style={{ maxWidth:420, margin:"40px auto", padding:"0 20px" }}>
    <Blk>
      <Sec icon="🔑" title={t.authResetConfirmTitle} />
      {done ? <div style={{ textAlign:"center", padding:"10px 0" }}>
        <div style={{ fontSize:36, marginBottom:10 }}>✅</div>
        <div style={{ fontSize:13, color:"#3a4a5a", marginBottom:18 }}>{t.authResetConfirmSuccess}</div>
        <button onClick={onDone} style={{ background:"#1b3a52", color:"white", border:"none", padding:"9px 24px", fontFamily:"inherit", fontSize:13, cursor:"pointer" }}>{t.authGoLogin}</button>
      </div> : <form onSubmit={handleSubmit}>
        <div style={{ marginBottom:14 }}><Lbl t={t} req>{t.authNewPasswordLabel}</Lbl><Inp type="password" value={next} onChange={e=>setNext(e.target.value)} /></div>
        <div style={{ marginBottom:14 }}><Lbl t={t} req>{t.authNewPasswordConfirmLabel}</Lbl><Inp type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} /></div>
        {error && <div style={AUTH_ERR_BOX}>{error}</div>}
        <button type="submit" disabled={loading} style={{ ...AUTH_SUBMIT_BTN("#1b3a52"), cursor: loading ? "wait" : "pointer" }}>
          {loading ? "…" : t.authResetConfirmSubmitBtn}
        </button>
      </form>}
    </Blk>
  </div>;
}

/* ── 疾患セレクター ──────────────────────────── */
function DiseaseSelector({ onSelect, t }) {
  const DISEASES=[
    {id:"pd",icon:"🧠",color:"#2c6e8a",label:t.pdLabel,sub:t.pdSub},
    {id:"ad",icon:"💭",color:"#6a4c9c",label:t.adLabel,sub:t.adSub},
    {id:"als",icon:"⚡",color:"#c0392b",label:t.alsLabel,sub:t.alsSub},
    {id:"cancer",icon:"🎯",color:"#2d6a4f",label:t.cancerLabel,sub:t.cancerSub},
    {id:"stroke",icon:"💢",color:"#d35400",label:t.strokeLabel,sub:t.strokeSub},
    {id:"sci",icon:"🦴",color:"#7f8c8d",label:t.sciLabel,sub:t.sciSub},
    {id:"dm",icon:"🩸",color:"#b8336a",label:t.dmLabel,sub:t.dmSub},
    {id:"other",icon:"📋",color:"#4a6572",label:t.otherLabel,sub:t.otherSub},
  ];
  return <div>
    <div style={{ textAlign:"center",marginBottom:20 }}>
      <div style={{ fontSize:16,fontWeight:700,color:"#1b3a52",marginBottom:4 }}>{t.selectDisease}</div>
      <div style={{ fontSize:13,color:"#6a7a8a" }}>{t.selectDiseaseSub}</div>
    </div>
    <div className="sc-disease-grid" style={{ display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12 }}>
      {DISEASES.map(d=>(
        <button key={d.id} onClick={()=>onSelect(d.id)} style={{ background:"white",border:`2px solid ${d.color}25`,padding:"20px 18px",textAlign:"left",cursor:"pointer",fontFamily:"inherit",transition:"all .2s" }}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=d.color;e.currentTarget.style.background=`${d.color}08`;e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 6px 18px rgba(0,0,0,.1)"}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=`${d.color}25`;e.currentTarget.style.background="white";e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none"}}>
          <div style={{ fontSize:28,marginBottom:8 }}>{d.icon}</div>
          <div style={{ fontSize:14.5,fontWeight:700,color:"#1b3a52",marginBottom:4 }}>{d.label}</div>
          <div style={{ fontSize:11.5,color:d.color,fontWeight:600 }}>{d.sub}</div>
        </button>
      ))}
    </div>
  </div>;
}

/* ── ダッシュボード ─────────────────────────── */
function Dashboard({ t, lang, diseasesMap, currentUser }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [trendMode, setTrendMode] = useState("individual");
  const [selectedDisease, setSelectedDisease] = useState("all");
  const [selectedCaseKey, setSelectedCaseKey] = useState("");
  const [compareCaseIds, setCompareCaseIds] = useState([]);
  const [compareMetricKey, setCompareMetricKey] = useState("");
  const [focusMetricKey, setFocusMetricKey] = useState("");
  const [meanMetricKey, setMeanMetricKey] = useState("");
  const [gridMetricKey, setGridMetricKey] = useState("");
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch("/api/cases")
      .then(res => { if (!cancelled) setCases((res.cases || []).map(normalizeApiCase)); })
      .catch(() => { if (!cancelled) setCases([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const byDisease = Object.keys(diseasesMap).map(id => ({
    id, name: diseasesMap[id].label, value: cases.filter(c=>c.disease===id).length
  })).filter(d=>d.value>0);

  const uniqueCaseKeys = Array.from(new Set(cases.map(c=>`${c.disease}|${c.caseId}`).filter(k=>k.split("|")[1])));
  const filteredCaseKeys = selectedDisease==="all" ? uniqueCaseKeys : uniqueCaseKeys.filter(k=>k.startsWith(selectedDisease+"|"));

  const [selDisease, selCaseId] = selectedCaseKey ? selectedCaseKey.split("|") : [null,null];
  const timelineRecords = selCaseId ? cases.filter(c=>c.disease===selDisease && c.caseId===selCaseId) : [];

  const handleDeleteMine = async () => {
    if (!window.confirm(t.dashboardDeleteMineConfirm)) return;
    try {
      await apiFetch("/api/cases/mine", { method:"DELETE" });
      setRefreshKey(k=>k+1);
    } catch (err) { alert(err.message); }
  };

  const histDisease = selectedDisease==="all" ? "pd" : selectedDisease;
  const histMetrics = KEY_METRICS[histDisease] || [];

  const trendDisease = selectedDisease === "all" ? "pd" : selectedDisease;
  const trendMetrics = KEY_METRICS[trendDisease] || [];
  const diseaseCases = cases.filter(c=>c.disease===trendDisease);
  const trendCaseIds = Array.from(new Set(diseaseCases.map(c=>c.caseId).filter(Boolean)));

  const currentCompareMetric = compareMetricKey || (trendMetrics[0]?.key || "");
  const currentFocusMetric = focusMetricKey || (trendMetrics[0]?.key || "");
  const currentMeanMetric = meanMetricKey || (trendMetrics[0]?.key || "");
  const currentGridMetric = gridMetricKey || (trendMetrics[0]?.key || "");
  const compareMetricLabel = trendMetrics.find(m=>m.key===currentCompareMetric)?.label || "";
  const focusMetricLabel = trendMetrics.find(m=>m.key===currentFocusMetric)?.label || "";
  const meanMetricLabel = trendMetrics.find(m=>m.key===currentMeanMetric)?.label || "";
  const gridMetricLabel = trendMetrics.find(m=>m.key===currentGridMetric)?.label || "";

  const toggleCompareCase = (cid) => {
    setCompareCaseIds(prev => prev.includes(cid) ? prev.filter(x=>x!==cid) : (prev.length >= 8 ? prev : [...prev, cid]));
  };

  const TREND_TABS = [
    { id:"individual", label:t.trendIndividual, icon:"👤" },
    { id:"compare",    label:t.trendCompare,    icon:"🔀" },
    { id:"metric",     label:t.trendMetric,     icon:"📊" },
    { id:"mean",       label:t.trendMean,       icon:"📐" },
    { id:"grid",       label:t.trendGrid,       icon:"🗂" },
  ];

  return <div key={refreshKey}>
    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,marginBottom:16 }}>
      <div>
        <div style={{ fontSize:16,fontWeight:700,color:"#1b3a52" }}>{t.dashboardTitle}</div>
        <div style={{ fontSize:12,color:"#6a7a8a",marginTop:2 }}>{t.dashboardSub}　({t.totalCases}: {cases.length})</div>
      </div>
      <div className="sc-action-btns" style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
        <button onClick={()=>exportCSV(cases)} style={{ background:"#2d6a4f",color:"white",border:"none",padding:"8px 16px",fontFamily:"inherit",fontSize:12.5,cursor:"pointer" }}>⬇ {t.exportCSV}</button>
        {currentUser && <button onClick={handleDeleteMine} style={{ background:"#c0392b",color:"white",border:"none",padding:"8px 16px",fontFamily:"inherit",fontSize:12.5,cursor:"pointer" }}>✕ {t.dashboardDeleteMineBtn}</button>}
      </div>
    </div>

    {loading ? <div style={{ textAlign:"center",padding:"30px",fontSize:13,color:"#8a9aaa" }}>…</div> :
    cases.length===0 ? <div style={{ background:"#fff8e1",border:"1px solid #f0c040",padding:"20px",textAlign:"center",fontSize:13,color:"#7a5c00" }}>
      {t.noData}
    </div> : <>
      <div className="sc-grid-2" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14 }}>
        <DiseasePieChart data={byDisease} title={t.byDisease} />
        <DiseaseBarChart data={byDisease} title={t.casesPerDisease} />
      </div>

      {/* 個人推移セクション */}
      <div style={{ background:"white",border:"1px solid #e5e0d8",padding:"16px 18px",marginBottom:14 }}>
        <div style={{ fontSize:14,fontWeight:700,color:"#1b3a52",marginBottom:12 }}>📈 {t.trendSectionTitle}</div>

        <div style={{ marginBottom:12 }}>
          <Lbl t={t}>{t.filterByDisease}</Lbl>
          <select value={selectedDisease} onChange={e=>{setSelectedDisease(e.target.value);setSelectedCaseKey("");setCompareCaseIds([]);setCompareMetricKey("");setFocusMetricKey("");setMeanMetricKey("");setGridMetricKey("");}}
            style={S.input}>
            <option value="all">{t.allDiseases}</option>
            {Object.keys(diseasesMap).map(id=>(<option key={id} value={id}>{diseasesMap[id].label}</option>))}
          </select>
        </div>

        <div style={{ display:"flex",gap:4,background:"#eef0f2",padding:3,marginBottom:14,flexWrap:"wrap" }}>
          {TREND_TABS.map(tb=>(
            <button key={tb.id} onClick={()=>setTrendMode(tb.id)}
              style={{ background:trendMode===tb.id?"white":"transparent",border:"none",padding:"7px 14px",fontFamily:"inherit",fontSize:12,cursor:"pointer",fontWeight:trendMode===tb.id?700:500,color:trendMode===tb.id?"#1b3a52":"#6a7a8a",borderRadius:4,boxShadow:trendMode===tb.id?"0 1px 3px rgba(0,0,0,.08)":"none",flex:"1 1 auto",whiteSpace:"nowrap" }}>
              {tb.icon} {tb.label}
            </button>
          ))}
        </div>

        {trendMode==="individual" && <>
          <div style={{ marginBottom:12 }}>
            <Lbl t={t}>{t.selectCase}</Lbl>
            <select value={selectedCaseKey} onChange={e=>setSelectedCaseKey(e.target.value)} style={S.input}>
              <option value="">{t.selectCasePh}</option>
              {filteredCaseKeys.map(k=>{const [d,id]=k.split("|");return <option key={k} value={k}>{id} ({diseasesMap[d]?.label||d})</option>;})}
            </select>
          </div>
          {selCaseId && timelineRecords.length>=2 && <>
            <TimelineChart records={timelineRecords} disease={selDisease} title={`${selCaseId} ${t.timelineTitle}`} />
            <div style={{ marginTop:14 }}>
              <div style={{ fontSize:12.5,fontWeight:700,color:"#1b3a52",marginBottom:8 }}>📊 {t.individualBreakdown}</div>
              <DetailedCaseCharts records={timelineRecords} disease={selDisease} t={t} />
            </div>
          </>}
          {selCaseId && timelineRecords.length<2 && <div style={{ background:"#fff8e1",border:"1px solid #f0c040",padding:"12px 16px",fontSize:12.5,color:"#7a5c00" }}>{t.timelineNeedMore}</div>}
          {!selCaseId && <div style={{ fontSize:12,color:"#8a9aaa",fontStyle:"italic",padding:"12px 0" }}>{t.selectCasePh}</div>}
        </>}

        {trendMode==="compare" && <>
          <div className="sc-grid-2" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12 }}>
            <div>
              <Lbl t={t}>{t.selectMetric}</Lbl>
              <select value={currentCompareMetric} onChange={e=>setCompareMetricKey(e.target.value)} style={S.input}>
                {trendMetrics.map(m=>(<option key={m.key} value={m.key}>{m.label}</option>))}
              </select>
            </div>
            <div style={{ fontSize:11.5,color:"#6a7a8a",alignSelf:"end",paddingBottom:6 }}>
              {t.compareHint} ({compareCaseIds.length} / {Math.min(8, trendCaseIds.length)})
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <Lbl t={t}>{t.selectCasesToCompare}</Lbl>
            <div style={{ display:"flex",flexWrap:"wrap",gap:6,maxHeight:130,overflowY:"auto",border:"1px solid #e5dfd5",padding:8,background:"#fafaf7" }}>
              {trendCaseIds.length===0 && <div style={{ fontSize:11.5,color:"#aaa" }}>{t.noData}</div>}
              {trendCaseIds.map(cid=>{
                const sel = compareCaseIds.includes(cid);
                return <label key={cid} style={{ display:"inline-flex",alignItems:"center",gap:4,padding:"4px 10px",border:"1px solid "+(sel?"#2c6e8a":"#d8d2c8"),background:sel?"#eaf4f8":"white",cursor:"pointer",fontSize:11.5,fontFamily:"monospace" }}>
                  <input type="checkbox" checked={sel} onChange={()=>toggleCompareCase(cid)} />{cid}
                </label>;
              })}
            </div>
          </div>
          {compareCaseIds.length>=1 ? <CompareCasesChart allCases={cases} disease={trendDisease} selectedCaseIds={compareCaseIds} metricKey={currentCompareMetric} metricLabel={compareMetricLabel} t={t} /> :
            <div style={{ fontSize:12,color:"#8a9aaa",fontStyle:"italic" }}>{t.compareNeedSelection}</div>}
        </>}

        {trendMode==="metric" && <>
          <div style={{ marginBottom:12 }}>
            <Lbl t={t}>{t.selectMetric}</Lbl>
            <select value={currentFocusMetric} onChange={e=>setFocusMetricKey(e.target.value)} style={S.input}>
              {trendMetrics.map(m=>(<option key={m.key} value={m.key}>{m.label}</option>))}
            </select>
          </div>
          {currentFocusMetric && <MetricFocusChart allCases={cases} disease={trendDisease} metricKey={currentFocusMetric} metricLabel={focusMetricLabel} t={t} />}
        </>}

        {trendMode==="mean" && <>
          <div style={{ marginBottom:12 }}>
            <Lbl t={t}>{t.selectMetric}</Lbl>
            <select value={currentMeanMetric} onChange={e=>setMeanMetricKey(e.target.value)} style={S.input}>
              {trendMetrics.map(m=>(<option key={m.key} value={m.key}>{m.label}</option>))}
            </select>
          </div>
          {currentMeanMetric && <DiseaseMeanChart allCases={cases} disease={trendDisease} metricKey={currentMeanMetric} metricLabel={meanMetricLabel} t={t} />}
        </>}

        {trendMode==="grid" && <>
          <div style={{ marginBottom:12 }}>
            <Lbl t={t}>{t.selectMetric}</Lbl>
            <select value={currentGridMetric} onChange={e=>setGridMetricKey(e.target.value)} style={S.input}>
              {trendMetrics.map(m=>(<option key={m.key} value={m.key}>{m.label}</option>))}
            </select>
          </div>
          {currentGridMetric && <CaseGridView allCases={cases} disease={trendDisease} metricKey={currentGridMetric} metricLabel={gridMetricLabel} t={t}
            onSelect={(cid)=>{setSelectedCaseKey(`${trendDisease}|${cid}`);setTrendMode("individual");}} />}
        </>}
      </div>

      {histMetrics.length>0 && <div style={{ marginTop:8 }}>
        <div style={{ fontSize:13,fontWeight:700,color:"#1b3a52",marginBottom:8 }}>📊 {t.histogramTitle} ({diseasesMap[histDisease]?.label})</div>
        <div className="sc-grid-2" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
          {histMetrics.slice(0,2).map(m=>(
            <HistogramChart key={m.key} records={cases} disease={histDisease} metricKey={m.key} metricLabel={m.label} title={m.label} />
          ))}
        </div>
      </div>}
    </>}
  </div>;
}

/* ══════════════════════════════════════════════
   症例詳細表示用の整形ヘルパー
══════════════════════════════════════════════ */
function formatFieldValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length > 0 ? v.join(", ") : "—";
  if (typeof v === "boolean") return v ? "✓" : "—";
  if (typeof v === "object") {
    if (v.gradeMap) {
      const entries = Object.entries(v.gradeMap).filter(([,g])=>g);
      return entries.length > 0 ? entries.map(([k,g])=>`${k}: ${g}`).join(", ") : "—";
    }
    return JSON.stringify(v);
  }
  return String(v);
}

/* 表示対象外のキー(内部管理用) */
const HIDDEN_KEYS = new Set(["disease","savedAt","ae","sae","id","is_owner","updatedAt"]);

function CaseDetail({ record, t, diseaseLabel, allCases }) {
  const sameCase = (allCases || []).filter(c => c.disease === record.disease && c.caseId === record.caseId)
    .sort((a, b) => (a.timePoint || "").localeCompare(b.timePoint || ""));
  const hasTimeline = sameCase.length >= 2;
  const entries = Object.entries(record).filter(([k,v])=>!HIDDEN_KEYS.has(k) && v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0));
  const printRef = "case-print-" + (record.savedAt || "x").replace(/[^a-zA-Z0-9]/g,"");
  const handlePrint = () => {
    const el = document.getElementById(printRef);
    if (!el) return;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { alert(t.errorTitle); return; }
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${record.caseId} - ${diseaseLabel}</title>
      <style>
        body{font-family:'Noto Sans JP','Hiragino Sans',sans-serif;padding:24px;color:#1a1a2e;font-size:13px;line-height:1.6;}
        h1{font-size:20px;color:#1b3a52;border-bottom:2px solid #1b3a52;padding-bottom:6px;margin:0 0 4px 0;}
        h2{font-size:14px;color:#1b3a52;background:#eef4f7;padding:6px 10px;margin:18px 0 8px 0;border-left:3px solid #2c6e8a;}
        .meta{color:#6a7a8a;font-size:11px;margin-bottom:18px;}
        .grid{display:grid;grid-template-columns:repeat(2,1fr);gap:6px 18px;}
        .item{font-size:11.5px;padding:3px 0;border-bottom:1px dotted #ddd;}
        .item .k{color:#6a7a8a;margin-right:6px;}
        .item .v{color:#1b3a52;font-weight:600;}
        .ae{color:#c0392b;font-weight:700;}
        .footer{margin-top:30px;padding-top:10px;border-top:1px solid #ddd;font-size:10px;color:#999;text-align:center;}
        @media print { body{padding:12px;} }
      </style></head><body>
      <h1>${record.caseId} — ${diseaseLabel}</h1>
      <div class="meta">${t.savedCaseInfo}: ${record.savedAt ? record.savedAt.slice(0,10) : "—"} / ${t.timePoint || ""}: ${record.timePoint || "—"}</div>
      <h2>${t.formTitle}</h2>
      <div class="grid">
        ${entries.map(([k,v])=>`<div class="item"><span class="k">${k}:</span><span class="v">${formatFieldValue(v).replace(/</g,'&lt;')}</span></div>`).join("")}
      </div>
      ${record.ae && Array.isArray(record.ae) && record.ae.length > 0 ? `<h2>${t.saeTitle || "AE"}</h2><div class="ae">${record.ae.join(", ")}${record.sae?" ⚠ SAE":""}</div>` : ""}
      ${record.notes ? `<h2>備考 / Notes</h2><div>${String(record.notes).replace(/</g,'&lt;')}</div>` : ""}
      <div class="footer">SC-NET Japan — 幹細胞上清液 臨床研究ネットワーク / Printed: ${new Date().toLocaleString()}</div>
      <script>setTimeout(()=>window.print(),250);</script>
      </body></html>`);
    w.document.close();
  };
  return <div style={{ background:"#f8f5f0", borderTop:"1px solid #e5dfd5", padding:"16px 22px" }}>
    <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:10 }}>
      <button onClick={handlePrint} style={{ background:"#1b3a52", color:"white", border:"none", padding:"6px 14px", fontFamily:"inherit", fontSize:11.5, cursor:"pointer" }}>📄 {t.exportPDF}</button>
    </div>
    <div id={printRef}>
    <div className="sc-detail-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px, 1fr))", gap:"8px 16px", marginBottom:14 }}>
      {entries.map(([k,v])=>(
        <div key={k} style={{ fontSize:12, lineHeight:1.6 }}>
          <span style={{ color:"#8a9aaa", marginRight:6 }}>{k}:</span>
          <strong style={{ color:"#1b3a52" }}>{formatFieldValue(v)}</strong>
        </div>
      ))}
    </div>
    {record.ae && Array.isArray(record.ae) && record.ae.length > 0 && <div style={{ marginBottom:10, fontSize:12 }}>
      <span style={{ color:"#8a9aaa", marginRight:6 }}>有害事象 (AE):</span>
      <strong style={{ color:"#c0392b" }}>{record.ae.join(", ")}</strong>
      {record.sae && <span style={{ marginLeft:8, color:"#c0392b", fontWeight:700 }}>⚠ SAE</span>}
    </div>}
    {record.notes && <div style={{ marginBottom:10, fontSize:12 }}>
      <span style={{ color:"#8a9aaa", marginRight:6 }}>備考:</span>
      <span style={{ color:"#3a4a5a" }}>{record.notes}</span>
    </div>}
    {hasTimeline && <TimelineChart records={sameCase} disease={record.disease} title={`${record.caseId} ${t.timelineTitle}`} />}
    {!hasTimeline && <div style={{ fontSize:11.5, color:"#8a9aaa", fontStyle:"italic", marginTop:8 }}>{t.timelineNeedMore}</div>}
    </div>
  </div>;
}

/* ══════════════════════════════════════════════
   自分のデータ一覧 (MyCasesList)
══════════════════════════════════════════════ */
function MyCasesList({ t, lang, diseasesMap, cases, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const [filterDisease, setFilterDisease] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");

  // フィルター
  let filtered = cases;
  if (filterDisease !== "all") filtered = filtered.filter(c => c.disease === filterDisease);
  if (search.trim()) {
    const q = search.trim().toLowerCase();
    filtered = filtered.filter(c =>
      (c.caseId || "").toLowerCase().includes(q) ||
      (c.institution || "").toLowerCase().includes(q) ||
      (c.diseaseName || "").toLowerCase().includes(q) ||
      (diseasesMap[c.disease]?.label || "").toLowerCase().includes(q)
    );
  }
  // 並び替え
  filtered = [...filtered].sort((a,b)=>{
    const aT = a.savedAt || "", bT = b.savedAt || "";
    return sortOrder === "newest" ? bT.localeCompare(aT) : aT.localeCompare(bT);
  });

  const handleDeleteClick = (record) => {
    if (!window.confirm(t.confirmDeleteCase + `\n\n${record.caseId} / ${record.timePoint || "—"}`)) return;
    onDelete && onDelete(record);
    setExpanded(null);
  };

  return <div>
    <div className="sc-search-row" style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:14 }}>
      <input type="text" placeholder={t.searchPlaceholder} value={search} onChange={e=>setSearch(e.target.value)}
        style={{ flex:"1 1 200px", minWidth:180, padding:"7px 10px", border:"1px solid #d8d2c8", fontSize:13, fontFamily:"inherit" }} />
      <select value={filterDisease} onChange={e=>setFilterDisease(e.target.value)}
        style={{ padding:"7px 10px", border:"1px solid #d8d2c8", fontSize:13, fontFamily:"inherit", background:"white" }}>
        <option value="all">{t.allDiseases}</option>
        {Object.keys(diseasesMap).map(id => <option key={id} value={id}>{diseasesMap[id].label}</option>)}
      </select>
      <select value={sortOrder} onChange={e=>setSortOrder(e.target.value)}
        style={{ padding:"7px 10px", border:"1px solid #d8d2c8", fontSize:13, fontFamily:"inherit", background:"white" }}>
        <option value="newest">{t.sortNewest}</option>
        <option value="oldest">{t.sortOldest}</option>
      </select>
    </div>

    <div style={{ fontSize:12, color:"#6a7a8a", marginBottom:10 }}>
      {t.matchedCases}: <strong style={{ color:"#1b3a52" }}>{filtered.length}</strong> / {cases.length}
    </div>

    {filtered.length === 0 ? <div style={{ background:"#fff8e1", border:"1px solid #f0c040", padding:"20px", textAlign:"center", fontSize:13, color:"#7a5c00" }}>
      {cases.length === 0 ? t.noData : t.noMatch}
    </div> : filtered.map((c) => {
      const dm = diseasesMap[c.disease] || {};
      const isOpen = expanded === c.id;
      const dateStr = c.savedAt ? c.savedAt.slice(0, 10) : "—";
      return <div key={c.id} style={{ background:"white", border:"1px solid #e5e0d8", marginBottom:2, transition:"all .2s" }}>
        <div onClick={()=>setExpanded(isOpen ? null : c.id)}
          style={{ padding:"14px 22px", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:"flex", gap:8, marginBottom:5, alignItems:"center", flexWrap:"wrap" }}>
              <span style={{ fontFamily:"monospace", fontSize:11.5, color:"#6a7a8a" }}>{c.caseId || "—"}</span>
              <span style={{ fontSize:11, padding:"2px 10px", borderRadius:20, background:`${dm.color}18`, color:dm.color, fontWeight:700 }}>{dm.icon} {dm.label}</span>
              {c.timePoint && <span style={{ fontSize:11, padding:"2px 9px", borderRadius:20, background:"#eef4f7", color:"#1b6080", fontWeight:600 }}>{c.timePoint}</span>}
            </div>
            <div style={{ fontSize:12, color:"#6a7a8a" }}>
              {c.institution && <span>🏥 {c.institution}　</span>}
              {c.age && <span>👤 {c.age}歳　</span>}
              📅 {dateStr}
            </div>
          </div>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            {c.is_owner && <button onClick={(e)=>{e.stopPropagation();onEdit && onEdit(c);}}
              style={{ background:"transparent", border:"1px solid #e5dfd5", color:"#2c6e8a", padding:"4px 10px", fontFamily:"inherit", fontSize:11.5, cursor:"pointer" }}>
              ✏ {t.edit}
            </button>}
            {c.is_owner && <button onClick={(e)=>{e.stopPropagation();handleDeleteClick(c);}}
              style={{ background:"transparent", border:"1px solid #e5dfd5", color:"#c0392b", padding:"4px 10px", fontFamily:"inherit", fontSize:11.5, cursor:"pointer" }}>
              🗑 {t.delete}
            </button>}
            <span style={{ fontSize:16, color:"#8a9aaa", transition:"transform .2s", transform:isOpen?"rotate(180deg)":"none" }}>▼</span>
          </div>
        </div>
        {isOpen && <CaseDetail record={c} t={t} diseaseLabel={dm.label} allCases={cases} />}
      </div>;
    })}
  </div>;
}

/* ══════════════════════════════════════════════
   メインアプリ
══════════════════════════════════════════════ */
const DISEASE_COLORS={pd:"#2c6e8a",ad:"#6a4c9c",als:"#c0392b",cancer:"#2d6a4f",stroke:"#d35400",sci:"#7f8c8d",dm:"#b8336a",other:"#4a6572"};

export default function App() {
  const [lang, setLang] = useState("ja");
  const [tab, setTab] = useState("cases");
  const [casesSubTab, setCasesSubTab] = useState("mine");
  const [disease, setDisease] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => (getAuthToken() ? getAuthUser() : null));
  const [authView, setAuthView] = useState(null); // null | "login" | "apply" | "change-password" | "reset-request" | "reset-confirm"
  const [resetToken, setResetToken] = useState(null);
  const [myCases, setMyCases] = useState([]);
  const [publicCases, setPublicCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);
  const [safetyStats, setSafetyStats] = useState(null);
  const [safetyLoading, setSafetyLoading] = useState(false);
  const [safetyError, setSafetyError] = useState(null);
  const t = TRANSLATIONS[lang];

  /* 401（トークン無効・期限切れ）を検知したら自動ログアウトしてログイン画面へ */
  useEffect(() => {
    const handler = () => { setCurrentUser(null); setAuthView("login"); };
    window.addEventListener("sc-net-auth-expired", handler);
    return () => window.removeEventListener("sc-net-auth-expired", handler);
  }, []);

  /* パスワード再設定メールのリンク（?token=xxx）を検知 */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tk = params.get("token");
    if (tk) { setResetToken(tk); setAuthView("reset-confirm"); }
  }, []);

  const loadMyCases = () => {
    setCasesLoading(true);
    apiFetch("/api/cases/mine")
      .then(res => setMyCases((res.cases || []).map(normalizeApiCase)))
      .catch(() => setMyCases([]))
      .finally(() => setCasesLoading(false));
  };
  const loadPublicCases = () => {
    setCasesLoading(true);
    apiFetch("/api/cases")
      .then(res => setPublicCases((res.cases || []).map(normalizeApiCase)))
      .catch(() => setPublicCases([]))
      .finally(() => setCasesLoading(false));
  };
  /* 症例データタブを開いたとき（サブタブ切替時含む）に都度取得する */
  useEffect(() => {
    if (tab !== "cases") return;
    if (casesSubTab === "mine" && currentUser) loadMyCases();
    if (casesSubTab === "public") loadPublicCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, casesSubTab, currentUser]);

  /* プロトコル・文書タブを開いたとき（ログイン中のみ）に一覧取得する */
  useEffect(() => {
    if (tab !== "protocols" || !currentUser) return;
    setDocumentsLoading(true);
    setDocumentsError(null);
    apiFetch("/api/documents")
      .then(res => setDocuments(res.documents || []))
      .catch(() => setDocumentsError(t.docErrorMsg))
      .finally(() => setDocumentsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, currentUser]);

  /* 安全性情報タブを開いたとき（未ログインでも取得可能）に統計取得する */
  useEffect(() => {
    if (tab !== "safety") return;
    setSafetyLoading(true);
    setSafetyError(null);
    apiFetch("/api/safety-stats")
      .then(res => setSafetyStats(res))
      .catch(() => setSafetyError(t.safetyErrorMsg))
      .finally(() => setSafetyLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleDownloadDocument = async (doc) => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/api/documents/${doc.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || doc.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(t.docErrorMsg);
    }
  };

  const handleDeleteCase = async (record) => {
    try {
      await apiFetch(`/api/cases/${record.id}`, { method:"DELETE" });
      if (casesSubTab === "mine") loadMyCases(); else loadPublicCases();
    } catch (err) { alert(err.message); }
  };

  const handleEditCase = (record) => {
    setEditingRecord(record);
    setDisease(record.disease);
    setTab("enter");
  };
  const handleCancelEdit = () => { setEditingRecord(null); setDisease(null); };

  const handleLoginSuccess = (user) => {
    setCurrentUser(user);
    if (user.must_change_password) {
      setAuthView("change-password");
    } else {
      setAuthView(null);
      setTab("cases"); setCasesSubTab("mine");
    }
  };
  const handleLogout = () => {
    clearAuth();
    setCurrentUser(null);
    setAuthView(null);
    setCasesSubTab("public");
    setDisease(null); setEditingRecord(null);
  };
  const handlePasswordChanged = () => {
    setCurrentUser(getAuthUser());
    setAuthView(null);
  };

  const TABS=[{id:"cases",label:t.tab1},{id:"enter",label:t.tab2},{id:"dashboard",label:t.tabDashboard},{id:"protocols",label:t.tab3},{id:"safety",label:t.tab4}];
  const DISEASES_MAP={pd:{icon:"🧠",color:"#2c6e8a",label:t.pdLabel},ad:{icon:"💭",color:"#6a4c9c",label:t.adLabel},als:{icon:"⚡",color:"#c0392b",label:t.alsLabel},cancer:{icon:"🎯",color:"#2d6a4f",label:t.cancerLabel},stroke:{icon:"💢",color:"#d35400",label:t.strokeLabel},sci:{icon:"🦴",color:"#7f8c8d",label:t.sciLabel},dm:{icon:"🩸",color:"#b8336a",label:t.dmLabel},other:{icon:"📋",color:"#4a6572",label:t.otherLabel}};

  return (
    <div style={{ fontFamily:"'Noto Sans JP', 'Hiragino Sans', 'BIZ UDPGothic', sans-serif", background:"#f7f5f0", minHeight:"100vh", color:"#1a1a2e", fontSize:14 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@300;400;500;700&family=Zen+Kaku+Gothic+New:wght@400;500;700&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        body { line-height:1.7; letter-spacing:0.02em; }
        input, select, textarea, button { font-family:'Noto Sans JP','Hiragino Sans','BIZ UDPGothic',sans-serif; letter-spacing:0.02em; }
        .lang-btn { background:none; border:none; padding:6px 10px; cursor:pointer; font-size:13px; color:rgba(244,241,235,.75); font-family:inherit; border-radius:4px; transition:all .2s; white-space:nowrap; }
        .lang-btn:hover, .lang-btn.active { background:rgba(255,255,255,.15); color:#f4f1eb; }
      `}</style>

      {/* Nav */}
      <nav className="sc-header" style={{ background:"#1b3a52", minHeight:58, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 28px", position:"sticky", top:0, zIndex:100, boxShadow:"0 2px 14px rgba(0,0,0,.25)" }}>
        <div
          onClick={()=>{setAuthView(null);setTab("cases");setDisease(null);setNavOpen(false);window.scrollTo({top:0,behavior:"smooth"});}}
          style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}
        >
          <span style={{ fontSize:20 }}>⚕</span>
          <div>
            <div style={{ fontFamily:"'Zen Kaku Gothic New', 'Hiragino Sans', sans-serif", fontSize:15, fontWeight:700, color:"#f4f1eb", letterSpacing:"0.08em" }}>{t.siteName}</div>
            <div className="sc-header-logo-sub" style={{ fontSize:9.5, color:"rgba(244,241,235,.5)", letterSpacing:"0.08em" }}>{t.siteTagline}</div>
          </div>
        </div>

        {/* 言語切り替え */}
        <div className="sc-lang-switch" style={{ display:"flex", alignItems:"center", gap:3, background:"rgba(0,0,0,.25)", borderRadius:8, padding:"4px" }}>
          {Object.entries(LANG_META).map(([code,{ label }])=>{
            const active = lang === code;
            const FlagComp = FLAG_MAP[code];
            return (
              <button key={code} onClick={()=>setLang(code)} className="lang-btn" style={{
                display:"flex", alignItems:"center", gap:6,
                background: active ? "rgba(255,255,255,.18)" : "transparent",
                border: active ? "1px solid rgba(255,255,255,.3)" : "1px solid transparent",
                borderRadius:6, padding:"5px 10px", cursor:"pointer",
                fontFamily:"inherit", transition:"all .2s",
              }}
              onMouseEnter={e=>{ if(!active){ e.currentTarget.style.background="rgba(255,255,255,.1)"; e.currentTarget.style.borderColor="rgba(255,255,255,.15)"; }}}
              onMouseLeave={e=>{ if(!active){ e.currentTarget.style.background="transparent"; e.currentTarget.style.borderColor="transparent"; }}}>
                <FlagComp />
                <span className="lang-label" style={{ fontSize:12, color: active ? "#f4f1eb" : "rgba(244,241,235,.65)", fontWeight: active ? 700 : 400, letterSpacing:"0.03em" }}>{label}</span>
              </button>
            );
          })}
        </div>

        <div className="sc-header-actions" style={{ display:"flex", gap:8, alignItems:"center" }}>
          <button onClick={()=>{setAuthView(null);setTab("enter");setDisease(null);}} style={{ background:"#2d6a4f", color:"white", border:"none", padding:"7px 16px", fontFamily:"inherit", fontSize:12.5, cursor:"pointer", fontWeight:600 }}>＋</button>
          {currentUser ? <>
            <span style={{ color:"rgba(244,241,235,.85)", fontSize:11.5, whiteSpace:"nowrap" }}>{currentUser.display_name}</span>
            <button onClick={handleLogout} style={{ background:"transparent", color:"rgba(244,241,235,.8)", border:"1px solid rgba(244,241,235,.35)", padding:"7px 14px", fontFamily:"inherit", fontSize:12.5, cursor:"pointer" }}>{t.authLogoutBtn}</button>
          </> : <button onClick={()=>setAuthView("login")} style={{ background:"transparent", color:"rgba(244,241,235,.8)", border:"1px solid rgba(244,241,235,.35)", padding:"7px 14px", fontFamily:"inherit", fontSize:12.5, cursor:"pointer" }}>{t.login}</button>}
        </div>
      </nav>

      {/* Hero */}
      <header style={{ background:"linear-gradient(135deg,#1b3a52 0%,#0f2738 100%)", padding:"40px 32px 36px", overflow:"hidden", position:"relative" }}>
        <div style={{ position:"absolute",top:-50,right:-50,width:280,height:280,borderRadius:"50%",background:"rgba(44,110,138,.15)" }} />
        <div style={{ maxWidth:860, margin:"0 auto", position:"relative" }}>
          <h1 style={{ fontFamily:"'Zen Kaku Gothic New', 'Hiragino Sans', sans-serif", fontSize:"clamp(18px,2.8vw,32px)", fontWeight:700, color:"#f4f1eb", lineHeight:1.5, marginBottom:10 }}>
            {t.heroTitle1}　<span style={{ fontStyle:"italic", color:"rgba(180,220,235,.9)" }}>{t.heroTitleItalic}</span><br />{t.heroTitle2}
          </h1>
          <p style={{ color:"rgba(220,235,240,.7)", fontSize:13, marginBottom:20 }}>{t.heroSub}</p>
          <div className="sc-hero-btns" style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:18 }}>
            <button onClick={()=>{setAuthView(null);setTab("enter");setDisease(null);}} style={{ background:"#2d6a4f",color:"white",border:"none",padding:"10px 22px",fontFamily:"inherit",fontSize:13,cursor:"pointer",fontWeight:600 }}>{t.btnInput}</button>
            <button onClick={()=>{setAuthView(null);setTab("cases");}} style={{ background:"transparent",color:"rgba(244,241,235,.8)",border:"1.5px solid rgba(244,241,235,.35)",padding:"9px 18px",fontFamily:"inherit",fontSize:13,cursor:"pointer" }}>{t.btnView}</button>
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {[{id:"pd",icon:"🧠",l:t.pdLabel},{id:"ad",icon:"💭",l:t.adLabel},{id:"als",icon:"⚡",l:t.alsLabel},{id:"cancer",icon:"🎯",l:t.cancerLabel}].map(d=>(
              <span key={d.id} style={{ fontSize:11, padding:"4px 12px", border:"1px solid rgba(255,255,255,.2)", color:"rgba(255,255,255,.7)", borderRadius:20 }}>{d.icon} {d.l}</span>
            ))}
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="sc-main" style={{ maxWidth:900, margin:"0 auto", padding:"20px 32px 60px" }}>
        {authView ? <>
          {authView==="login" && <LoginScreen t={t} onLoginSuccess={handleLoginSuccess} onSwitchToApply={()=>setAuthView("apply")} onSwitchToReset={()=>setAuthView("reset-request")} />}
          {authView==="apply" && <ApplyScreen t={t} onSwitchToLogin={()=>setAuthView("login")} />}
          {authView==="change-password" && <ChangePasswordScreen t={t} mustChange={!!(currentUser && currentUser.must_change_password)} onChanged={handlePasswordChanged} onCancel={()=>setAuthView(null)} />}
          {authView==="reset-request" && <ResetRequestScreen t={t} onSwitchToLogin={()=>setAuthView("login")} />}
          {authView==="reset-confirm" && <ResetConfirmScreen t={t} token={resetToken} onDone={()=>{setResetToken(null);setAuthView("login");window.history.replaceState({}, "", window.location.pathname);}} />}
        </> : <>
        <div style={{ display:"flex", alignItems:"center", marginBottom:22 }}>
          <button className="sc-hamburger" onClick={()=>setNavOpen(o=>!o)} aria-label={navOpen ? t.navClose||"閉じる" : t.navOpen||"メニュー"}>
            {navOpen ? "✕" : "☰"}
          </button>
          <div className={navOpen ? "sc-tabnav sc-tabnav-open" : "sc-tabnav"} style={{ display:"flex", borderBottom:"1px solid #e0dbd2", overflowX:"auto", flex:1 }}>
            {TABS.map(tb=>(
              <button key={tb.id} className={"sc-tab-btn"+(tab===tb.id?" active":"")} onClick={()=>{setAuthView(null);setTab(tb.id);if(tb.id==="enter")setDisease(null);setNavOpen(false);}} style={{
                background:"none",border:"none",borderBottom:tab===tb.id?"2.5px solid #1b3a52":"2.5px solid transparent",
                padding:"11px 16px",fontFamily:"inherit",fontSize:13,color:tab===tb.id?"#1b3a52":"#8a8a9a",
                fontWeight:tab===tb.id?700:400,cursor:"pointer",marginBottom:-1,whiteSpace:"nowrap"
              }}>{tb.label}</button>
            ))}
          </div>
          {navOpen && <div className="sc-tabnav-backdrop" onClick={()=>setNavOpen(false)} />}
        </div>

        {/* 症例データ */}
        {tab==="cases" && <>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10 }}>
            <div style={{ display:"flex",gap:4,background:"#eef0f2",padding:3,borderRadius:6 }}>
              <button onClick={()=>setCasesSubTab("mine")}
                style={{ background:casesSubTab==="mine"?"white":"transparent",border:"none",padding:"7px 16px",fontFamily:"inherit",fontSize:12.5,cursor:"pointer",fontWeight:casesSubTab==="mine"?700:500,color:casesSubTab==="mine"?"#1b3a52":"#6a7a8a",borderRadius:4,boxShadow:casesSubTab==="mine"?"0 1px 3px rgba(0,0,0,.08)":"none" }}>
                {t.subTabMine}
              </button>
              <button onClick={()=>setCasesSubTab("public")}
                style={{ background:casesSubTab==="public"?"white":"transparent",border:"none",padding:"7px 16px",fontFamily:"inherit",fontSize:12.5,cursor:"pointer",fontWeight:casesSubTab==="public"?700:500,color:casesSubTab==="public"?"#1b3a52":"#6a7a8a",borderRadius:4,boxShadow:casesSubTab==="public"?"0 1px 3px rgba(0,0,0,.08)":"none" }}>
                {t.subTabPublic}
              </button>
            </div>
            <button onClick={()=>{setAuthView(null);setTab("enter");setDisease(null);}} style={{ background:"#2d6a4f",color:"white",border:"none",padding:"8px 18px",fontFamily:"inherit",fontSize:12.5,cursor:"pointer",fontWeight:600 }}>{t.btnAddCase}</button>
          </div>

          {casesSubTab==="mine" && (
            !currentUser ? <div style={{ textAlign:"center", padding:"50px 20px" }}>
              <div style={{ fontSize:36, marginBottom:12 }}>🔒</div>
              <div style={{ fontSize:13, color:"#6a7a8a", marginBottom:18 }}>{t.authMineLoginRequired}</div>
              <button onClick={()=>setAuthView("login")} style={{ background:"#1b3a52", color:"white", border:"none", padding:"9px 22px", fontFamily:"inherit", fontSize:13, cursor:"pointer" }}>{t.authLoginBtn}</button>
            </div>
            : casesLoading ? <div style={{ textAlign:"center", padding:"30px", color:"#8a9aaa", fontSize:13 }}>…</div>
            : <MyCasesList t={t} lang={lang} diseasesMap={DISEASES_MAP} cases={myCases} onEdit={handleEditCase} onDelete={handleDeleteCase} />
          )}

          {casesSubTab==="public" && (
            casesLoading ? <div style={{ textAlign:"center", padding:"30px", color:"#8a9aaa", fontSize:13 }}>…</div>
            : <MyCasesList t={t} lang={lang} diseasesMap={DISEASES_MAP} cases={publicCases} onEdit={handleEditCase} onDelete={handleDeleteCase} />
          )}
        </>}

        {/* 症例入力 */}
        {tab==="enter" && <>
          {!currentUser ? <div style={{ textAlign:"center", padding:"60px 20px" }}>
            <div style={{ fontSize:40, marginBottom:14 }}>🔒</div>
            <div style={{ fontSize:15, fontWeight:700, color:"#1b3a52", marginBottom:8 }}>{t.authLoginRequiredTitle}</div>
            <div style={{ fontSize:13, color:"#6a7a8a", marginBottom:22, maxWidth:420, marginLeft:"auto", marginRight:"auto" }}>{t.authLoginRequiredDesc}</div>
            <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
              <button onClick={()=>setAuthView("login")} style={{ background:"#1b3a52", color:"white", border:"none", padding:"10px 24px", fontFamily:"inherit", fontSize:13, cursor:"pointer", fontWeight:600 }}>{t.authLoginBtn}</button>
              <button onClick={()=>setAuthView("apply")} style={{ background:"white", color:"#1b3a52", border:"1.5px solid #1b3a52", padding:"10px 24px", fontFamily:"inherit", fontSize:13, cursor:"pointer" }}>{t.authGoApply}</button>
            </div>
          </div> : <>
          <div style={{ background:"#eaf4f8",border:"1px solid #b3d4e0",padding:"10px 16px",marginBottom:18,fontSize:13,color:"#1b3a52" }}>
            {t.notice}<strong>{t.noticeStrong}</strong>{t.noticeEnd}
          </div>
          {!disease && <DiseaseSelector onSelect={setDisease} t={t} />}
          {disease && <>
            <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:18,flexWrap:"wrap" }}>
              <button onClick={()=>{setDisease(null);setEditingRecord(null);}} style={{ background:"white",border:"1.5px solid #d8d2c8",padding:"6px 14px",fontFamily:"inherit",fontSize:12.5,cursor:"pointer",color:"#5a6a7a" }}>{t.backDisease}</button>
              <div style={{ fontSize:14.5,fontWeight:700,color:DISEASES_MAP[disease]?.color }}>
                {DISEASES_MAP[disease]?.icon}　{DISEASES_MAP[disease]?.label} — {editingRecord ? t.editFormTitle : t.formTitle}
              </div>
              {editingRecord && <span style={{ fontSize:11,padding:"3px 10px",borderRadius:20,background:"#fff3cd",color:"#856404",fontWeight:700 }}>✏ {t.editingMode}: {editingRecord.caseId}</span>}
            </div>
            {disease==="pd"     && <PDForm     key={editingRecord?.savedAt||"new-pd"}     t={t} lang={lang} initial={editingRecord} onCancelEdit={handleCancelEdit} />}
            {disease==="ad"     && <ADForm     key={editingRecord?.savedAt||"new-ad"}     t={t} lang={lang} initial={editingRecord} onCancelEdit={handleCancelEdit} />}
            {disease==="als"    && <ALSForm    key={editingRecord?.savedAt||"new-als"}    t={t} lang={lang} initial={editingRecord} onCancelEdit={handleCancelEdit} />}
            {disease==="cancer" && <CancerForm key={editingRecord?.savedAt||"new-cancer"} t={t} lang={lang} initial={editingRecord} onCancelEdit={handleCancelEdit} />}
            {disease==="stroke" && <StrokeForm key={editingRecord?.savedAt||"new-stroke"} t={t} lang={lang} initial={editingRecord} onCancelEdit={handleCancelEdit} />}
            {disease==="sci"    && <SCIForm    key={editingRecord?.savedAt||"new-sci"}    t={t} lang={lang} initial={editingRecord} onCancelEdit={handleCancelEdit} />}
            {disease==="dm"     && <DMForm     key={editingRecord?.savedAt||"new-dm"}     t={t} lang={lang} initial={editingRecord} onCancelEdit={handleCancelEdit} />}
            {disease==="other"  && <OtherForm  key={editingRecord?.savedAt||"new-other"}  t={t} lang={lang} initial={editingRecord} onCancelEdit={handleCancelEdit} />}
          </>}
          </>}
        </>}

        {/* ダッシュボード */}
        {tab==="dashboard" && <Dashboard t={t} lang={lang} diseasesMap={DISEASES_MAP} currentUser={currentUser} />}

        {/* プロトコル */}
        {tab==="protocols" && <>
          <div style={{ fontSize:16,fontWeight:700,color:"#1b3a52",marginBottom:16 }}>{t.protocolTitle}</div>
          {!currentUser && (
            <div style={{ background:"white",border:"1px solid #e5e0d8",padding:"32px 20px",textAlign:"center",color:"#6a7a8a",fontSize:13.5 }}>
              {t.docLoginMsg}
            </div>
          )}
          {currentUser && documentsLoading && (
            <div style={{ background:"white",border:"1px solid #e5e0d8",padding:"32px 20px",textAlign:"center",color:"#8a9aaa",fontSize:13.5 }}>
              {t.docLoadingMsg}
            </div>
          )}
          {currentUser && !documentsLoading && documentsError && (
            <div style={{ background:"#fdecea",border:"1px solid #e0a0a0",padding:"16px 20px",color:"#a04040",fontSize:13.5 }}>
              {documentsError}
            </div>
          )}
          {currentUser && !documentsLoading && !documentsError && documents.length===0 && (
            <div style={{ background:"white",border:"1px solid #e5e0d8",padding:"32px 20px",textAlign:"center",color:"#8a9aaa",fontSize:13.5 }}>
              {t.docEmpty}
            </div>
          )}
          {currentUser && !documentsLoading && !documentsError && documents.map(doc=>(
            <div key={doc.id} style={{ background:"white",border:"1px solid #e5e0d8",padding:"15px 20px",marginBottom:2,display:"flex",gap:12,alignItems:"center",flexWrap:"wrap" }}>
              <div style={{ flex:1,minWidth:180 }}>
                <div style={{ fontSize:13.5,fontWeight:600,color:"#1b3a52" }}>{doc.title}</div>
                {doc.description && <div style={{ fontSize:12,color:"#6a7a8a",marginTop:3 }}>{doc.description}</div>}
                <div style={{ fontSize:11,color:"#9aabb5",marginTop:4 }}>
                  {t.docUploadedLabel}: {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ""}
                </div>
              </div>
              <button onClick={()=>handleDownloadDocument(doc)}
                style={{ background:"#2c6e8a",color:"white",border:"none",padding:"7px 16px",fontFamily:"inherit",fontSize:12.5,cursor:"pointer",whiteSpace:"nowrap" }}>
                {t.docDownloadBtn}
              </button>
            </div>
          ))}
        </>}

        {/* 安全性情報 */}
        {tab==="safety" && <>
          <div style={{ background:"#fff8e1",border:"1px solid #f0c040",padding:"16px 20px",marginBottom:18 }}>
            <strong style={{ color:"#7a5c00",fontSize:13.5 }}>⚠ {t.saeTitle}</strong>
            <p style={{ fontSize:13,color:"#7a5c00",marginTop:5,lineHeight:1.8 }}>{t.saeDesc}</p>
            <button style={{ marginTop:10,background:"#856404",color:"white",border:"none",padding:"7px 18px",fontFamily:"inherit",fontSize:13,cursor:"pointer" }}>{t.saeBtn}</button>
          </div>

          <div style={{ fontSize:16,fontWeight:700,color:"#1b3a52",marginBottom:16 }}>{t.safetyStatsTitle}</div>

          {safetyLoading && (
            <div style={{ background:"white",border:"1px solid #e5e0d8",padding:"32px 20px",textAlign:"center",color:"#8a9aaa",fontSize:13.5 }}>
              {t.safetyLoadingMsg}
            </div>
          )}
          {!safetyLoading && safetyError && (
            <div style={{ background:"#fdecea",border:"1px solid #e0a0a0",padding:"16px 20px",color:"#a04040",fontSize:13.5 }}>
              {safetyError}
            </div>
          )}
          {!safetyLoading && !safetyError && safetyStats && safetyStats.total_cases===0 && (
            <div style={{ background:"white",border:"1px solid #e5e0d8",padding:"32px 20px",textAlign:"center",color:"#8a9aaa",fontSize:13.5 }}>
              {t.safetyEmpty}
            </div>
          )}
          {!safetyLoading && !safetyError && safetyStats && safetyStats.total_cases>0 && <>
            <div style={{ display:"flex",gap:12,flexWrap:"wrap",marginBottom:14 }}>
              <div style={{ flex:"1 1 160px",background:"white",border:"1px solid #e5e0d8",padding:"16px 18px" }}>
                <div style={{ fontSize:11.5,color:"#6a7a8a",marginBottom:6 }}>{t.safetyTotalCases}</div>
                <div style={{ fontSize:26,fontWeight:700,color:"#1b3a52" }}>{safetyStats.total_cases}<span style={{ fontSize:13,fontWeight:400,marginLeft:4 }}>{t.safetyCasesUnit}</span></div>
              </div>
              <div style={{ flex:"1 1 160px",background:"white",border:"1px solid #e5e0d8",padding:"16px 18px" }}>
                <div style={{ fontSize:11.5,color:"#6a7a8a",marginBottom:6 }}>{t.safetySaeCount}</div>
                <div style={{ fontSize:26,fontWeight:700,color:"#c0392b" }}>{safetyStats.sae_count}<span style={{ fontSize:13,fontWeight:400,marginLeft:4 }}>{t.safetyCasesUnit}</span></div>
              </div>
              <div style={{ flex:"1 1 160px",background:"white",border:"1px solid #e5e0d8",padding:"16px 18px" }}>
                <div style={{ fontSize:11.5,color:"#6a7a8a",marginBottom:6 }}>{t.safetyOverallRate}</div>
                <div style={{ fontSize:26,fontWeight:700,color:"#1b3a52" }}>
                  {safetyStats.total_cases ? `${(safetyStats.sae_count/safetyStats.total_cases*100).toFixed(1)}%` : "—"}
                </div>
              </div>
            </div>

            <DiseaseBarChart
              title={t.safetyByDiseaseTitle}
              data={(safetyStats.sae_rate_by_disease||[]).map(r=>({
                name: (DISEASES_MAP[r.disease] && DISEASES_MAP[r.disease].label) || r.disease,
                value: Math.round((r.rate||0)*1000)/10,
              }))}
            />

            <DiseaseBarChart
              title={t.safetyTopEventsTitle}
              data={(safetyStats.top_adverse_events||[]).map(e=>({ name:e.event, value:e.count }))}
            />
          </>}
        </>}
        </>}
      </main>

      <footer style={{ background:"linear-gradient(135deg,#0f2738,#1b3a52)", padding:"40px 32px", textAlign:"center" }}>
        <div style={{ fontFamily:"'Zen Kaku Gothic New', 'Hiragino Sans', sans-serif",fontSize:22,color:"#f4f1eb",marginBottom:8,fontWeight:700 }}>{t.footerTitle}</div>
        <p style={{ color:"rgba(220,235,240,.6)",fontSize:13,marginBottom:20,lineHeight:1.9,whiteSpace:"pre-line" }}>{t.footerSub}</p>
        <button onClick={()=>setAuthView("apply")} style={{ background:"#1b3a52",color:"white",border:"2px solid rgba(255,255,255,.3)",padding:"11px 36px",fontFamily:"inherit",fontSize:14,cursor:"pointer",fontWeight:600 }}>{t.footerBtn}</button>
        <div style={{ marginTop:32,fontSize:11,color:"rgba(220,235,240,.3)" }}>{t.footerCopy}</div>
      </footer>
    </div>
  );
}
